import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { X, MapPin, Maximize2, Plus, Trash2, Edit2, Pencil, Eye, EyeOff, PenTool, Check, Info, Loader2, Grid, Navigation, Camera, Minimize2, ChevronUp, ChevronDown, AlertTriangle, AlertCircle } from 'lucide-react';
import { API_BASE_URL as API_BASE } from "../../config/apiConfig";
import { motion } from 'framer-motion';

// Import constants and utilities from extracted files
import { STRUCTURE_TYPES, rgbToHex, GIS_LAYERS } from "./MapModal/constants";
import {
    detectJurisdiction,
    getJurisdictionInfo,
    getJurisdictionStyle,
    calculateLAH,
    calculateLosAltos,
    calculatePaloAlto,
    calculateSaratoga,
    calculateLosGatos,
    calculateMonteSereno,
    calculateCupertino,
    calculateSanJose,
    calculateFremont,
    calculateHillsborough,
    calculateAtherton,
    calculateWoodside,
    calculateOrinda,
    calculateLafayette,
    calculateMountainView,
    calculateSunnyvale,
    calculateSantaClara,
    calculateOakland,
    calculateAlameda,
    calculateBerkeley,
    calculateHayward,
    calculateLivermore,
    calculatePleasanton,
    calculateDublin,
    calculateNewark,
    calculateUnionCity,
    calculateSanLeandro,
    calculateEmeryville,
    calculateAlbany,
    calculatePiedmont,
    calculateRedwoodCity,
    calculateSanMateo,
    calculateDalyCity,
    calculateSouthSanFrancisco,
    calculateFosterCity,
    calculateBurlingame,
    calculateBelmont,
    calculateSanCarlos,
    calculateMenloPark,
    calculateMillbrae,
    calculatePacifica,
    calculateHalfMoonBay,
    calculateWalnutCreek,
    calculateConcord,
    calculateRichmond,
    calculateAntioch,
    calculateBrentwood,
    calculatePittsburg,
    calculateSanRamon,
    calculatePleasantHill,
    calculateClayton,
    calculateMoraga,
    calculateDanville,
    calculateAlamo,
    calculateBelvedere,
    calculateCorteMadera,
    calculateFairfax,
    calculateLarkspur,
    calculateMillValley,
    calculateNovato,
    calculateRoss,
    calculateSanAnselmo,
    calculateSanRafael,
    calculateSausalito,
    calculateTiburon,
    calculateAmericanCanyon,
    calculateCalistoga,
    calculateNapaCity,
    calculateStHelena,
    calculateYountville,
    calculateBenicia,
    calculateDixon,
    calculateFairfield,
    calculateRioVista,
    calculateSuisunCity,
    calculateVacaville,
    calculateVallejo,
    calculateOakley,
    calculateSanPablo,
    calculatePortolaValley,
    calculateArnold
} from "./MapModal/utils/jurisdictionHelpers";
import { extendRectangleRings } from "./MapModal/utils/geometryHelpers";
import { isAddressPointCompatibleWithParcel } from "./MapModal/utils/parcelMatch";

// UI Components are available but not yet integrated:
// import { NotificationToast, DeleteConfirmModal, MapHeader, TestFitPanel, 
//          TerrainAnalysisPanel, DevCapacityPanel, ErrorDisplay,
//          LoadingOverlay, FloatingStatusPill, DrawingModeIndicator 
// } from "./MapModal/components";

const DEFAULT_CONTOUR_DISPLAY_INTERVAL_FT = 2;

const MapModal = ({ isOpen, onClose, address, apn, parcelAreaAcres, mda, mfa, grossBuildingArea, livingArea, avgSlope, isInline = false, analysisId, initialContourInterval, initialContourTrace, onAnalysisComplete, onContoursGenerated, onSnapshotsReady, sr_wkid, county: countyProp }) => {
    const mapDiv = useRef(null);
    const [loadingStep, setLoadingStep] = useState("initializing");
    const [error, setError] = useState(null);
    const mapView = useRef(null);
    const [scriptsLoaded, setScriptsLoaded] = useState(false);
    const [locationInfo, setLocationInfo] = useState(null);
    const [boundaryStatus, setBoundaryStatus] = useState("searching");
    const [parcelMatchWarning, setParcelMatchWarning] = useState(null);
    const [gridSettings, setGridSettings] = useState({ size: 10, unit: 'ft' });
    const modulesRef = useRef({});
    const boundaryGeometryRef = useRef(null);
    const [analysisStats, setAnalysisStats] = useState(null);
    const [developmentAreaTrace, setDevelopmentAreaTrace] = useState(null);
    const [developmentAreaTraceError, setDevelopmentAreaTraceError] = useState(null);
    const [demContourTrace, setDemContourTrace] = useState(initialContourTrace || null);
    const [demContourError, setDemContourError] = useState(null);
    const [demContourLoading, setDemContourLoading] = useState(false);
    const [demContourStatus, setDemContourStatus] = useState(null);
    const terrainTraceRequestKeyRef = useRef(null);
    const demContourPollTimerRef = useRef(null);
    const initialContourTraceRef = useRef(initialContourTrace || null);
    const onContoursGeneratedRef = useRef(onContoursGenerated);
    const [viewMode] = useState("standard");
    const [showGrid, setShowGrid] = useState(true);
    const [showContours, setShowContours] = useState(true);
    const contourInterval = Number.isFinite(parseFloat(initialContourInterval))
        ? parseFloat(initialContourInterval)
        : DEFAULT_CONTOUR_DISPLAY_INTERVAL_FT;
    const [viewFilter, setViewFilter] = useState(["all"]); // Changed to array for multi-select
    const [elevationError, setElevationError] = useState(null);
    const hasLoggedErrorRef = useRef(false);

    // New Feature States
    const [isFullscreen, setIsFullscreen] = useState(false);
    const modalRef = useRef(null);
    const [retryCount, setRetryCount] = useState(0);
    const maxRetries = 3;

    // Notification state for toast messages
    const [notification, setNotification] = useState(null); // { type: 'success' | 'error' | 'warning', message: string }

    // Delete confirmation modal state
    const [deleteConfirmModal, setDeleteConfirmModal] = useState(null); // { id, type, area }

    // Helper to get user_id from JWT token
    const getUserIdFromToken = useCallback(() => {
        try {
            const token = localStorage.getItem('accessToken');
            if (!token) return null;
            // Decode JWT payload (base64)
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.sub || payload.user_id || payload.id || null;
        } catch (e) {
            console.warn('Could not decode user_id from token:', e);
            return null;
        }
    }, []);

    // Test Fit Feature State
    const [testFitMode, setTestFitMode] = useState(false);
    const [testFitStructures, setTestFitStructures] = useState([]); // Array of { id, area, width, length, isValid, shape, type }
    const [savedStructuresLoadedForProperty, setSavedStructuresLoadedForProperty] = useState(null);
    const [activeStructureId, setActiveStructureId] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [structureVariant, setStructureVariant] = useState('new'); // Toggle for 'new' or 'existing' structures
    const [drawingMode, setDrawingMode] = useState('box'); // 'box' or 'draw' - user's preferred drawing mode
    // eslint-disable-next-line no-unused-vars
    const [selectionMode, setSelectionMode] = useState(null); // 'EXISTING_MFA' | 'EXISTING_MDA' | null - for selecting existing buildings


    const sketchViewModelRef = useRef(null);
    const testFitLayerRef = useRef(null);
    const pendingStructureTypeRef = useRef('NEW_MFA');

    // Logic Refs to prevent stale closures in external event handlers (ArcGIS SketchViewModel)
    const validatePlacementRef = useRef(null);
    const updateStructureStateRef = useRef(null);
    const setupSketchViewModelRef = useRef(null);
    // Stores the intended type when starting manual draw
    const buildingsLayerRef = useRef(null); // Deprecated: automatic footprint overlays are intentionally hidden
    const originalBuildingsRef = useRef([]); // Detected footprint metadata for stats only
    const importedTraceKeyRef = useRef(null);
    const hasSavedStructuresRef = useRef(false);
    const dataFetchedForApnRef = useRef(null); // Track which APN has been loaded to prevent unnecessary refetches
    const structureGeometryCacheRef = useRef({}); // Cache geometries for structures to persist across modal close/open
    const lastSavedSnapshotRef = useRef(null); // Track last saved snapshot fingerprint to avoid duplicate writes
    const lastVisualSnapshotFingerprintRef = useRef(null); // Track last visual snapshot to avoid redundant captures

    // Extend Mode State - for extending existing buildings
    const [extendMode, setExtendMode] = useState(false);

    // Accordion State for Panels
    const [isTerrainExpanded, setIsTerrainExpanded] = useState(true);
    const [isCapacityExpanded, setIsCapacityExpanded] = useState(true);
    const [isStructuresExpanded, setIsStructuresExpanded] = useState(true);
    const [manualMfaStructures, setManualMfaStructures] = useState([]); // Array of { id, stories, area }
    const [isEditingMfaOffset, setIsEditingMfaOffset] = useState(false);
    const [editingMfaStructureId, setEditingMfaStructureId] = useState(null); // ID of entry being edited
    const [mfaOffsetInput, setMfaOffsetInput] = useState("");
    const [mfaStoriesInput, setMfaStoriesInput] = useState("");
    const [activeReportTab, setActiveReportTab] = useState("overview"); // 'overview' | 'worksheet'



    // Jurisdiction Detection - determine which rules apply based on address
    // NOTE: Define jurisdiction-related calculations FIRST since totalStats depends on them
    const jurisdiction = React.useMemo(() => detectJurisdiction(address), [address]);
    const jurisdictionInfo = React.useMemo(() => getJurisdictionInfo(jurisdiction), [jurisdiction]);
    const county = React.useMemo(() => {
        if (countyProp) return countyProp;
        return jurisdictionInfo?.county || 'SCC';
    }, [countyProp, jurisdictionInfo]);
    const resolvedWkid = React.useMemo(() => {
        if (sr_wkid) return sr_wkid;
        if (county === 'COLLIN' || county === 'DENTON') {
            return 2276;
        }
        return 2227;
    }, [sr_wkid, county]);
    const mdaMetricLabel = jurisdictionInfo?.mdaLabel || 'MDA';
    const mdaMetricShortLabel = (jurisdiction === 'monte_sereno') ? 'Structural Cov' : (jurisdictionInfo?.mdaLabel || 'MDA');
    const mfaMetricLabel = jurisdictionInfo?.mfaLabel || 'MFA';
    const mfaMetricShortLabel = (jurisdiction === 'saratoga') ? 'Floor Area' : (jurisdiction === 'cupertino' ? 'Max Floor' : (jurisdiction === 'monte_sereno' ? 'Main Dwelling' : (jurisdiction === 'los_gatos' ? 'FAR' : 'MFA')));
    const assessorFloorArea = React.useMemo(() => {
        const parsed = livingArea !== undefined && livingArea !== null ? parseFloat(livingArea) : 0;
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }, [livingArea]);

    useEffect(() => {
        initialContourTraceRef.current = initialContourTrace || null;
    }, [initialContourTrace]);

    useEffect(() => {
        onContoursGeneratedRef.current = onContoursGenerated;
    }, [onContoursGenerated]);

    useEffect(() => {
        if (!isOpen || (!address && !apn)) {
            terrainTraceRequestKeyRef.current = null;
            if (demContourPollTimerRef.current) {
                clearTimeout(demContourPollTimerRef.current);
                demContourPollTimerRef.current = null;
            }
            setDevelopmentAreaTrace(null);
            setDevelopmentAreaTraceError(null);
            setDemContourTrace(null);
            setDemContourError(null);
            setDemContourLoading(false);
            setDemContourStatus(null);
            return;
        }

        const requestKey = [
            address || "",
            apn || "",
            jurisdiction || "",
            analysisId || "",
            contourInterval || DEFAULT_CONTOUR_DISPLAY_INTERVAL_FT,
        ].join("|");

        if (terrainTraceRequestKeyRef.current === requestKey) {
            return;
        }
        terrainTraceRequestKeyRef.current = requestKey;
        if (demContourPollTimerRef.current) {
            clearTimeout(demContourPollTimerRef.current);
            demContourPollTimerRef.current = null;
        }

        const controller = new AbortController();
        importedTraceKeyRef.current = null;
        hasSavedStructuresRef.current = false;
        setSavedStructuresLoadedForProperty(null);
        setDevelopmentAreaTrace(null);
        setDevelopmentAreaTraceError(null);
        setDemContourTrace(initialContourTraceRef.current);
        setDemContourError(null);
        setDemContourLoading(true);

        axios.get(`${API_BASE}/api/parcel-development-area`, {
            params: { address, apn, city: jurisdiction, includeGeometry: true },
            signal: controller.signal
        }).then((response) => {
            const trace = response.data;
            if (trace?.success) {
                setDevelopmentAreaTrace(trace);
            }
        }).catch((err) => {
            if (axios.isCancel?.(err) || err.name === "CanceledError") return;
            console.warn("Development area trace failed:", err);
            setDevelopmentAreaTraceError(err?.response?.data?.detail || err.message || "Trace unavailable");
        });

        const fetchContours = async (isPolling = false) => {
            if (!isPolling) {
                setDemContourLoading(true);
                setDemContourStatus("Initializing LiDAR grid sampling...");
            }

            try {
                const response = await axios.get(`${API_BASE}/api/parcel-dem-contours`, {
                    params: { address, apn, city: jurisdiction, analysis_id: analysisId, interval_ft: contourInterval, grid_spacing_ft: 8, includeGeometry: true },
                    signal: controller.signal
                });

                const trace = response.data;
                if (trace?.success) {
                    if (trace.status === "processing") {
                        const statusMsg = trace.message || "LiDAR Processing...";
                        setDemContourStatus(statusMsg);
                        console.log(`[LiDAR Telemetry] ${statusMsg}`);

                        // Poll again in 3 seconds
                        demContourPollTimerRef.current = setTimeout(() => {
                            if (!controller.signal.aborted) {
                                fetchContours(true);
                            }
                        }, 3000);
                    } else {
                        setDemContourTrace(trace);
                        if (onContoursGeneratedRef.current) {
                            onContoursGeneratedRef.current(trace);
                        }
                        setDemContourLoading(false);
                        setDemContourStatus(null);
                    }
                } else {
                    setDemContourError(trace?.reason || "DEM contours unavailable");
                    setDemContourLoading(false);
                }
            } catch (err) {
                if (axios.isCancel?.(err) || err.name === "CanceledError") {
                    setDemContourLoading(false);
                    return;
                }
                console.warn("DEM contour trace failed:", err);
                setDemContourError(err?.response?.data?.detail || err.message || "DEM contours unavailable");
                setDemContourLoading(false);
            }
        };

        // Only fetch if we don't already have trace data (including geometry) to prevent infinite re-render loops
        if (!initialContourTrace || (initialContourTrace.success && !initialContourTrace.features)) {
            fetchContours();
        } else {
            // Ensure loading states are cleared if we use initial trace
            setDemContourLoading(false);
            setDemContourStatus(null);
        }

        return () => controller.abort();
    }, [isOpen, address, apn, jurisdiction, analysisId, initialContourTrace, onContoursGenerated, contourInterval]);

    useEffect(() => {
        const view = mapView.current;
        const modules = modulesRef.current;
        if (!view?.map || !modules?.GraphicsLayer || !modules?.Graphic || !modules?.Polyline || !demContourTrace) {
            return;
        }

        const { GraphicsLayer, Graphic, Polyline, Point } = modules;
        const features = (demContourTrace.features || []).filter(feature => feature?.geometry?.paths);
        if (features.length === 0) return;

        let layer = view.map.findLayerById("dem-contour-layer");
        if (!layer) {
            layer = new GraphicsLayer({ id: "dem-contour-layer", title: "Generated 2ft DEM Contours" });
            view.map.add(layer, 11);
        }
        layer.removeAll();
        layer.visible = showContours;

        const labelEvery = Math.max(1, Math.ceil(features.length / 80));
        features.forEach((feature, index) => {
            const polyline = new Polyline({
                paths: feature.geometry.paths,
                spatialReference: feature.geometry.spatialReference || { wkid: resolvedWkid }
            });

            layer.add(new Graphic({
                geometry: polyline,
                symbol: {
                    type: "simple-line",
                    color: [255, 255, 255, 0.88],
                    width: 3.4,
                    style: "solid"
                },
                attributes: {
                    type: "dem-contour-casing",
                    elevation_ft: feature.elevation_ft
                },
                popupEnabled: false
            }));

            layer.add(new Graphic({
                geometry: polyline,
                symbol: {
                    type: "simple-line",
                    color: [8, 13, 26, 0.98],
                    width: 1.85,
                    style: "solid"
                },
                attributes: {
                    type: "dem-contour",
                    elevation_ft: feature.elevation_ft
                },
                popupTemplate: {
                    title: "Generated 2ft DEM Contour",
                    content: `${Math.round(feature.elevation_ft).toLocaleString()} ft`
                }
            }));

            if (Point && index % labelEvery === 0) {
                const path = feature.geometry.paths?.[0];
                if (path && path.length >= 2) {
                    const mid = path[Math.floor(path.length / 2)];
                    layer.add(new Graphic({
                        geometry: new Point({
                            x: mid[0],
                            y: mid[1],
                            spatialReference: feature.geometry.spatialReference || { wkid: resolvedWkid }
                        }),
                        symbol: {
                            type: "text",
                            text: `${Math.round(feature.elevation_ft)}`,
                            color: [15, 23, 42, 0.95],
                            haloColor: [255, 255, 255, 0.9],
                            haloSize: 1,
                            font: { size: 8, weight: "bold", family: "Arial" }
                        },
                        attributes: { type: "dem-contour-label" }
                    }));
                }
            }
        });

        const publicContourLayer = view.map.findLayerById("contour-layer");
        if (publicContourLayer) publicContourLayer.visible = false;
        view.map.reorder(layer, view.map.layers.length - 1);
    }, [demContourTrace, loadingStep, showContours, analysisStats, resolvedWkid]);

    useEffect(() => {
        const view = mapView.current;
        const modules = modulesRef.current;
        if (!view?.map || !modules?.GraphicsLayer || !modules?.Graphic || !modules?.Polygon || !developmentAreaTrace) {
            return;
        }

        const {
            GraphicsLayer, Graphic, SimpleFillSymbol, Polygon, Point
        } = modules;

        const features = (developmentAreaTrace.features || []).filter(feature => feature?.geometry?.rings);
        if (features.length === 0) return;
        const totalMfaTraceArea = features
            .filter(feature => feature.counts_toward_mfa)
            .reduce((sum, feature) => sum + (parseFloat(feature.area_ft2) || 0), 0);

        const propertyKey = apn || address || "property";
        // Allow traces to render even if saved structures are still loading, 
        // to provide immediate visual feedback.

        const traceKey = `${propertyKey}:${features.map(f => `${f.id}:${f.area_ft2}:${f.type}`).join("|")}`;
        console.log(`🗺️ Trace Overlay: Rendering ${features.length} features for ${propertyKey}. TraceKey match: ${importedTraceKeyRef.current === traceKey}`);

        if (importedTraceKeyRef.current === traceKey) return;

        // Traces are now allowed to render even if manual structures exist, 
        // to ensure MFA/MDA labels are always visible for the underlying property traces.

        let layer = view.map.findLayerById("test-fit-layer");
        if (!layer) {
            layer = new GraphicsLayer({ id: "test-fit-layer", title: "Test Fit" });
            view.map.add(layer);
        }
        // Always ensure test-fit layer is high in the stack so traces show above terrain grid
        view.map.reorder(layer, view.map.layers.length - 1);
        testFitLayerRef.current = layer;

        layer.graphics
            .filter(g => g.attributes?.isTraceOverlay)
            .forEach(g => layer.remove(g));

        const importedStructures = [];

        features.forEach((feature, index) => {
            const baseId = `trace-${feature.id || index}`;
            const polygon = new Polygon({
                rings: feature.geometry.rings,
                spatialReference: feature.geometry.spatialReference || { wkid: resolvedWkid }
            });
            const isPool = feature.type === "MDA_POOL_CANDIDATE";
            const extent = polygon.extent;
            const width = extent ? Math.round(extent.width * 3.28084) : 0;
            const length = extent ? Math.round(extent.height * 3.28084) : 0;

            const addTraceGraphic = ({ id, structureType, traceLabel, labelOffsetY = 0, outlineStyle = "dash", fillAlpha = null }) => {
                const typeConfig = STRUCTURE_TYPES[structureType] || STRUCTURE_TYPES.EXISTING_MDA;
                const color = [...typeConfig.color];
                if (fillAlpha !== null) color[3] = fillAlpha;
                const tracedArea = parseFloat(feature.area_ft2) || 0;
                const area = structureType === "EXISTING_MFA" && assessorFloorArea > 0 && totalMfaTraceArea > 0
                    ? assessorFloorArea * (tracedArea / totalMfaTraceArea)
                    : tracedArea;

                const graphic = new Graphic({
                    geometry: polygon,
                    symbol: new SimpleFillSymbol({
                        color,
                        outline: { color: isPool && !feature.counts_toward_mda ? [245, 158, 11, 1] : typeConfig.outline, width: structureType === "EXISTING_MFA" ? 3 : 2, style: outlineStyle }
                    }),
                    attributes: {
                        id,
                        type: structureType,
                        isTraceOverlay: true,
                        traceFeatureType: feature.type,
                        traceLabel,
                        lastValidGeometry: polygon.clone()
                    },
                    popupEnabled: false
                });
                layer.add(graphic);

                structureGeometryCacheRef.current[id] = polygon.toJSON();
                importedStructures.push({
                    id,
                    area,
                    originalArea: area,
                    isValid: true,
                    rejectReason: feature.review_required ? "Review candidate" : "",
                    width,
                    length,
                    shape: isPool ? "pool-trace" : "trace",
                    type: structureType,
                    isTraceOverlay: true,
                    traceFeatureType: feature.type,
                    traceLabel,
                    countsTowardMda: structureType === "EXISTING_MDA",
                    countsTowardMfa: structureType === "EXISTING_MFA"
                });

                if (extent?.center && Point) {
                    layer.add(new Graphic({
                        geometry: new Point({
                            x: extent.center.x,
                            y: extent.center.y,
                            spatialReference: polygon.spatialReference
                        }),
                        symbol: {
                            type: "text",
                            text: traceLabel,
                            color: "white",
                            font: { size: isPool ? 11 : 9, weight: "bold", family: "Arial" },
                            haloColor: typeConfig.outline,
                            haloSize: 2,
                            yoffset: labelOffsetY
                        },
                        attributes: {
                            id: `label-${id}`,
                            parentId: id,
                            type: "label",
                            isTraceOverlay: true
                        }
                    }));
                }
            };

            if (feature.counts_toward_mda && jurisdiction !== 'los_gatos') {
                addTraceGraphic({
                    id: `${baseId}-mda`,
                    structureType: "EXISTING_MDA",
                    traceLabel: isPool ? "POOL" : mdaMetricShortLabel,
                    labelOffsetY: feature.counts_toward_mfa ? -12 : 0,
                    outlineStyle: isPool ? "solid" : "dash",
                    fillAlpha: isPool ? 0.38 : 0.24
                });
            }

            if (feature.counts_toward_mfa) {
                addTraceGraphic({
                    id: `${baseId}-mfa`,
                    structureType: "EXISTING_MFA",
                    traceLabel: jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel,
                    labelOffsetY: feature.counts_toward_mda ? 12 : 0,
                    outlineStyle: "solid",
                    fillAlpha: 0.20
                });
            }
        });

        setTestFitStructures(prev => {
            const withoutTrace = prev.filter(s => !s.isTraceOverlay);
            return [...withoutTrace, ...importedStructures];
        });
        importedTraceKeyRef.current = traceKey;

        if (!sketchViewModelRef.current && setupSketchViewModelRef.current) {
            sketchViewModelRef.current = setupSketchViewModelRef.current(layer);
        }
    }, [developmentAreaTrace, loadingStep, apn, address, jurisdiction, testFitStructures, mdaMetricShortLabel, mfaMetricShortLabel, savedStructuresLoadedForProperty, assessorFloorArea, resolvedWkid]);

    // Calculate average slope from terrain analysis stats
    const estimatedSlope = React.useMemo(() => {
        if (!analysisStats) return null;

        const totalTerrainArea = (analysisStats.flatArea || 0) + (analysisStats.moderateArea || 0) + (analysisStats.steepArea || 0);
        if (totalTerrainArea === 0) return null;

        // Weighted average slope estimation based on terrain categories
        // Flat: ~5%, Moderate: ~15%, Steep: ~30%
        const avgSlope = (
            ((analysisStats.flatArea || 0) * 5) +
            ((analysisStats.moderateArea || 0) * 15) +
            ((analysisStats.steepArea || 0) * 30)
        ) / totalTerrainArea;

        return Math.round(avgSlope * 10) / 10;
    }, [analysisStats]);

    // Jurisdiction-specific MDA/MFA calculations
    // Backend values are preferred when available (more accurate, based on actual contour data)
    // Frontend calculations are kept as fallback and for displaying slope analysis details
    const jurisdictionCalc = React.useMemo(() => {
        // Check if backend has provided MDA/MFA values
        const hasBackendValues = Boolean(mda && mfa);
        const effectiveSlope = (avgSlope !== undefined && avgSlope !== null) ? avgSlope : estimatedSlope;

        const baseResult = (() => {
            if (jurisdiction === 'palo_alto') return { type: 'palo_alto', ...((parcelAreaAcres && effectiveSlope !== null) ? calculatePaloAlto(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'los_altos_hills') return { type: 'los_altos_hills', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateLAH(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'los_altos') return { type: 'los_altos', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateLosAltos(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'saratoga') return { type: 'saratoga', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSaratoga(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'los_gatos') return { type: 'los_gatos', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateLosGatos(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'monte_sereno') return { type: 'monte_sereno', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateMonteSereno(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'cupertino') return { type: 'cupertino', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateCupertino(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'san_jose') {
                const stories = manualMfaStructures.length > 0 ? parseInt(manualMfaStructures[0].stories) || 2 : 2;
                const selectedInterval = gridSettings.unit === 'ft' ? parseFloat(gridSettings.size) : 5;
                const usedInterval = analysisStats?.contourInterval || selectedInterval;
                return { type: 'san_jose', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSanJose(effectiveSlope, parcelAreaAcres, "R-1-8", stories, selectedInterval, usedInterval) : {}) };
            }
            if (jurisdiction === 'fremont') return { type: 'fremont', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateFremont(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'hillsborough') return { type: 'hillsborough', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateHillsborough(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'atherton') return { type: 'atherton', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateAtherton(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'woodside') return { type: 'woodside', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateWoodside(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'orinda') return { type: 'orinda', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateOrinda(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'lafayette') return { type: 'lafayette', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateLafayette(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'mountain_view') return { type: 'mountain_view', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateMountainView(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'sunnyvale') return { type: 'sunnyvale', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSunnyvale(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'berkeley') return { type: 'berkeley', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateBerkeley(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'hayward') return { type: 'hayward', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateHayward(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'livermore') return { type: 'livermore', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateLivermore(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'oakland') return { type: 'oakland', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateOakland(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'alameda') return { type: 'alameda', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateAlameda(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'pleasanton') return { type: 'pleasanton', ...((parcelAreaAcres && effectiveSlope !== null) ? calculatePleasanton(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'dublin') return { type: 'dublin', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateDublin(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'newark') return { type: 'newark', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateNewark(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'union_city') return { type: 'union_city', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateUnionCity(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'san_leandro') return { type: 'san_leandro', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSanLeandro(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'emeryville') return { type: 'emeryville', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateEmeryville(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'albany') return { type: 'albany', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateAlbany(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'piedmont') return { type: 'piedmont', ...((parcelAreaAcres && effectiveSlope !== null) ? calculatePiedmont(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'redwood_city') return { type: 'redwood_city', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateRedwoodCity(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'san_mateo') return { type: 'san_mateo', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSanMateo(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'daly_city') return { type: 'daly_city', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateDalyCity(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'south_san_francisco') return { type: 'south_san_francisco', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSouthSanFrancisco(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'foster_city') return { type: 'foster_city', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateFosterCity(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'burlingame') return { type: 'burlingame', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateBurlingame(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'belmont') return { type: 'belmont', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateBelmont(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'san_carlos') return { type: 'san_carlos', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSanCarlos(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'menlo_park') return { type: 'menlo_park', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateMenloPark(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'millbrae') return { type: 'millbrae', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateMillbrae(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'pacifica') return { type: 'pacifica', ...((parcelAreaAcres && effectiveSlope !== null) ? calculatePacifica(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'half_moon_bay') return { type: 'half_moon_bay', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateHalfMoonBay(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'walnut_creek') return { type: 'walnut_creek', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateWalnutCreek(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'concord') return { type: 'concord', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateConcord(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'richmond') return { type: 'richmond', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateRichmond(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'antioch') return { type: 'antioch', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateAntioch(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'brentwood') return { type: 'brentwood', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateBrentwood(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'pittsburg') return { type: 'pittsburg', ...((parcelAreaAcres && effectiveSlope !== null) ? calculatePittsburg(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'san_ramon') return { type: 'san_ramon', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSanRamon(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'pleasant_hill') return { type: 'pleasant_hill', ...((parcelAreaAcres && effectiveSlope !== null) ? calculatePleasantHill(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'clayton') return { type: 'clayton', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateClayton(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'moraga') return { type: 'moraga', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateMoraga(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'danville') return { type: 'danville', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateDanville(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'alamo') return { type: 'alamo', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateAlamo(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'oakley') return { type: 'oakley', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateOakley(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'san_pablo') return { type: 'san_pablo', ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSanPablo(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'portola_valley') return { type: 'portola_valley', ...((parcelAreaAcres && effectiveSlope !== null) ? calculatePortolaValley(effectiveSlope, parcelAreaAcres) : {}) };
            if (['santa_clara', 'milpitas', 'campbell', 'gilroy', 'morgan_hill'].includes(jurisdiction)) return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSantaClara(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'arnold' || jurisdiction === 'calaveras_generic') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateArnold(effectiveSlope, parcelAreaAcres) : {}) };

            // Marin County Cities
            if (jurisdiction === 'belvedere') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateBelvedere(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'corte_madera') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateCorteMadera(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'fairfax') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateFairfax(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'larkspur') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateLarkspur(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'mill_valley') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateMillValley(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'novato') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateNovato(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'ross') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateRoss(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'san_anselmo') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSanAnselmo(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'san_rafael') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSanRafael(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'sausalito') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSausalito(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'tiburon') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateTiburon(effectiveSlope, parcelAreaAcres) : {}) };

            // Napa County Cities
            if (jurisdiction === 'american_canyon') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateAmericanCanyon(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'calistoga') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateCalistoga(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'napa') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateNapaCity(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'st_helena') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateStHelena(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'yountville') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateYountville(effectiveSlope, parcelAreaAcres) : {}) };

            // Solano County Cities
            if (jurisdiction === 'benicia') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateBenicia(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'dixon') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateDixon(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'fairfield') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateFairfield(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'rio_vista') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateRioVista(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'suisun_city') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateSuisunCity(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'vacaville') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateVacaville(effectiveSlope, parcelAreaAcres) : {}) };
            if (jurisdiction === 'vallejo') return { type: jurisdiction, ...((parcelAreaAcres && effectiveSlope !== null) ? calculateVallejo(effectiveSlope, parcelAreaAcres) : {}) };

            if (jurisdiction === 'marin_generic' || jurisdiction === 'napa_generic' || jurisdiction === 'solano_generic') {
                // Fallback to simple baseline if needed
                const lotAreaFt2 = parcelAreaAcres * 43560.0;
                return { type: jurisdiction, mda: lotAreaFt2 * 0.35, mfa: lotAreaFt2 * 0.40, formulaLabel: 'Baseline', formulaValue: 0.40 };
            }
            return {};
        })();

        if (!baseResult.type) return { useBackendValues: hasBackendValues };

        let convertedValue = baseResult.formulaValue;
        const ratioBased = [
            'palo_alto', 'los_altos', 'cupertino', 'cupertino_hillside', 'san_jose', 'fremont', 'hillsborough', 'atherton', 'woodside', 'orinda', 'lafayette', 'mountain_view', 'sunnyvale', 'santa_clara', 'milpitas', 'oakland', 'alameda', 'berkeley', 'hayward', 'livermore', 'pleasanton', 'dublin', 'newark', 'union_city', 'san_leandro', 'emeryville', 'albany', 'piedmont', 'redwood_city', 'san_mateo', 'daly_city', 'south_san_francisco', 'foster_city', 'burlingame', 'belmont', 'san_carlos', 'menlo_park', 'millbrae', 'pacifica', 'half_moon_bay', 'walnut_creek', 'concord', 'richmond', 'antioch', 'brentwood', 'pittsburg', 'san_ramon', 'pleasant_hill', 'clayton', 'moraga', 'danville', 'alamo',
            'belvedere', 'corte_madera', 'fairfax', 'larkspur', 'mill_valley', 'novato', 'ross', 'san_anselmo', 'san_rafael', 'sausalito', 'tiburon', 'marin_generic',
            'american_canyon', 'calistoga', 'napa', 'st_helena', 'yountville', 'napa_generic',
            'benicia', 'dixon', 'fairfield', 'rio_vista', 'suisun_city', 'vacaville', 'vallejo', 'solano_generic',
            'oakley', 'san_pablo', 'portola_valley', 'los_gatos'
        ];

        if (ratioBased.includes(jurisdiction)) {
            convertedValue = parcelAreaAcres * baseResult.formulaValue;
        } else if (['saratoga', 'gilroy', 'morgan_hill', 'campbell'].includes(jurisdiction)) {
            convertedValue = baseResult.formulaValue / 43560.0;
        } else if (jurisdiction === 'monte_sereno') {
            convertedValue = parcelAreaAcres * (baseResult.formulaValue / 100.0);
        }

        return {
            ...baseResult,
            convertedValue: convertedValue,
            useBackendValues: hasBackendValues
        };
    }, [jurisdiction, parcelAreaAcres, estimatedSlope, avgSlope, mda, mfa, manualMfaStructures, gridSettings.size, gridSettings.unit, analysisStats?.contourInterval]);

    // Derived Stats for UI - Uses jurisdictionCalc for effective limits
    const totalStats = React.useMemo(() => {
        // Calculate already built from analysis
        // IMPORTANT: Prefer backend grossBuildingArea for consistency, fallback to dynamic analysis only if needed
        const currentUsed = grossBuildingArea ? parseFloat(grossBuildingArea) : (analysisStats ? analysisStats.buildingArea : 0);

        // Determine effective MDA/MFA limits - MUST match DEV CAPACITY panel calculation
        // Use backend values when available, otherwise use frontend jurisdictionCalc values
        // Improved fallback: use props if jurisdictionCalc hasn't produced specific values yet
        const effectiveMdaLimit = jurisdiction === 'los_gatos' ? null : ((mda !== undefined && mda !== null) ? mda : (jurisdictionCalc?.mda || 0));
        const effectiveMfaLimit = (mfa !== undefined && mfa !== null) ? mfa : (jurisdictionCalc?.mfa || 0);

        // Separate existing buildings into MDA and MFA using analysis data FIRST
        // Sum all manual existing structures of each type
        const manualExistMfaArea = testFitStructures
            .filter(s => s.type === 'EXISTING_MFA')
            .reduce((sum, s) => sum + (s.originalArea || s.area), 0);

        const manualExistMdaArea = testFitStructures
            .filter(s => s.type === 'EXISTING_MDA')
            .reduce((sum, s) => sum + (s.originalArea || s.area), 0);

        const tracedMfaArea = developmentAreaTrace?.existing_mfa_ft2 || 0;
        const tracedMdaArea = developmentAreaTrace?.known_existing_mda_ft2 || 0;
        const mapDetectedMdaArea = (analysisStats?.mdaBuildingArea || 0) || (
            (analysisStats?.mfaBuildingArea || 0) + (analysisStats?.accessoryBuildingArea || 0)
        );

        // Fallback to trace/map stats only if no manual structures are defined for that category.
        // MDA includes the main building footprint plus accessory/development footprints.
        const mfaAlreadyBuilt = manualExistMfaArea > 0
            ? manualExistMfaArea
            : (assessorFloorArea || tracedMfaArea || analysisStats?.mfaBuildingArea || 0);
        const mdaAlreadyBuilt = manualExistMdaArea > 0 ? manualExistMdaArea : (tracedMdaArea || mapDetectedMdaArea || 0);

        // Sum all manual MFA structures from our new array
        const totalManualMfaArea = manualMfaStructures.reduce((sum, s) => sum + (s.area || 0), 0);

        // MFA Stats - Include all MFA variants
        const mfaExtensions = testFitStructures.filter(s => s.type === 'EXTEND_MFA').reduce((sum, s) => sum + s.area, 0);
        const mfaNew = testFitStructures.filter(s => s.type === 'NEW_MFA').reduce((sum, s) => sum + s.area, 0);
        // For EXISTING_MFA, only count the delta (difference from original)
        const mfaExistingDelta = testFitStructures
            .filter(s => s.type === 'EXISTING_MFA')
            .reduce((sum, s) => sum + (s.area - (s.originalArea || s.area)), 0);
        const mfaPlaced = mfaExtensions + mfaNew + mfaExistingDelta;
        // Use mfaAlreadyBuilt instead of currentUsed for MFA calculations
        const mfaRemaining = effectiveMfaLimit - mfaAlreadyBuilt - mfaPlaced - totalManualMfaArea;

        // MDA Stats - Include all MDA variants
        const mdaNew = testFitStructures.filter(s => s.type === 'MDA' || s.type === 'NEW_MDA' || s.type === 'NEW_MFA').reduce((sum, s) => sum + s.area, 0);
        // For existing development footprints, count only the delta from edits.
        const mdaExistingDelta = testFitStructures
            .filter(s => s.type === 'EXISTING_MDA')
            .reduce((sum, s) => sum + (s.area - (s.originalArea || s.area)), 0);
        const mdaPlaced = mdaNew + mdaExistingDelta;
        // Use mdaAlreadyBuilt instead of currentUsed for MDA calculations
        const mdaRemaining = effectiveMdaLimit - mdaAlreadyBuilt - mdaPlaced;

        return {
            mfaLimit: effectiveMfaLimit,
            mfaPlaced,
            mfaRemaining,
            mdaLimit: effectiveMdaLimit,
            mdaPlaced,
            mdaRemaining,
            alreadyBuilt: currentUsed,
            mdaAlreadyBuilt,  // Existing MDA development footprint
            mfaAlreadyBuilt,  // Existing MFA (main) structures
            manualMfaOffset: totalManualMfaArea,
            isMfaOver: mfaRemaining < 0,
            isMdaOver: mdaRemaining < 0
        };
    }, [testFitStructures, grossBuildingArea, analysisStats, mda, mfa, jurisdictionCalc, manualMfaStructures, developmentAreaTrace, assessorFloorArea, jurisdiction]);

    // Auto-sync Existing MFA/MDA to DB snapshot if they change via reclassification
    useEffect(() => {
        if (!analysisId || !analysisStats) return;

        const syncSnapshot = async () => {
            try {
                if (totalStats.mfaAlreadyBuilt === analysisStats.mfaBuildingArea &&
                    totalStats.mdaAlreadyBuilt === analysisStats.mdaBuildingArea &&
                    totalStats.mfaPlaced === (analysisStats.proposedMfa || 0) &&
                    totalStats.mdaPlaced === (analysisStats.proposedMda || 0)) {
                    return;
                }

                const payload = {
                    mfa_building_area_sqft: totalStats.mfaAlreadyBuilt,
                    mda_building_area_sqft: totalStats.mdaAlreadyBuilt,
                    proposed_mfa_sqft: totalStats.mfaPlaced,
                    proposed_mda_sqft: totalStats.mdaPlaced
                };

                await axios.post(`${API_BASE}/api/map-analysis-snapshot/${analysisId}/update`, payload);
                console.log("🔄 Auto-synced reclassified building areas to terrain snapshot");

                setAnalysisStats(prev => ({
                    ...prev,
                    mfaBuildingArea: totalStats.mfaAlreadyBuilt,
                    mdaBuildingArea: totalStats.mdaAlreadyBuilt,
                    proposedMfa: totalStats.mfaPlaced,
                    proposedMda: totalStats.mdaPlaced
                }));
            } catch (err) {
                console.warn("Could not auto-sync reclassification to snapshot:", err.message);
            }
        };

        const timer = setTimeout(syncSnapshot, 1500);
        return () => clearTimeout(timer);
    }, [analysisId, totalStats.mfaAlreadyBuilt, totalStats.mdaAlreadyBuilt, totalStats.mfaPlaced, totalStats.mdaPlaced, analysisStats]);




    // Retry handler for network errors
    const handleRetry = useCallback(() => {
        if (retryCount < maxRetries) {
            setError(null);
            setRetryCount(prev => prev + 1);
            setLoadingStep("initializing");
            setBoundaryStatus("searching");
            setLocationInfo(null);
            // Cleanup existing map view
            if (mapView.current) {
                mapView.current.destroy();
                mapView.current = null;
            }
        }
    }, [retryCount]);

    // Grid generation function - defined at component level for reuse
    const buildGridAndCalculateSlope = async (boundaryGeometry, view, modules, settings) => {
        if (!boundaryGeometry || !modules || !view || !view.map) return;

        const {
            GraphicsLayer, ElevationLayer, geometryEngine, Graphic,
            SimpleFillSymbol, webMercatorUtils, Polygon, Query, query
        } = modules;

        setLoadingStep("processing-grid");
        setElevationError(null);
        if (!settings || !settings.size) return; // Prevent division by zero or invalid grid
        hasLoggedErrorRef.current = false;
        console.log("Starting grid generation with settings:", settings);

        // 1. Ensure Geometry is WebMercator (Meters)
        let metricBoundary = boundaryGeometry;
        if (boundaryGeometry.spatialReference && boundaryGeometry.spatialReference.isWGS84 && webMercatorUtils) {
            metricBoundary = webMercatorUtils.geographicToWebMercator(boundaryGeometry);
        }

        // 1.5 Query building footprints - Try multiple sources
        let buildingGeometries = [];
        console.log("🏗️ Starting building footprint detection...");
        console.log("📍 Boundary extent:", metricBoundary.extent);

        const layers = GIS_LAYERS[county] || GIS_LAYERS.SCC;

        // Source 1: USA Structures (FEMA/DHS dataset - very reliable for US)
        try {
            const usaStructuresUrl = layers.USA_STRUCTURES;

            const buildingQuery = new Query();
            buildingQuery.geometry = metricBoundary;
            buildingQuery.geometryType = "esriGeometryPolygon";
            buildingQuery.spatialRelationship = "esriSpatialRelIntersects";
            buildingQuery.returnGeometry = true;
            buildingQuery.outSpatialReference = { wkid: 3857 };
            buildingQuery.outFields = ["*"];

            console.log("🔍 Querying USA Structures database...");
            const buildingResults = await query.executeQueryJSON(usaStructuresUrl, buildingQuery);

            if (buildingResults.features && buildingResults.features.length > 0) {
                buildingGeometries = buildingResults.features.map(f => f.geometry);
                console.log(`✅ Found ${buildingGeometries.length} building footprints from USA Structures`);
            } else {
                console.log("ℹ️ No buildings found in USA Structures layer");
            }
        } catch (usaErr) {
            console.warn("USA Structures query failed:", usaErr.message || usaErr);
        }

        // Source 2: Microsoft Building Footprints (if Source 1 failed)
        if (buildingGeometries.length === 0) {
            try {
                const msBuildingUrl = layers.MS_BUILDINGS;

                const buildingQuery = new Query();
                buildingQuery.geometry = metricBoundary;
                buildingQuery.geometryType = "esriGeometryPolygon";
                buildingQuery.spatialRelationship = "esriSpatialRelIntersects";
                buildingQuery.returnGeometry = true;
                buildingQuery.outSpatialReference = { wkid: 3857 };
                buildingQuery.outFields = ["*"];

                console.log("🔍 Querying Microsoft Building Footprints...");
                const buildingResults = await query.executeQueryJSON(msBuildingUrl, buildingQuery);

                if (buildingResults.features && buildingResults.features.length > 0) {
                    buildingGeometries = buildingResults.features.map(f => f.geometry);
                    console.log(`✅ Found ${buildingGeometries.length} building footprints from Microsoft Buildings`);
                } else {
                    console.log("ℹ️ No buildings found in Microsoft Buildings layer");
                }
            } catch (msErr) {
                console.warn("Microsoft Building Footprints query failed:", msErr.message || msErr);
            }
        }

        // Source 3: County-specific building database (final fallback)
        if (buildingGeometries.length === 0) {
            try {
                console.log(`🔍 Trying ${county} building database...`);
                const buildingUrl = layers.BUILDINGS;
                const geomServiceUrl = `${layers.GEOMETRY_SERVICE}/project`;

                // Properly serialize the geometry for projection
                const geometryToProject = {
                    rings: metricBoundary.rings,
                    spatialReference: { wkid: 3857 }
                };

                const boundaryInFeet = await fetch(geomServiceUrl, {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: new URLSearchParams({
                        f: "json",
                        inSR: "3857",
                        outSR: resolvedWkid.toString(),
                        geometries: JSON.stringify({
                            geometryType: "esriGeometryPolygon",
                            geometries: [geometryToProject]
                        })
                    })
                }).then(r => r.json());

                console.log("📐 Geometry projection response:", boundaryInFeet);

                if (boundaryInFeet.geometries && boundaryInFeet.geometries.length > 0) {
                    // Create actual Polygon instance to avoid Accessor#set error (missing type: 'polygon')
                    const projectedBoundary = new Polygon({
                        rings: boundaryInFeet.geometries[0].rings,
                        spatialReference: { wkid: resolvedWkid }
                    });

                    const buildingQuery = new Query();
                    buildingQuery.geometry = projectedBoundary;
                    buildingQuery.geometryType = "esriGeometryPolygon";
                    buildingQuery.spatialRelationship = "esriSpatialRelIntersects";
                    buildingQuery.returnGeometry = true;
                    buildingQuery.outSpatialReference = { wkid: 3857 };
                    buildingQuery.outFields = ["*"];

                    const buildingResults = await query.executeQueryJSON(buildingUrl, buildingQuery);
                    if (buildingResults.features && buildingResults.features.length > 0) {
                        buildingGeometries = buildingResults.features.map(f => f.geometry);
                        console.log(`✅ Found ${buildingGeometries.length} building footprints from ${county}`);
                    } else {
                        console.log(`ℹ️ No buildings found in ${county} layer`);
                    }
                }
            } catch (err) {
                console.warn(`${county} building query failed:`, err.message || err);
            }
        }

        if (buildingGeometries.length === 0) {
            console.warn("⚠️ No building footprints found from any source. Buildings will not be highlighted.");
        } else {
            console.log(`🏗️ Building detection (raw) complete. Total buildings found: ${buildingGeometries.length}`);

            // FILTER: Only keep buildings whose centroid is within our parcel boundary
            // This prevents neighbor building footprints from appearing if they slightly touch the line
            if (metricBoundary && geometryEngine) {
                const countBefore = buildingGeometries.length;
                buildingGeometries = buildingGeometries.filter(bldg => {
                    try {
                        const poly = new Polygon({
                            rings: bldg.rings,
                            spatialReference: bldg.spatialReference || { wkid: 3857 }
                        });
                        const centroid = poly.extent ? poly.extent.center : null;
                        if (!centroid) return true;

                        // Return true if centroid is WITHIN the parcel boundary
                        return geometryEngine.within(centroid, metricBoundary);
                    } catch (err) {
                        return true; // Keep if we can't determine (safety)
                    }
                });
                console.log(`🎯 Centroid-in-Parcel filter: ${countBefore} -> ${buildingGeometries.length} buildings`);
            }
        }

        // 2. Create/Clear Grid Layer
        if (!view.map) return;
        let gridLayer = view.map.findLayerById("grid-layer");
        if (!gridLayer) {
            gridLayer = new GraphicsLayer({ id: "grid-layer", title: "Slope Analysis Grid" });
            view.map.add(gridLayer);
        }
        gridLayer.removeAll();

        // 3. Elevation Layer
        const elevationLayer = new ElevationLayer({
            url: "https://elevation3d.arcgis.com/arcgis/rest/services/WorldElevation3D/Terrain3D/ImageServer"
        });

        // 4. Define Grid Size
        // Convert input to meters
        let gridSizeMeters = 3.048; // Default 10ft

        if (settings.unit === 'sqft') {
            // If sqft, user inputs Area. Side = sqrt(Area).
            const sideFt = Math.sqrt(settings.size);
            gridSizeMeters = sideFt * 0.3048;
        } else {
            // linear feet
            gridSizeMeters = settings.size * 0.3048;
        }

        // Safety clamp: minimum 3ft (approx 1m) to prevent crash
        if (gridSizeMeters < 0.9) gridSizeMeters = 0.9;

        // Ensure extent exists
        let extent = metricBoundary.extent;
        if (!extent) {
            const rings = metricBoundary.rings || [];
            let xmin = Infinity, ymin = Infinity, xmax = -Infinity, ymax = -Infinity;
            rings.forEach(ring => {
                ring.forEach(([x, y]) => {
                    xmin = Math.min(xmin, x);
                    ymin = Math.min(ymin, y);
                    xmax = Math.max(xmax, x);
                    ymax = Math.max(ymax, y);
                });
            });
            extent = { xmin, ymin, xmax, ymax };
        }

        const gridPolygons = [];
        // Reset stats
        setAnalysisStats(null);
        let currentStats = {
            flat: 0, moderate: 0, steep: 0, building: 0, accessory: 0, total: 0,
            flatArea: 0, moderateArea: 0, steepArea: 0, buildingArea: 0, accessoryArea: 0, totalArea: 0,
            contourInterval
        };

        // Classify buildings by size: MFA (main buildings) vs MDA (accessory structures)
        // Threshold: 400 sq ft (~37 sq meters) - smaller structures are likely pools, sheds, garages
        const ACCESSORY_THRESHOLD_SQFT = 400;
        const ACCESSORY_THRESHOLD_SQM = ACCESSORY_THRESHOLD_SQFT * 0.0929; // ~37 sq meters

        // Filter out invalid geometries and create proper Polygon objects
        const buildingPolygons = buildingGeometries
            .filter(bldg => bldg && bldg.rings && bldg.rings.length > 0)
            .map(bldg => {
                try {
                    const polygon = new Polygon({
                        rings: bldg.rings,
                        spatialReference: { wkid: 3857 }
                    });
                    // Calculate area in square meters (Web Mercator)
                    const areaSqM = Math.abs(geometryEngine.geodesicArea(polygon, "square-meters"));
                    const isAccessory = areaSqM < ACCESSORY_THRESHOLD_SQM;
                    return { polygon, isAccessory };
                } catch (err) {
                    console.warn("Invalid building geometry skipped:", err.message);
                    return null;
                }
            })
            .filter(item => item !== null);

        // Keep detected footprint metadata for totals only. We do not draw these
        // as map structures because source footprints can be offset/generalized
        // and visually misrepresent the actual editable area.
        const existingBuildingsLayer = view.map.findLayerById("buildings-layer");
        if (existingBuildingsLayer) existingBuildingsLayer.removeAll();
        buildingsLayerRef.current = null;

        const buildingGraphicsData = buildingPolygons.map((bldgData, index) => {
            const id = `building - ${Date.now()} -${index} `;
            const areaSqFt = Math.abs(geometryEngine.geodesicArea(bldgData.polygon, "square-feet"));

            return {
                id: id,
                polygon: bldgData.polygon.clone(),
                areaSqFt: areaSqFt,
                isAccessory: bldgData.isAccessory
            };
        });

        originalBuildingsRef.current = buildingGraphicsData;
        console.log(`🏠 Detected ${buildingGraphicsData.length} building footprints for stats only`);

        const accessoryBuildingArea = buildingGraphicsData
            .filter(b => b.isAccessory)
            .reduce((sum, b) => sum + b.areaSqFt, 0);

        const mfaBuildingArea = buildingGraphicsData
            .filter(b => !b.isAccessory)
            .reduce((sum, b) => sum + b.areaSqFt, 0);

        // Existing MDA includes all detected development footprints. Vector data
        // covers buildings here; pools/pathways/driveways are handled by the
        // server trace/manual drawing flow until aerial segmentation is added.
        const mdaBuildingArea = mfaBuildingArea + accessoryBuildingArea;

        console.log(`📊 Building Area Summary: MDA = ${Math.round(mdaBuildingArea)} sqft, MFA = ${Math.round(mfaBuildingArea)} sqft`);

        // Store in current stats for later use
        currentStats.mdaBuildingArea = mdaBuildingArea;
        currentStats.mfaBuildingArea = mfaBuildingArea;
        currentStats.accessoryBuildingArea = accessoryBuildingArea;
        currentStats.buildingArea = mfaBuildingArea;



        // 5. Generate Grid Cells
        for (let x = extent.xmin; x < extent.xmax; x += gridSizeMeters) {
            for (let y = extent.ymin; y < extent.ymax; y += gridSizeMeters) {
                const polygon = new Polygon({
                    rings: [[
                        [x, y],
                        [x + gridSizeMeters, y],
                        [x + gridSizeMeters, y + gridSizeMeters],
                        [x, y + gridSizeMeters],
                        [x, y]
                    ]],
                    spatialReference: metricBoundary.spatialReference
                });

                if (geometryEngine.intersects(metricBoundary, polygon)) {
                    let isBuilding = false;
                    for (const bldgPoly of buildingPolygons) {
                        if (geometryEngine.intersects(bldgPoly.polygon, polygon)) {
                            isBuilding = true;
                            break;
                        }
                    }

                    const clippedGeometry = geometryEngine.intersect(metricBoundary, polygon);
                    if (clippedGeometry) {
                        gridPolygons.push({ geometry: clippedGeometry, isBuilding });
                    }
                }
            }
        }

        const MAX_CELLS = 15000;
        const cellsToProcess = gridPolygons.length > MAX_CELLS ? gridPolygons.slice(0, MAX_CELLS) : gridPolygons;

        if (gridPolygons.length > MAX_CELLS) {
            console.warn(`Grid too large(${gridPolygons.length} cells).Limiting to ${MAX_CELLS}.`);
        }

        // 6. Calculate Slope (ArcGIS standard 5-point)
        const processBatch = async (batch) => {
            const queryPoints = [];

            // For slope calc, we need a small delta. 
            // Using logic from before:
            // The run length for calculation should strictly be related to the grid size OR a fixed small value
            // effectively determining the resolution of the slope. 
            // Previous code used 10ft (3.048m) fixed run. 
            // Let's stick to the gridSizeMeters as the run to adapt to resolution.
            const run = gridSizeMeters;

            batch.forEach((cellData, index) => {
                const center = cellData.geometry.extent.center;
                queryPoints.push([center.x, center.y]);           // Center
                queryPoints.push([center.x + run, center.y]);     // East
                queryPoints.push([center.x - run, center.y]);     // West
                queryPoints.push([center.x, center.y + run]);     // North
                queryPoints.push([center.x, center.y - run]);     // South
            });

            const multipoint = {
                type: "multipoint",
                points: queryPoints,
                spatialReference: batch[0].geometry.spatialReference
            };

            try {
                const elevationResult = await elevationLayer.queryElevation(multipoint);
                const points = elevationResult.geometry.points;
                const batchGraphics = [];

                for (let i = 0; i < batch.length; i++) {
                    const cellData = batch[i];

                    const baseIdx = i * 5;
                    const zCenter = points[baseIdx][2];
                    const zEast = points[baseIdx + 1][2];
                    const zWest = points[baseIdx + 2][2];
                    const zNorth = points[baseIdx + 3][2];
                    const zSouth = points[baseIdx + 4][2];

                    const dzdx = (zEast - zWest) / (2 * run);
                    const dzdy = (zNorth - zSouth) / (2 * run);
                    const rise_run = Math.sqrt(Math.pow(dzdx, 2) + Math.pow(dzdy, 2));
                    const slopeDegrees = Math.atan(rise_run) * (180 / Math.PI);
                    const slopePct = Math.tan(slopeDegrees * Math.PI / 180) * 100;

                    if (isNaN(slopePct)) continue;

                    if (isNaN(slopePct)) continue;

                    let color = [34, 197, 94, 0.25];
                    let label = "Flat";
                    let strokeColor = [22, 163, 74, 0.5];
                    let hexColor = "#22c55e";
                    let recommendation = "Generally suitable for standard slab or perimeter foundation.";
                    let costFactor = "Low";

                    if (slopePct >= 10 && slopePct < 20) {
                        color = [234, 179, 8, 0.35];
                        label = "Moderate";
                        strokeColor = [202, 138, 4, 0.6];
                        hexColor = "#eab308";
                        recommendation = "May require stepped foundation or minor retaining walls. Grading likely needed.";
                        costFactor = "Medium";
                    } else if (slopePct >= 20 && slopePct < 30) {
                        color = [249, 115, 22, 0.45];
                        label = "Steep";
                        strokeColor = [234, 88, 12, 0.7];
                        hexColor = "#f97316";
                        recommendation = "Complex foundation required. Geotechnical report essential. Significant grading/retaining walls.";
                        costFactor = "High";
                    } else if (slopePct >= 30) {
                        color = [239, 68, 68, 0.55];
                        label = "Critical";
                        strokeColor = [220, 38, 38, 0.8];
                        hexColor = "#ef4444";
                        recommendation = "Critical slope. Specialized engineering required. May be non-buildable or require variance.";
                        costFactor = "Very High";
                    }

                    const elevFt = (zCenter * 3.28084).toFixed(1);

                    const graphic = new Graphic({
                        geometry: cellData.geometry,
                        symbol: new SimpleFillSymbol({
                            color: color,
                            outline: { color: strokeColor, width: 1 }
                        }),
                        attributes: {
                            slope: slopePct.toFixed(1),
                            slopeDegrees: slopeDegrees.toFixed(1),
                            label: label,
                            elevation: elevFt,
                            recommendation: recommendation,
                            costFactor: costFactor,
                            hexColor: hexColor
                        },
                        popupTemplate: {
                            title: `< div style = "display: flex; align-items: center; gap: 6px;" > <span>📊</span> Terrain Analysis</div > `,
                            content: `
    < div style = "font-family: 'Inter', system-ui, sans-serif; min-width: 280px; background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%); border-radius: 12px; overflow: hidden;" >
                                    < !--Hero Section-- >
                                    <div style="background: linear-gradient(135deg, {hexColor}12 0%, {hexColor}06 100%); padding: 16px; border-bottom: 1px solid #e2e8f0;">
                                        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                                            <div>
                                                <div style="font-size: 36px; font-weight: 800; color: {hexColor}; line-height: 1;">{slope}<span style="font-size: 20px;">%</span></div>
                                                <div style="font-size: 10px; color: #64748b; margin-top: 4px; text-transform: uppercase; letter-spacing: 1px; font-weight: 600;">Slope Gradient</div>
                                            </div>
                                            <div style="text-align: right; background: white; padding: 10px 12px; border-radius: 10px; border: 1px solid #e2e8f0;">
                                                <div style="font-size: 18px; font-weight: 700; color: #1e293b;">{slopeDegrees}°</div>
                                                <div style="font-size: 9px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px;">Angle</div>
                                            </div>
                                        </div>
                                        <div style="margin-top: 10px; display: inline-flex; align-items: center; gap: 6px; background: white; border: 1.5px solid {hexColor}30; padding: 6px 12px; border-radius: 99px;">
                                            <div style="width: 8px; height: 8px; background: {hexColor}; border-radius: 50%;"></div>
                                            <span style="font-size: 11px; font-weight: 700; color: {hexColor}; text-transform: uppercase; letter-spacing: 0.5px;">{label}</span>
                                        </div>
                                    </div>

                                    <!--Metrics Grid-- >
                                    <div style="padding: 12px; display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
                                        <div style="background: #f1f5f9; border: 1px solid #e2e8f0; padding: 10px; border-radius: 10px; text-align: center;">
                                            <div style="font-size: 9px; color: #64748b; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; margin-bottom: 4px;">⛰️ Elevation</div>
                                            <div style="font-size: 16px; font-weight: 700; color: #1e293b;">{elevation}<span style="font-size: 11px; color: #64748b;"> ft</span></div>
                                        </div>
                                        <div style="background: #f1f5f9; border: 1px solid #e2e8f0; padding: 10px; border-radius: 10px; text-align: center;">
                                            <div style="font-size: 9px; color: #64748b; text-transform: uppercase; font-weight: 600; letter-spacing: 0.5px; margin-bottom: 4px;">📐 Grid</div>
                                            <div style="font-size: 16px; font-weight: 700; color: #1e293b;">${settings.size}<span style="font-size: 11px; color: #64748b;"> ${settings.unit}</span></div>
                                        </div>
                                    </div>

                                    <!--Constructability -->
                                    <div style="padding: 0 12px 12px 12px;">
                                        <div style="background: white; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; position: relative; overflow: hidden;">
                                            <div style="position: absolute; top: 0; left: 0; width: 3px; height: 100%; background: {hexColor};"></div>
                                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; padding-left: 8px;">
                                                <div style="display: flex; align-items: center; gap: 4px;">
                                                    <span style="font-size: 12px;">🏗️</span>
                                                    <span style="font-size: 10px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px;">Constructability</span>
                                                </div>
                                                <div style="background: {hexColor}15; border: 1px solid {hexColor}25; padding: 3px 8px; border-radius: 99px;">
                                                    <span style="font-size: 9px; font-weight: 700; color: {hexColor};">💰 {costFactor}</span>
                                                </div>
                                            </div>
                                            <div style="font-size: 12px; color: #475569; line-height: 1.5; padding-left: 16px;">{recommendation}</div>
                                        </div>
                                    </div>
                                </div >
    `
                        }
                    });
                    batchGraphics.push(graphic);
                }
                return batchGraphics;
            } catch (e) {
                // Handle network/service errors gracefully
                if (!hasLoggedErrorRef.current) {
                    console.error("Error batch calculating slope:", e);
                    setElevationError("Terrain data is currently unavailable. Slope analysis could not be performed.");
                    hasLoggedErrorRef.current = true;
                }
                return [];
            }
        };

        const BATCH_SIZE = 250;
        for (let i = 0; i < cellsToProcess.length; i += BATCH_SIZE) {
            const batch = cellsToProcess.slice(i, i + BATCH_SIZE);
            const graphics = await processBatch(batch);

            // Accumulate stats
            graphics.forEach(g => {
                const areaSqFt = geometryEngine.geodesicArea(g.geometry, "square-feet");

                if (g.attributes.label === "Flat") {
                    currentStats.flat++;
                    currentStats.flatArea += areaSqFt;
                }
                else if (g.attributes.label === "Moderate") {
                    currentStats.moderate++;
                    currentStats.moderateArea += areaSqFt;
                }
                else if (g.attributes.label === "Steep" || g.attributes.label === "Critical") {
                    currentStats.steep++;
                    currentStats.steepArea += areaSqFt;
                }
                currentStats.total++;
                currentStats.totalArea += areaSqFt;
            });

            gridLayer.addMany(graphics);

            // Update stats incrementally for live feedback (optional, but nice)
            if (i % 1000 === 0) setAnalysisStats({ ...currentStats });

            await new Promise(r => setTimeout(r, 20));
        }

        setAnalysisStats(currentStats);

        if (onAnalysisComplete) {
            onAnalysisComplete(currentStats);
        }

        if (analysisId) {
            const buildFingerprint = (s) => [
                Math.round(s.flat || 0),
                Math.round(s.moderate || 0),
                Math.round(s.steep || 0),
                Math.round(s.building || 0),
                Math.round(s.flatArea || 0),
                Math.round(s.moderateArea || 0),
                Math.round(s.steepArea || 0),
                Math.round(s.buildingArea || 0),
                Math.round(s.mfaBuildingArea || 0),
                Math.round(s.mdaBuildingArea || 0),
                Math.round(s.totalArea || 0),
            ].join('|');

            const newFingerprint = buildFingerprint(currentStats);
            if (lastSavedSnapshotRef.current === newFingerprint) {
                console.log('📸 Map snapshot unchanged — skipping save');
            } else {
                axios.get(`${API_BASE}/api/map-analysis-snapshot/${analysisId}`)
                    .then(res => {
                        const snap = res.data?.snapshot;
                        if (snap) {
                            const dbFingerprint = [
                                Math.round(snap.flat_cells || 0),
                                Math.round(snap.moderate_cells || 0),
                                Math.round(snap.steep_cells || 0),
                                Math.round(snap.building_cells || 0),
                                Math.round(snap.flat_area_sqft || 0),
                                Math.round(snap.moderate_area_sqft || 0),
                                Math.round(snap.steep_area_sqft || 0),
                                Math.round(snap.building_area_sqft || 0),
                                Math.round(snap.mfa_building_area_sqft || 0),
                                Math.round(snap.mda_building_area_sqft || 0),
                                Math.round(snap.total_area_sqft || 0),
                            ].join('|');

                            if (dbFingerprint === newFingerprint) {
                                console.log('📸 Map snapshot matches DB — no update needed');
                                lastSavedSnapshotRef.current = newFingerprint;
                                return null;
                            }
                        }

                        return axios.post(`${API_BASE}/api/map-analysis-snapshot`, {
                            analysis_id: analysisId,
                            address: address || null,
                            apn: apn || null,
                            flat_cells: currentStats.flat || 0,
                            moderate_cells: currentStats.moderate || 0,
                            steep_cells: currentStats.steep || 0,
                            building_cells: currentStats.building || 0,
                            total_cells: currentStats.total || 0,
                            flat_area_sqft: currentStats.flatArea || 0,
                            moderate_area_sqft: currentStats.moderateArea || 0,
                            steep_area_sqft: currentStats.steepArea || 0,
                            building_area_sqft: currentStats.buildingArea || 0,
                            accessory_area_sqft: currentStats.accessoryArea || 0,
                            total_area_sqft: currentStats.totalArea || 0,
                            mfa_building_area_sqft: currentStats.mfaBuildingArea || 0,
                            mda_building_area_sqft: currentStats.mdaBuildingArea || 0,
                            mfa_limit_sqft: mfa || null,
                            mda_limit_sqft: mda || null,
                            avg_slope_used: avgSlope || null,
                            contour_interval_ft: currentStats.contourInterval || null,
                            grid_size_ft: settings?.size || null,
                        });
                    })
                    .then(res => {
                        if (res) {
                            lastSavedSnapshotRef.current = newFingerprint;
                            console.log('📸 Map analysis snapshot saved to DB (data changed)');
                        }
                    })
                    .catch(err => console.warn('⚠️ Failed to save map snapshot:', err.message));
            }
        }
        setLoadingStep("complete");
    };

    const handleUpdateGrid = () => {
        if (!boundaryGeometryRef.current || !mapView.current || !mapView.current.map) return;
        buildGridAndCalculateSlope(
            boundaryGeometryRef.current,
            mapView.current,
            modulesRef.current,
            gridSettings
        );
    };

    // Handle View Mode Switching
    useEffect(() => {
        if (!mapView.current || !mapView.current.map) return;

        const layer = mapView.current.map.findLayerById("grid-layer");
        if (!layer || layer.graphics.length === 0) return;

        // We need the SimpleFillSymbol constructor
        const SimpleFillSymbol = modulesRef.current.SimpleFillSymbol;
        if (!SimpleFillSymbol) return;

        layer.graphics.forEach(graphic => {
            const type = graphic.attributes.label; // Flat, Moderate, Steep, Critical, Building

            let color, outlineColor, outlineWidth = 1;

            if (viewMode === "development") {
                // DEVELOPMENT VIEW
                if (type === "Building") {
                    // Highlight Buildings
                    color = [56, 189, 248, 0.85]; // Light Blue for buildings
                    outlineColor = [14, 165, 233, 1];
                    outlineWidth = 2;
                } else if (type === "Flat" || type === "Moderate") {
                    // Highlight Feasible Areas (Future Dev)
                    // Unified Green for "Buildable"
                    color = [34, 197, 94, 0.4];
                    outlineColor = [22, 163, 74, 0.6];
                } else {
                    // Dim Restricted Areas (Steep/Critical)
                    color = [200, 200, 200, 0.1]; // Very faint grey
                    outlineColor = [200, 200, 200, 0.2];
                }
            } else {
                // STANDARD VIEW (Original Logic)
                if (type === "Building") {
                    color = [56, 189, 248, 0.8]; // Light Blue for buildings
                    outlineColor = [14, 165, 233, 1];
                    outlineWidth = 2;
                } else if (type === "Flat") {
                    color = [34, 197, 94, 0.25];
                    outlineColor = [22, 163, 74, 0.5];
                } else if (type === "Moderate") {
                    color = [234, 179, 8, 0.35];
                    outlineColor = [202, 138, 4, 0.6];
                } else if (type === "Steep") {
                    color = [249, 115, 22, 0.45];
                    outlineColor = [234, 88, 12, 0.7];
                } else { // Critical
                    color = [239, 68, 68, 0.55];
                    outlineColor = [220, 38, 38, 0.8];
                }
            }

            graphic.symbol = new SimpleFillSymbol({
                color: color,
                outline: { color: outlineColor, width: outlineWidth }
            });
        });

    }, [viewMode, analysisStats]); // Run when mode changes or new grid is generated

    // Handle Grid Visibility Toggle
    useEffect(() => {
        if (!mapView.current || !mapView.current.map) return;

        const layer = mapView.current.map.findLayerById("grid-layer");
        if (!layer) return;

        layer.visible = showGrid;
    }, [showGrid]);

    // Render the same queryable contour features used by the backend.
    useEffect(() => {
        if (!mapView.current || !mapView.current.map) return;

        const layer = mapView.current.map.findLayerById("contour-layer");
        const demLayer = mapView.current.map.findLayerById("dem-contour-layer");
        if (!layer && !demLayer) return;

        if (demLayer) {
            demLayer.visible = showContours;
        }

        if (layer) {
            layer.visible = showContours && !demLayer;
            layer.definitionExpression = "contourelevation IS NOT NULL";
        }

        if (showContours) {
            mapView.current.map.reorder(demLayer || layer, mapView.current.map.layers.length - 1);
            console.log(demLayer
                ? `🔝 Showing generated ${contourInterval}ft DEM contours`
                : `🔝 Showing queryable ${contourInterval}ft source contours`);
        }
    }, [showContours, contourInterval]);

    // Handle View Filter - Show/Hide specific grid cell types and buildings
    useEffect(() => {
        if (!mapView.current || !mapView.current.map) return;

        const SimpleFillSymbol = modulesRef.current.SimpleFillSymbol;
        if (!SimpleFillSymbol) return;

        // Filter terrain grid layer
        const gridLayer = mapView.current.map.findLayerById("grid-layer");
        if (gridLayer && gridLayer.graphics.length > 0) {
            gridLayer.graphics.forEach(graphic => {
                const type = graphic.attributes.label; // Flat, Moderate, Steep, Critical, Building
                let shouldShow = false;

                // If "all" is selected, show everything
                if (viewFilter.includes("all")) {
                    shouldShow = true;
                } else {
                    // Check each selected filter
                    viewFilter.forEach(filter => {
                        switch (filter) {
                            case "structures":
                                if (type === "Building") shouldShow = true;
                                break;
                            case "mfa":
                            case "mda":
                                // MFA and MDA filters should NOT show grid-layer Building cells
                                // They show buildings-layer and test-fit-layer instead
                                if (type === "Building") shouldShow = false;
                                break;
                            case "flat":
                                if (type === "Flat") shouldShow = true;
                                break;
                            case "moderate":
                                if (type === "Moderate") shouldShow = true;
                                break;
                            case "buildable":
                                if (type === "Flat" || type === "Moderate") shouldShow = true;
                                break;
                            case "steep":
                                if (type === "Steep" || type === "Critical") shouldShow = true;
                                break;
                            default:
                                // No action needed for unknown filters
                                break;
                        }
                    });
                }

                // Update visibility through opacity
                if (!shouldShow) {
                    graphic.symbol = new SimpleFillSymbol({
                        color: [0, 0, 0, 0], // Fully transparent
                        outline: { color: [0, 0, 0, 0], width: 0 }
                    });
                } else {
                    // Re-apply the original colors based on viewMode and type
                    let color, outlineColor, outlineWidth = 1;

                    if (viewMode === "development") {
                        if (type === "Building") {
                            color = [56, 189, 248, 0.85];
                            outlineColor = [14, 165, 233, 1];
                            outlineWidth = 2;
                        } else if (type === "Flat" || type === "Moderate") {
                            color = [34, 197, 94, 0.4];
                            outlineColor = [22, 163, 74, 0.6];
                        } else {
                            color = [200, 200, 200, 0.1];
                            outlineColor = [200, 200, 200, 0.2];
                        }
                    } else {
                        if (type === "Building") {
                            color = [56, 189, 248, 0.8];
                            outlineColor = [14, 165, 233, 1];
                            outlineWidth = 2;
                        } else if (type === "Flat") {
                            color = [34, 197, 94, 0.25];
                            outlineColor = [22, 163, 74, 0.5];
                        } else if (type === "Moderate") {
                            color = [234, 179, 8, 0.35];
                            outlineColor = [202, 138, 4, 0.6];
                        } else if (type === "Steep") {
                            color = [249, 115, 22, 0.45];
                            outlineColor = [234, 88, 12, 0.7];
                        } else {
                            color = [239, 68, 68, 0.55];
                            outlineColor = [220, 38, 38, 0.8];
                        }
                    }

                    graphic.symbol = new SimpleFillSymbol({
                        color: color,
                        outline: { color: outlineColor, width: outlineWidth }
                    });
                }
            });
        }

        // Filter buildings layer (MDA and MFA structures from building footprints)
        const buildingsLayer = mapView.current.map.findLayerById("buildings-layer");
        if (buildingsLayer && buildingsLayer.graphics.length > 0) {
            buildingsLayer.graphics.forEach(graphic => {
                const isAccessory = graphic.attributes.isAccessory;
                let shouldShow = false;

                // If "all" is selected, show everything
                if (viewFilter.includes("all")) {
                    shouldShow = true;
                } else {
                    // Check each selected filter
                    viewFilter.forEach(filter => {
                        if (filter === "structures") {
                            // "structures" filter does NOT show buildings-layer (only grid-layer blue cells)
                            // Keep shouldShow = false
                        } else if (filter === "mda") {
                            // Show only accessory structures (purple)
                            if (isAccessory) shouldShow = true;
                        } else if (filter === "mfa") {
                            // Show only main structures (blue) - existing MFA buildings
                            if (!isAccessory) shouldShow = true;
                        }
                    });
                }

                // Update visibility through opacity
                if (!shouldShow) {
                    graphic.symbol = new SimpleFillSymbol({
                        color: [0, 0, 0, 0], // Fully transparent
                        outline: { color: [0, 0, 0, 0], width: 0 }
                    });
                } else {
                    if (isAccessory) {
                        graphic.symbol = new SimpleFillSymbol({
                            color: [168, 85, 247, 0.6],
                            outline: { color: [139, 92, 246, 1], width: 2 }
                        });
                    } else {
                        const hasUserdefinedExistingMfa = testFitStructures.some(s => s.type === 'EXISTING_MFA');

                        if (hasUserdefinedExistingMfa) {
                            graphic.symbol = new SimpleFillSymbol({
                                color: [0, 0, 0, 0],
                                outline: { color: [0, 0, 0, 0], width: 0 }
                            });
                        } else {
                            graphic.symbol = new SimpleFillSymbol({
                                color: [56, 189, 248, 0.6],
                                outline: { color: [14, 165, 233, 1], width: 2 }
                            });
                        }
                    }
                }
            });
        }

        // Filter test-fit layer (custom MDA and MFA structures added by user)
        const testFitLayer = mapView.current.map.findLayerById("test-fit-layer");
        if (testFitLayer && testFitLayer.graphics.length > 0) {
            testFitLayer.graphics.forEach(graphic => {
                const type = graphic.attributes.type; // 'MDA', 'NEW_MFA', 'EXTEND_MFA', 'NEW_MDA', 'EXISTING_MFA', 'EXISTING_MDA'
                let shouldShow = false;

                // If "all" is selected, show everything
                if (viewFilter.includes("all")) {
                    shouldShow = true;
                } else {
                    // Check each selected filter
                    viewFilter.forEach(filter => {
                        if (filter === "structures") {
                            // "structures" filter does NOT show test-fit layer (only grid-layer blue cells)
                            // Keep shouldShow = false
                        } else if (filter === "mda") {
                            // Show ALL MDA variants (MDA, NEW_MDA, EXISTING_MDA)
                            if (type === "MDA" || type === "NEW_MDA" || type === "EXISTING_MDA") shouldShow = true;
                            // Also show labels for these
                            if (type === "label") {
                                const parent = testFitLayer.graphics.find(g => g.attributes.id === graphic.attributes.parentId);
                                const pType = parent?.attributes?.type;
                                if (pType === "MDA" || pType === "NEW_MDA" || pType === "EXISTING_MDA") shouldShow = true;
                            }
                        } else if (filter === "mfa") {
                            // Show ALL MFA variants (NEW_MFA, EXISTING_MFA, EXTEND_MFA)
                            if (type === "NEW_MFA" || type === "EXISTING_MFA" || type === "EXTEND_MFA") shouldShow = true;
                            // Also show labels for these
                            if (type === "label") {
                                const parent = testFitLayer.graphics.find(g => g.attributes.id === graphic.attributes.parentId);
                                const pType = parent?.attributes?.type;
                                if (pType === "NEW_MFA" || pType === "EXISTING_MFA" || pType === "EXTEND_MFA") shouldShow = true;
                            }
                        }
                    });
                }

                // Update visibility
                if (!shouldShow) {
                    graphic.visible = false;
                } else {
                    graphic.visible = true;
                }
            });
        }


    }, [viewFilter, viewMode, analysisStats, testFitStructures]);

    // Keyboard Shortcuts
    useEffect(() => {
        if (!isOpen && !isInline) return;

        const handleKeyDown = (e) => {
            if (e.key === "Escape") {
                if (isFullscreen) {
                    setIsFullscreen(false);
                } else if (onClose) {
                    onClose();
                }
            }
            // F key for fullscreen
            if (e.key === "f" || e.key === "F") {
                if (e.target.tagName !== "INPUT") {
                    setIsFullscreen(prev => !prev);
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, isFullscreen, onClose, isInline]);

    // Screenshot function
    const handleScreenshot = useCallback(async (silent = false, keySuffix = "") => {
        if (!mapView.current) return;

        try {
            // If we have a boundary, ensure we're perfectly centered and zoomed for the best shot
            if (boundaryGeometryRef.current) {
                await mapView.current.goTo({
                    target: boundaryGeometryRef.current.extent.expand(1.05)
                }, { animate: false });

                // Give the map tiles and layers a moment to redraw at the new extent
                await new Promise(resolve => setTimeout(resolve, silent ? 1500 : 500));
            }

            // Wait for map to stop 'updating' (tiles loading, etc)
            let checkCount = 0;
            while (mapView.current.updating && checkCount < 10) {
                await new Promise(resolve => setTimeout(resolve, 500));
                checkCount++;
            }

            const screenshot = await mapView.current.takeScreenshot({
                width: 2400,
                height: 1600,
                format: "jpg",
                quality: 100
            });

            const baseKey = analysisId
                ? `developmentAnalyzerMapSnapshot:id:${analysisId.toString()}`
                : `developmentAnalyzerMapSnapshot:${(apn || address || "property").toString()}`;

            const snapshotKey = keySuffix ? `${baseKey}:${keySuffix}` : baseKey;

            const snapshotPayload = {
                dataUrl: screenshot.dataUrl,
                address,
                apn,
                capturedAt: new Date().toISOString(),
                source: "arcgis-mapview-screenshot-ultra",
            };
            try {
                sessionStorage.setItem(snapshotKey, JSON.stringify(snapshotPayload));
                localStorage.setItem(snapshotKey, JSON.stringify(snapshotPayload));

                if (!silent) {
                    setNotification({
                        type: "success",
                        message: `High-resolution map snapshot saved${keySuffix ? ` (${keySuffix})` : ""}.`
                    });
                }
            } catch (storageError) {
                console.warn("Could not cache high-res map screenshot:", storageError);
                // Fallback to lower res if storage is full
                if (storageError.name === 'QuotaExceededError') {
                    const lowRes = await mapView.current.takeScreenshot({
                        width: 1200,
                        height: 800,
                        format: "jpg",
                        quality: 80
                    });
                    sessionStorage.setItem(snapshotKey, JSON.stringify({ ...snapshotPayload, dataUrl: lowRes.dataUrl, source: "arcgis-mapview-screenshot-fallback" }));
                }
            }

            if (!silent) {
                // Create download link
                const link = document.createElement("a");
                const extension = screenshot.dataUrl?.startsWith("data:image/jpeg") ? "jpg" : "png";
                link.download = `terrain-analysis-ultra-${keySuffix ? `${keySuffix}-` : ""}${new Date().toISOString().slice(0, 10)}.${extension}`;
                link.href = screenshot.dataUrl;
                link.click();
            }
        } catch (err) {
            console.error("Screenshot failed:", err);
        }
    }, [address, apn, analysisId]);

    // Automatic Snapshot Capture for PDF Export
    useEffect(() => {
        if (loadingStep === "complete" && mapView.current) {
            // Generate a fingerprint of current state to see if we really need a new snapshot
            const currentFingerprint = JSON.stringify({
                apn,
                address,
                structures: testFitStructures.map(s => ({ id: s.id, type: s.type, area: s.area })),
                filter: viewFilter,
                jurisdiction
            });

            // Check if we already have these snapshots in session storage for the current APN
            const aerialKey = `aerialSnapshot_${apn || address}`;
            const aerialNoContoursKey = `aerialSnapshotNoContours_${apn || address}`;
            const hasExistingSnapshots = sessionStorage.getItem(aerialKey) && sessionStorage.getItem(aerialNoContoursKey);

            // If state hasn't changed and we have the images, skip the expensive capture
            if (lastVisualSnapshotFingerprintRef.current === currentFingerprint && hasExistingSnapshots) {
                console.log("📸 Map state unchanged and snapshots exist. Skipping automatic capture.");
                return;
            }

            // Wait for animations/rendering to settle (5 seconds for ultra-high-res)
            const timer = setTimeout(async () => {
                if (!mapView.current || !mapView.current.map) {
                    console.log("📸 Map view/map not available, skipping capture.");
                    return;
                }
                console.log("📸 Capturing automatic high-resolution map snapshots for PDF report...");

                // 1. Capture primary snapshot (with contours ON - default)
                await handleScreenshot(true);

                if (!mapView.current || !mapView.current.map) {
                    console.log("📸 Map view/map not available for secondary snapshot, skipping.");
                    return;
                }

                // 2. Capture secondary snapshot (without contours)
                console.log("📸 Toggling contours OFF for secondary snapshot (Direct Layer Mode)...");

                // Find layer directly to bypass React state re-renders
                const contourLayer = mapView.current.map.findLayerById("dem-contour-layer") ||
                    mapView.current.map.findLayerById("contour-layer");

                const originalVisibility = contourLayer ? contourLayer.visible : false;
                if (contourLayer) contourLayer.visible = false;

                // Wait for the layer to hide and map to refresh
                setTimeout(async () => {
                    if (!mapView.current || !mapView.current.map) {
                        console.log("📸 Map view/map not available to restore/capture, skipping.");
                        return;
                    }
                    await handleScreenshot(true, "no-contours");
                    console.log("📸 Secondary snapshot captured. Restoring contours...");

                    // Restore directly
                    if (contourLayer) contourLayer.visible = originalVisibility;

                    // Update fingerprint after successful capture
                    lastVisualSnapshotFingerprintRef.current = currentFingerprint;

                    // Notify parent that snapshots are ready
                    if (onSnapshotsReady) onSnapshotsReady();
                }, 2000);

            }, 5000);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingStep, handleScreenshot, testFitStructures, viewFilter, apn, address, jurisdiction]);

    // ----------------------------------------------------
    // TEST FIT LOGIC
    // ----------------------------------------------------

    const validatePlacement = useCallback((geometry, checkCapacity = false) => {
        if (!modulesRef.current.geometryEngine) return { isValid: true, area: 0 };
        const { geometryEngine } = modulesRef.current;

        const areaSqFt = Math.abs(geometryEngine.geodesicArea(geometry, "square-feet"));
        let isValid = true;
        let rejectReason = "";

        // Check intersection with grid (Steep slopes, Critical slopes, existing buildings)
        if (mapView.current && mapView.current.map) {
            const gridLayer = mapView.current.map.findLayerById("grid-layer");
            if (gridLayer) {
                const intersectingCells = gridLayer.graphics.filter(g =>
                    geometryEngine.intersects(g.geometry, geometry)
                );

                for (const cell of intersectingCells) {
                    const type = cell.attributes.label;
                    if (type === "Building") {
                        isValid = false;
                        rejectReason = "Building Conflict";
                        break;
                    }
                    // Reject on Steep (20%+) terrain as well as Critical (30%+)
                    if (type === "Steep" || type === "Critical") {
                        isValid = false;
                        rejectReason = type === "Critical"
                            ? "Unsuitable: Critical slope terrain (≥30%). Structures cannot be placed on slopes exceeding 30%."
                            : "Unsuitable: Steep slope terrain (≥20%). Structures cannot be placed on slopes exceeding 20%.";
                        break;
                    }
                }
            }
        }

        // Check capacity limit (only if placement is otherwise valid)
        if (isValid && checkCapacity) {
            // Both NEW_MFA and EXTEND_MFA count towards MFA limit
            const currentUsed = grossBuildingArea ? parseFloat(grossBuildingArea) : (analysisStats ? analysisStats.buildingArea : 0);

            // Determine if this is an extension by checking if geometry matches any in buildings layer
            const buildingGraphic = buildingsLayerRef.current?.graphics?.find(g =>
                geometryEngine.equals(g.geometry, geometry) || geometryEngine.within(g.geometry, geometry)
            );

            const isExtension = !!buildingGraphic;
            const originalBuildingArea = isExtension ? buildingGraphic.attributes.areaSqFt : 0;
            const netAddedArea = isExtension ? Math.max(0, areaSqFt - originalBuildingArea) : areaSqFt;

            // Determine if the current active structure is MFA or MDA
            // We default to MFA if not found (safer for capacity check)
            const typeCategory = (testFitStructures.find(s => s.type === 'MDA') && !isExtension) ? 'MDA' : 'MFA';

            if (typeCategory === 'MFA') {
                const limit = mfa || 0;
                // Total MFA = Used + Already Placed (excluding current) + Net Added
                const placedMfaNotCurrent = testFitStructures
                    .filter(s => (s.type === 'EXTEND_MFA' || s.type === 'NEW_MFA'))
                    .reduce((sum, s) => sum + s.area, 0);

                if (limit > 0 && (currentUsed + placedMfaNotCurrent + netAddedArea) > limit) {
                    // We no longer set isValid = false for capacity (requested by user to allow editing)
                    rejectReason = "MFA Limit Exceeded";
                }
            } else {
                const limit = mda || 0;
                // For MDA check, we only include new buildings (NEW_MFA), not extensions
                const totalMfaNew = testFitStructures
                    .filter(s => s.type === 'NEW_MFA')
                    .reduce((sum, s) => sum + s.area, 0);
                const placedMdaNotCurrent = testFitStructures
                    .filter(s => s.type === 'MDA')
                    .reduce((sum, s) => sum + s.area, 0);

                if (limit > 0 && (currentUsed + totalMfaNew + placedMdaNotCurrent + netAddedArea) > limit) {
                    // We no longer set isValid = false for capacity
                    rejectReason = "MDA Limit Exceeded";
                }
            }
        }

        return { isValid, area: areaSqFt, message: rejectReason };
    }, [mda, mfa, grossBuildingArea, analysisStats, testFitStructures]);

    const updateStructureState = useCallback((graphic) => {
        if (!graphic || !graphic.attributes || !graphic.attributes.id) return;
        if (!graphic.geometry) {
            console.warn("updateStructureState: No geometry on graphic");
            return;
        }

        try {
            const { Graphic, Point } = modulesRef.current;
            const { isValid, area } = validatePlacement(graphic.geometry);

            // Approximate dimensions from bounding box for UI display
            const extent = graphic.geometry.extent;
            let width = 0, length = 0;
            if (extent && extent.width && extent.height) {
                width = Math.round(extent.width * 3.28084); // meters to feet
                length = Math.round(extent.height * 3.28084);
            }

            // Update Label Position
            if (testFitLayerRef.current && extent?.center && Point) {
                const labelId = `label-${graphic.attributes.id}`;
                const labelGraphic = testFitLayerRef.current.graphics.find(g => g.attributes?.id === labelId);

                if (labelGraphic) {
                    labelGraphic.geometry = new Point({
                        x: extent.center.x,
                        y: extent.center.y,
                        spatialReference: graphic.geometry.spatialReference
                    });
                } else {
                    // Create label if it doesn't exist yet (e.g. for newly drawn ones)
                    const type = graphic.attributes.type || 'NEW_MFA';
                    const typeConfig = STRUCTURE_TYPES[type] || STRUCTURE_TYPES.NEW_MFA;
                    const labelText = type.includes('MFA') ? 'MFA' : (type.includes('MDA') ? 'MDA' : '');

                    if (labelText) {
                        testFitLayerRef.current.add(new Graphic({
                            geometry: new Point({
                                x: extent.center.x,
                                y: extent.center.y,
                                spatialReference: graphic.geometry.spatialReference
                            }),
                            symbol: {
                                type: "text",
                                text: labelText,
                                color: "white",
                                font: { size: 9, weight: "bold", family: "Arial" },
                                haloColor: typeConfig.outline,
                                haloSize: 2
                            },
                            attributes: {
                                id: labelId,
                                parentId: graphic.attributes.id,
                                type: "label"
                            }
                        }));
                    }
                }
            }

            setTestFitStructures(prev => prev.map(s =>
                s.id === graphic.attributes.id
                    ? { ...s, area, isValid, width, length }
                    : s
            ));
        } catch (err) {
            console.warn("updateStructureState error:", err.message);
        }
    }, [validatePlacement]);

    // Keep logic refs updated with latest function versions
    useEffect(() => {
        validatePlacementRef.current = validatePlacement;
        updateStructureStateRef.current = updateStructureState;
    }, [validatePlacement, updateStructureState]);

    const endTestFit = useCallback(() => {
        setTestFitMode(false);
        // Persist structures and active selection as requested by user
        // setTestFitStructures([]); 
        // setActiveStructureId(null);
        setExtendMode(false);


        if (mapView.current) {
            mapView.current.popup.autoOpenEnabled = true;

            // Show building graphics back in grid-layer if they were hidden
            const gridLayer = mapView.current.map.findLayerById("grid-layer");
            if (gridLayer) {
                gridLayer.graphics.forEach(g => {
                    if (g.attributes && g.attributes.label === "Building") {
                        g.visible = true;
                    }
                });
            }
        }

        if (sketchViewModelRef.current) {
            sketchViewModelRef.current.cancel();
            sketchViewModelRef.current.destroy();
            sketchViewModelRef.current = null;
        }
        // Keep structures visible on map when panel closes - don't remove graphics
        // if (testFitLayerRef.current) {
        //     testFitLayerRef.current.removeAll();
        // }
    }, []);

    // Unified SketchViewModel initialization with shared listeners
    const setupSketchViewModel = useCallback((layer) => {
        if (!mapView.current || !modulesRef.current.SketchViewModel) return null;

        const { SimpleFillSymbol } = modulesRef.current;

        const svm = new modulesRef.current.SketchViewModel({
            view: mapView.current,
            layer: layer,
            updateOnGraphicClick: true,
            defaultUpdateOptions: { toggleToolOnClick: false }
        });

        // Attach create event listener
        svm.on("create", (event) => {
            if (event.state === "start") {
                setIsDrawing(true);
            }
            if (event.state === "complete") {
                setIsDrawing(false);
                const graphic = event.graphic;

                // 1. Assign ID & Initial Attributes
                const id = Date.now();
                // Use the stored pending type
                const structureType = pendingStructureTypeRef.current || 'NEW_MFA';
                graphic.attributes = { id, lastValidGeometry: graphic.geometry.clone(), type: structureType };

                // 2. Validate & Symbol
                const { isValid, area, message } = validatePlacementRef.current ? validatePlacementRef.current(graphic.geometry, true) : { isValid: true, area: 0, message: '' };
                const activeTypeConfig = STRUCTURE_TYPES[structureType] || STRUCTURE_TYPES.NEW_MFA;

                const symbol = new SimpleFillSymbol({
                    color: activeTypeConfig.color,
                    outline: { color: activeTypeConfig.outline, width: 2, style: "dash" }
                });
                graphic.symbol = symbol;

                // 3. Cache the geometry (CRITICAL for persistence)
                structureGeometryCacheRef.current[id] = graphic.geometry.toJSON();

                // 4. Update State
                const extent = graphic.geometry.extent;
                const width = extent ? Math.round(extent.width * 3.28084) : 0;
                const length = extent ? Math.round(extent.height * 3.28084) : 0;

                const newStructure = {
                    id,
                    area,
                    isValid,
                    rejectReason: message || '',
                    width,
                    length,
                    shape: 'manual',
                    type: structureType
                };

                setTestFitStructures(prev => [...prev, newStructure]);
                setActiveStructureId(id);

                // Reset pending type after use
                pendingStructureTypeRef.current = 'NEW_MFA';

                // 5. Switch to Edit Mode immediately
                setTimeout(() => {
                    if (sketchViewModelRef.current) {
                        sketchViewModelRef.current.update(graphic, { tool: "transform" });
                    }
                }, 100);
            }
            if (event.state === "cancel") {
                setIsDrawing(false);
            }
        });

        // Attach update event listener
        svm.on("update", (event) => {
            if (!event.graphics || event.graphics.length === 0) return;
            const graphic = event.graphics[0];
            if (!graphic || !graphic.attributes) return;

            // Handle start state separately
            if (event.state === "start") {
                graphic.attributes.lastValidGeometry = graphic.geometry.clone();
                setActiveStructureId(graphic.attributes.id);
                return;
            }

            // Handle active and complete states
            if (event.state === "active" || event.state === "complete") {
                // Update the state on any geometry change
                if (updateStructureStateRef.current) {
                    updateStructureStateRef.current(graphic);
                }

                // Update cached geometry
                if (graphic.attributes.id && graphic.geometry) {
                    structureGeometryCacheRef.current[graphic.attributes.id] = graphic.geometry.toJSON();
                }

                const currentType = graphic.attributes.type || 'NEW_MFA';
                const activeTypeConfig = STRUCTURE_TYPES[currentType] || STRUCTURE_TYPES.NEW_MFA;

                const symbol = graphic.symbol.clone();
                symbol.color = activeTypeConfig.color;
                symbol.outline.color = activeTypeConfig.outline;
                graphic.symbol = symbol;

                if (event.state === "active") {
                    setActiveStructureId(graphic.attributes.id);
                }

                if (event.state === "complete") {
                    // Always commit the geometry when finished, even if "invalid" 
                    // (e.g. touching steep slopes) as requested by user to prevent 
                    // reverting back to a rectangle.
                    graphic.attributes.lastValidGeometry = graphic.geometry.clone();
                }
            }
        });

        return svm;
    }, [validatePlacementRef, updateStructureStateRef]);
    setupSketchViewModelRef.current = setupSketchViewModel;

    // Start Extend Mode - enables clicking on existing buildings to extend them (kept for future use)
    // eslint-disable-next-line no-unused-vars
    const startExtendMode = useCallback(() => {
        if (!mapView.current || !buildingsLayerRef.current || !modulesRef.current.SketchViewModel) {
            console.warn("Cannot start extend mode - missing requirements");
            return;
        }

        // Capacity Alert
        if (totalStats.mfaRemaining <= 0 && totalStats.mdaRemaining <= 0) {
            setNotification({ type: 'warning', message: 'No property capacity remaining (MFA and MDA limits reached). Please remove existing structures first.' });
            return;
        }

        // Close and disable popups
        if (mapView.current) {
            mapView.current.popup.close();
            mapView.current.popup.autoOpenEnabled = false;

            // Hide building graphics in grid-layer to avoid double highlight/popup
            const gridLayer = mapView.current.map.findLayerById("grid-layer");
            if (gridLayer) {
                gridLayer.graphics.forEach(g => {
                    if (g.attributes && g.attributes.label === "Building") {
                        g.visible = false;
                    }
                });
            }
        }

        setExtendMode(true);
        setTestFitMode(true);
        setIsDrawing(false);

        const { SketchViewModel } = modulesRef.current;

        // Create SketchViewModel for the buildings layer if not exists
        if (!sketchViewModelRef.current) {
            const svm = new SketchViewModel({
                view: mapView.current,
                layer: buildingsLayerRef.current,
                updateOnGraphicClick: true,
                defaultUpdateOptions: {
                    tool: "transform",
                    enableRotation: false,
                    enableScaling: true,
                    preserveAspectRatio: false,
                    toggleToolOnClick: false
                }
            });

            // Listen for update events (when user transforms a building)
            svm.on("update", (event) => {
                const graphic = event.graphics[0];
                if (!graphic || !graphic.attributes || !graphic.attributes.id) return;

                if (event.state === "start") {

                    console.log("🎯 Selected building for extension:", graphic.attributes.id);
                }

                if (event.state === "active" || event.state === "complete") {
                    const { geometryEngine } = modulesRef.current;
                    const newAreaSqFt = Math.abs(geometryEngine.geodesicArea(graphic.geometry, "square-feet"));
                    const originalAreaSqFt = graphic.attributes.areaSqFt || 0;
                    const addedAreaSqFt = Math.max(0, newAreaSqFt - originalAreaSqFt);

                    // Update in list
                    setTestFitStructures(prev => {
                        const existingIdx = prev.findIndex(s => s.id === graphic.attributes.id);
                        if (existingIdx >= 0) {
                            const newArr = [...prev];
                            newArr[existingIdx] = {
                                ...newArr[existingIdx],
                                area: addedAreaSqFt,
                                newArea: newAreaSqFt
                            };
                            return newArr;
                        }
                        // If not in list, add it (though it should be pre-populated)
                        return [...prev, {
                            id: graphic.attributes.id,
                            type: 'EXTEND_MFA',
                            area: addedAreaSqFt,
                            originalArea: originalAreaSqFt,
                            newArea: newAreaSqFt,
                            isValid: true,
                            shape: 'extended',
                            width: 0,
                            length: 0
                        }];
                    });
                }
            });

            sketchViewModelRef.current = svm;
        } else {
            // Update existing SVM to use buildings layer
            sketchViewModelRef.current.layer = buildingsLayerRef.current;
        }

        // 1. Populate structures list with current buildings (0 extension initially)
        const initialExtendList = buildingsLayerRef.current?.graphics?.map(g => ({
            id: g.attributes.id,
            type: 'EXTEND_MFA',
            area: 0,
            originalArea: g.attributes.areaSqFt || 0,
            newArea: g.attributes.areaSqFt || 0,
            isValid: true,
            shape: 'extended',
            width: 0,
            length: 0
        })).toArray() || [];

        setTestFitStructures(initialExtendList);

        // 2. AUTO-SELECT the first building to show handles immediately
        const firstGraphic = buildingsLayerRef.current?.graphics?.getItemAt(0);
        if (firstGraphic) {
            sketchViewModelRef.current.update(firstGraphic, {
                tool: "transform",
                enableRotation: false,
                enableScaling: true,
                preserveAspectRatio: false,
                toggleToolOnClick: false
            });

            console.log("✨ Auto-selected first building for extension");
        }

        console.log("🔧 Extend mode activated - handles should be visible on the first blue building");
    }, [totalStats]);

    // eslint-disable-next-line no-unused-vars
    const endExtendMode = useCallback(() => {
        setExtendMode(false);

        if (mapView.current) {
            mapView.current.popup.autoOpenEnabled = true;

            // Show building graphics back in grid-layer
            const gridLayer = mapView.current.map.findLayerById("grid-layer");
            if (gridLayer) {
                gridLayer.graphics.forEach(g => {
                    if (g.attributes && g.attributes.label === "Building") {
                        g.visible = true;
                    }
                });
            }
        }
        if (sketchViewModelRef.current) {
            sketchViewModelRef.current.cancel();
        }
    }, []);

    const toggleTestFitMode = useCallback(() => {
        if (testFitMode) {
            endTestFit();
            return;
        }

        if (boundaryStatus === "apn-mismatch") {
            setNotification({
                type: 'error',
                message: parcelMatchWarning || 'Address marker does not match the APN parcel. Correct the parcel before planning development.'
            });
            return;
        }

        if (!mapView.current || !mapView.current.map || !modulesRef.current.SketchViewModel) return;
        const { GraphicsLayer } = modulesRef.current;

        // 1. Setup Layer
        let layer = mapView.current.map.findLayerById("test-fit-layer");
        if (!layer) {
            layer = new GraphicsLayer({ id: "test-fit-layer", title: "Test Fit" });
            mapView.current.map.add(layer);
        }
        testFitLayerRef.current = layer;

        // 2. Setup SVM (No Graphic Creation yet)
        if (!sketchViewModelRef.current) {
            sketchViewModelRef.current = setupSketchViewModel(layer);
        }

        setTestFitMode(true);
    }, [testFitMode, endTestFit, setupSketchViewModel, boundaryStatus, parcelMatchWarning]);

    const addTestFitStructure = useCallback((shapeType = "rectangle", defaultType = 'EXTEND_MFA') => {
        if (boundaryStatus === "apn-mismatch") {
            setNotification({
                type: 'error',
                message: parcelMatchWarning || 'Address marker does not match the APN parcel. Correct the parcel before drawing structures.'
            });
            return;
        }

        if (!mapView.current || !modulesRef.current.SketchViewModel) {
            console.warn('[addTestFitStructure] Map or SketchViewModel module not ready');
            return;
        }
        const { Polygon, Graphic, SimpleFillSymbol, GraphicsLayer } = modulesRef.current;

        // Ensure testFitLayer exists
        if (!testFitLayerRef.current) {
            let layer = mapView.current.map.findLayerById("test-fit-layer");
            if (!layer) {
                layer = new GraphicsLayer({ id: "test-fit-layer", title: "Test Fit" });
                mapView.current.map.add(layer);
            }
            testFitLayerRef.current = layer;
        }

        // Ensure sketchViewModel exists - initialize on demand using unified setup
        if (!sketchViewModelRef.current) {
            console.log('[addTestFitStructure] Initializing SketchViewModel on demand');
            sketchViewModelRef.current = setupSketchViewModel(testFitLayerRef.current);
        }

        // Ensure a clean state before starting anything
        if (sketchViewModelRef.current) {
            sketchViewModelRef.current.cancel();
        }

        if (shapeType === 'manual') {
            setActiveStructureId(null); // Clear selection
            setIsDrawing(true); // UI feedback
            setTestFitMode(true);

            // Store the intended structure type for when drawing completes
            pendingStructureTypeRef.current = defaultType;

            // Ensure SVM layer is set to testFitLayer for drawing
            if (sketchViewModelRef.current && testFitLayerRef.current) {
                sketchViewModelRef.current.layer = testFitLayerRef.current;
            }

            // Ensure SVM is ready and start polygon drawing
            setTimeout(() => {
                if (sketchViewModelRef.current) {
                    sketchViewModelRef.current.cancel(); // Cancel any existing operation
                    console.log('[NEW MDA] Starting polygon creation mode');
                    sketchViewModelRef.current.create("polygon", { mode: "click" });
                }
            }, 100);
            return;
        }

        // 2. Create Graphic Geometry
        const id = Date.now();
        const center = mapView.current.center;

        // Offset slightly if multiple
        const offset = testFitStructures.length * 5;
        const cX = center.x + offset;
        const cY = center.y - offset;
        let ring = [];

        if (shapeType === "l-shape") {
            // L-Shape Dimensions (approx 50x50ft bounding box)
            // 7.62m = 25ft
            const s = 7.62;
            const w = s * 0.5; // Width of the legs

            // 6-point Polygon
            ring = [
                [cX - s, cY + s], // Top-Left
                [cX - s + w, cY + s], // Top-Left-Inner
                [cX - s + w, cY - s + w], // Inner-Corner
                [cX + s, cY - s + w], // Bottom-Right-Inner
                [cX + s, cY - s], // Bottom-Right
                [cX - s, cY - s]  // Bottom-Left
            ];
        } else {
            // Default Rectangle (50x50ft)
            const sizeMeters = 7.62; // ~25ft half-width
            ring = [
                [cX - sizeMeters, cY - sizeMeters],
                [cX + sizeMeters, cY - sizeMeters],
                [cX + sizeMeters, cY + sizeMeters],
                [cX - sizeMeters, cY + sizeMeters],
                [cX - sizeMeters, cY - sizeMeters]
            ];
        }

        const geometry = new Polygon({
            rings: [ring],
            spatialReference: center.spatialReference
        });

        const graphic = new Graphic({
            geometry: geometry,
            attributes: { id, lastValidGeometry: geometry.clone(), type: defaultType }, // Include type for update handler
            symbol: new SimpleFillSymbol({
                color: STRUCTURE_TYPES[defaultType].color,
                outline: { color: STRUCTURE_TYPES[defaultType].outline, width: 2, style: "dash" }
            })
        });

        testFitLayerRef.current.add(graphic);

        // Add text label showing structure type (MDA or MFA)
        const { TextSymbol, Point, Graphic: LabelGraphic } = modulesRef.current;
        if (TextSymbol && Point && LabelGraphic) {
            const centroid = geometry.extent.center;
            const isMdaType = defaultType === 'MDA' || defaultType === 'NEW_MDA' || defaultType === 'EXISTING_MDA';
            const labelText = isMdaType ? mdaMetricShortLabel : (jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel);
            const labelColor = isMdaType ? [168, 85, 247] : [56, 189, 248]; // Purple or Blue

            const textGraphic = new LabelGraphic({
                geometry: new Point({
                    x: centroid.x,
                    y: centroid.y,
                    spatialReference: center.spatialReference
                }),
                symbol: {
                    type: "text",
                    text: labelText,
                    color: "white",
                    font: {
                        size: 12,
                        weight: "bold",
                        family: "Arial"
                    },
                    haloColor: labelColor,
                    haloSize: 2
                },
                attributes: {
                    id: `label - ${id} `,
                    parentId: id,
                    type: 'label'
                }
            });

            testFitLayerRef.current.add(textGraphic);
        }


        // 3. Update State (with capacity check for new structures)
        const { area, isValid, message } = validatePlacement(geometry, true);
        const newStructure = {
            id,
            area,
            isValid,
            rejectReason: message || '',
            width: 50,
            length: 50,
            shape: shapeType,
            type: defaultType
        };

        setTestFitStructures(prev => [...prev, newStructure]);
        setActiveStructureId(id);
        setTestFitMode(true);

        // 4. Select for Editing - Ensure SVM layer is correct and start transform
        // Ensure the SketchViewModel is using the right layer
        if (sketchViewModelRef.current.layer !== testFitLayerRef.current) {
            sketchViewModelRef.current.layer = testFitLayerRef.current;
        }

        // Small delay to ensure graphic is added to layer before starting transform
        setTimeout(() => {
            if (sketchViewModelRef.current && graphic) {
                sketchViewModelRef.current.update(graphic, { tool: "transform" });
            }
        }, 50);

    }, [testFitStructures, validatePlacement, setupSketchViewModel, boundaryStatus, parcelMatchWarning, mdaMetricShortLabel, mfaMetricShortLabel, jurisdiction]);

    // eslint-disable-next-line no-unused-vars
    const removeTestFitStructure = useCallback((id) => {
        const struct = testFitStructures.find(s => s.id === id);
        if (!struct) return;

        // 0. Cancel active edit - Always cancel to prevent conflicts
        try {
            if (sketchViewModelRef.current) {
                // Check if not destroyed
                if (!sketchViewModelRef.current.destroyed) {
                    sketchViewModelRef.current.cancel();
                } else {
                    sketchViewModelRef.current = null;
                }
            }
        } catch (e) {
            console.warn("Error cancelling sketch view model:", e);
        }

        setActiveStructureId(null);

        const isExistingBuilding = struct.type === 'EXTEND_MFA';

        if (isExistingBuilding) {
            // REVERT Existing Building (Reset)
            try {
                if (buildingsLayerRef.current && buildingsLayerRef.current.graphics) {
                    const graphic = buildingsLayerRef.current.graphics.find(g => g.attributes.id === id);
                    if (graphic && graphic.attributes.originalGeometry) {
                        // Safely clone and assign
                        const originalInfo = graphic.attributes.originalGeometry;
                        if (originalInfo && typeof originalInfo.clone === 'function') {
                            graphic.geometry = originalInfo.clone();
                        }
                    }
                }
            } catch (e) {
                console.warn("Error reverting building geometry:", e);
            }

            // Reset state but keep in list
            setTestFitStructures(prev => prev.map(s => {
                if (s.id === id) {
                    return {
                        ...s,
                        area: 0,
                        newArea: s.originalArea
                    };
                }
                return s;
            }));
            console.log(`♻️ Building ${id} reverted to original geometry`);
        } else {
            // DELETE New Structure
            setTestFitStructures(prev => prev.filter(s => s.id !== id));

            // Clean up cached geometry
            if (structureGeometryCacheRef.current[id]) {
                delete structureGeometryCacheRef.current[id];
                console.log(`🗑️ Cleaned up cached geometry for structure ${id}`);
            }

            try {
                if (testFitLayerRef.current && testFitLayerRef.current.graphics) {
                    const graphic = testFitLayerRef.current.graphics.find(g => g.attributes.id === id);
                    if (graphic) {
                        testFitLayerRef.current.remove(graphic);
                    }
                    // Also remove associated label
                    const labelGraphic = testFitLayerRef.current.graphics.find(g => g.attributes.id === `label-${id}`);
                    if (labelGraphic) {
                        testFitLayerRef.current.remove(labelGraphic);
                    }
                }
            } catch (e) {
                console.warn("Error removing test fit graphic:", e);
            }
        }
    }, [testFitStructures]);

    // Delete structure and sync remaining structures to backend
    const deleteStructureAndSync = useCallback(async (id) => {
        const struct = testFitStructures.find(s => s.id === id);
        if (!struct) return;

        // Show custom confirmation modal instead of window.confirm
        setDeleteConfirmModal({
            id: struct.id,
            type: struct.type === 'MDA' ? 'MDA' : 'MFA',
            area: Math.round(struct.area)
        });
    }, [testFitStructures]);

    // Actually perform the delete after modal confirmation
    const confirmDeleteStructure = useCallback(async () => {
        if (!deleteConfirmModal) return;
        const id = deleteConfirmModal.id;
        const struct = testFitStructures.find(s => s.id === id);
        if (!struct) {
            setDeleteConfirmModal(null);
            return;
        }

        setDeleteConfirmModal(null); // Close modal

        // 1. Remove locally first (optimistic update)
        // Cancel active edit
        try {
            if (sketchViewModelRef.current && !sketchViewModelRef.current.destroyed) {
                sketchViewModelRef.current.cancel();
            }
        } catch (e) {
            console.warn("Error cancelling sketch view model:", e);
        }
        setActiveStructureId(null);

        const isExistingBuilding = struct.type === 'EXTEND_MFA';

        if (isExistingBuilding) {
            // REVERT Existing Building (Reset)
            try {
                if (buildingsLayerRef.current && buildingsLayerRef.current.graphics) {
                    const graphic = buildingsLayerRef.current.graphics.find(g => g.attributes.id === id);
                    if (graphic && graphic.attributes.originalGeometry) {
                        const originalInfo = graphic.attributes.originalGeometry;
                        if (originalInfo && typeof originalInfo.clone === 'function') {
                            graphic.geometry = originalInfo.clone();
                        }
                    }
                }
            } catch (e) {
                console.warn("Error reverting building geometry:", e);
            }
        } else {
            // Remove graphic from map
            try {
                if (testFitLayerRef.current && testFitLayerRef.current.graphics) {
                    const graphic = testFitLayerRef.current.graphics.find(g => g.attributes.id === id);
                    if (graphic) {
                        testFitLayerRef.current.remove(graphic);
                    }
                    // Also remove associated label
                    const labelGraphic = testFitLayerRef.current.graphics.find(g => g.attributes.id === `label-${id}`);
                    if (labelGraphic) {
                        testFitLayerRef.current.remove(labelGraphic);
                    }
                }
            } catch (e) {
                console.warn("Error removing test fit graphic:", e);
            }
        }

        // 2. Update state (remove from list)
        // Clean up geometry cache
        delete structureGeometryCacheRef.current[id];

        const remainingStructures = testFitStructures.filter(s => s.id !== id);
        setTestFitStructures(remainingStructures);

        // 3. Sync remaining structures to backend
        try {
            setNotification({ type: 'info', message: '🔄 Syncing changes to database...' });

            const structuresData = remainingStructures
                .filter(s => STRUCTURE_TYPES[s.type]?.saveable === true)
                .map(s => {
                    const testFitGraphic = testFitLayerRef.current?.graphics?.find(g => g.attributes.id === s.id);
                    const buildingGraphic = buildingsLayerRef.current?.graphics?.find(g => g.attributes.id === s.id);
                    const graphic = testFitGraphic || buildingGraphic;

                    const typeConfig = STRUCTURE_TYPES[s.type];
                    const colorRgba = typeConfig ? `rgba(${typeConfig.color.join(',')})` : 'rgba(100,100,100,0.5)';
                    const colorHex = typeConfig ? rgbToHex(typeConfig.color[0], typeConfig.color[1], typeConfig.color[2]) : '#646464';

                    return {
                        id: s.id,
                        type: s.type,
                        typeLabel: typeConfig?.label || s.type,
                        category: (s.type === 'MDA' || s.type === 'NEW_MDA' || s.type === 'EXISTING_MDA') ? 'MDA' : 'MFA',
                        color: colorHex,
                        colorRgba: colorRgba,
                        geometry: graphic ? graphic.geometry.toJSON() : null,
                        area: s.area,
                        dimensions: { width: s.width, length: s.length },
                        isExtended: s.shape === 'extended'
                    };
                });

            const userId = getUserIdFromToken();

            const payload = {
                user_id: userId,
                address,
                apn,
                structures: structuresData,
                totalMfaAdded: remainingStructures.filter(s => s.type === 'EXTEND_MFA' || s.type === 'NEW_MFA' || s.type === 'EXISTING_MFA').reduce((sum, s) => sum + s.area, 0),
                totalMdaAdded: remainingStructures.filter(s => s.type === 'MDA' || s.type === 'NEW_MDA' || s.type === 'NEW_MFA' || s.type === 'EXISTING_MDA').reduce((sum, s) => sum + s.area, 0),
                timestamp: new Date().toISOString()
            };

            await axios.post(`${API_BASE}/api/save-custom-structures`, payload);

            console.log(`✅ Successfully deleted structure ${id} and synced ${structuresData.length} remaining structures`);
            setNotification({ type: 'success', message: '🗑️ Structure deleted and changes synced!' });

            // Auto-dismiss after 3 seconds
            setTimeout(() => setNotification(null), 3000);

        } catch (err) {
            console.error("Failed to sync after delete:", err);
            setNotification({ type: 'error', message: `Failed to sync: ${err.message}. Structure removed locally but may reappear on reload.` });
        }
    }, [deleteConfirmModal, testFitStructures, address, apn, getUserIdFromToken]);


    // Effect to handle dimension changes from UI
    const handleDimensionChange = useCallback((id, field, value) => {
        if (!testFitLayerRef.current) return;

        const graphic = testFitLayerRef.current.graphics.find(g => g.attributes.id === id);
        if (!graphic || !modulesRef.current.geometryEngine) return;

        // Find current structure to get its current dimensions
        const currentStruct = testFitStructures.find(s => s.id === id);
        if (!currentStruct) return;

        // Parse the new value
        const newValue = parseInt(value) || 0;
        if (newValue <= 0) return;

        // Get current dimensions
        const currentWidth = currentStruct.width || 50;
        const currentLength = currentStruct.length || 50;

        // Calculate new dimensions based on which field changed
        const widthFt = field === 'width' ? newValue : currentWidth;
        const lengthFt = field === 'length' ? newValue : currentLength;

        const { Polygon, geometryEngine } = modulesRef.current;
        const center = graphic.geometry.extent.center;

        // Convert feet to meters (approx) for WebMercator
        // 1 ft = 0.3048 meters
        const widthMeters = (widthFt * 0.3048) / 2;
        const lengthMeters = (lengthFt * 0.3048) / 2;

        const ring = [
            [center.x - widthMeters, center.y - lengthMeters],
            [center.x + widthMeters, center.y - lengthMeters],
            [center.x + widthMeters, center.y + lengthMeters],
            [center.x - widthMeters, center.y + lengthMeters],
            [center.x - widthMeters, center.y - lengthMeters]
        ];

        const newGeom = new Polygon({
            rings: [ring],
            spatialReference: graphic.geometry.spatialReference
        });

        graphic.geometry = newGeom;

        // Calculate actual area from new geometry
        const actualAreaSqFt = Math.abs(geometryEngine.geodesicArea(newGeom, "square-feet"));

        // Update state immediately with new dimensions and area
        setTestFitStructures(prev => prev.map(s =>
            s.id === id
                ? { ...s, width: widthFt, length: lengthFt, area: actualAreaSqFt }
                : s
        ));

        // Refresh SVM selection if active
        if (sketchViewModelRef.current && activeStructureId === id) {
            try {
                sketchViewModelRef.current.update(graphic, { tool: "transform" });
            } catch (e) {
                console.warn("Failed to update SVM:", e);
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStructureId, testFitStructures, totalStats]);

    const handleTypeChange = useCallback((id, newType) => {
        if (!STRUCTURE_TYPES[newType]) return;

        // Find graphic in either the test-fit layer or the buildings layer
        const testFitGraphic = testFitLayerRef.current?.graphics?.find(g => g.attributes.id === id);
        const buildingGraphic = buildingsLayerRef.current?.graphics?.find(g => g.attributes.id === id);
        const graphic = testFitGraphic || buildingGraphic;

        if (!graphic) return;

        // Update Layer Symbol
        const symbol = graphic.symbol.clone();
        symbol.color = STRUCTURE_TYPES[newType].color;
        graphic.symbol = symbol;

        // Update State
        setTestFitStructures(prev => prev.map(s =>
            s.id === id ? { ...s, type: newType } : s
        ));
    }, []);

    // Directional Extension Logic
    const handleDirectionalExtend = useCallback((struct, direction, mode = 'step') => {
        try {
            if (!mapView.current || !modulesRef.current.geometryEngine) return;

            const { Polygon } = modulesRef.current;
            const id = struct.id;

            // Find graphic
            const testFitGraphic = testFitLayerRef.current?.graphics?.find(g => g.attributes?.id === id);
            const buildingGraphic = buildingsLayerRef.current?.graphics?.find(g => g.attributes?.id === id);
            const graphic = testFitGraphic || buildingGraphic;

            if (!graphic || !graphic.geometry) {
                console.warn("handleDirectionalExtend: No valid graphic found for id:", id);
                return;
            }

            // Determine limits
            const isMfaType = struct.type === 'EXTEND_MFA' || struct.type === 'NEW_MFA';
            const remainingLimit = isMfaType ? totalStats.mfaRemaining : totalStats.mdaRemaining;

            // Distance to move (meters)
            let distanceMeters = 0;

            if (mode === 'step') {
                distanceMeters = 0.3048; // 1 ft
            } else if (mode === 'max') {
                // Calculate max distance based on area limit
                const extent = graphic.geometry.extent;
                if (!extent || !extent.width || !extent.height) {
                    console.warn("handleDirectionalExtend: Invalid extent");
                    return;
                }

                const widthMeters = extent.width;
                const lengthMeters = extent.height;

                // Convert remaining limit (sqft) to sqm, but cap at 0 so MAX doesn't shrink structure if already over limit
                const remainingSqm = Math.max(0, remainingLimit) * 0.092903;

                // Determine the perpendicular dimension
                let perpDim = 0;
                if (direction === 'north' || direction === 'south') {
                    perpDim = widthMeters;
                } else {
                    perpDim = lengthMeters;
                }

                if (perpDim <= 0) return; // safety

                let maxDistMeters = remainingSqm / perpDim;

                // Cap at a reasonable max to prevent infinite extension or huge leaps (e.g. 100ft)
                maxDistMeters = Math.min(maxDistMeters, 30.48); // max 100ft jump

                distanceMeters = maxDistMeters;
            }

            if (distanceMeters <= 0) return;

            // Perform Extension
            if (!graphic.geometry.rings || graphic.geometry.rings.length === 0) {
                console.warn("handleDirectionalExtend: Invalid geometry rings");
                return;
            }

            const newRings = extendRectangleRings(graphic.geometry.rings, direction, distanceMeters);
            const newGeom = new Polygon({
                rings: newRings,
                spatialReference: graphic.geometry.spatialReference || { wkid: 3857 }
            });

            // Update Graphic
            graphic.geometry = newGeom;

            // Update State
            updateStructureState(graphic);

            // Refresh handles
            if (sketchViewModelRef.current && activeStructureId === id) {
                try {
                    sketchViewModelRef.current.update(graphic, { tool: "transform" });
                } catch (svmErr) {
                    console.warn("handleDirectionalExtend: SketchViewModel update failed:", svmErr.message);
                }
            }

        } catch (err) {
            console.error("handleDirectionalExtend error:", err);
        }

    }, [totalStats, updateStructureState, activeStructureId]);

    // Square Footage Extension - extend structure proportionally outward (professional uniform expansion)
    // Uses outward buffer/expansion for natural-looking growth
    const handleSqFtExtend = useCallback((struct, targetSqFtToAdd) => {
        try {
            if (!mapView.current || !modulesRef.current.geometryEngine) return;
            if (targetSqFtToAdd <= 0) return;

            const { Polygon } = modulesRef.current;
            const id = struct.id;

            // Find graphic
            const testFitGraphic = testFitLayerRef.current?.graphics?.find(g => g.attributes?.id === id);
            const buildingGraphic = buildingsLayerRef.current?.graphics?.find(g => g.attributes?.id === id);
            const graphic = testFitGraphic || buildingGraphic;

            if (!graphic || !graphic.geometry) {
                console.warn("handleSqFtExtend: No valid graphic found for id:", id);
                return;
            }

            // Ensure geometry has extent
            const extent = graphic.geometry.extent;
            if (!extent || !extent.width || !extent.height) {
                console.warn("handleSqFtExtend: Invalid geometry extent for structure:", id);
                return;
            }

            // Get current area

            // Get current dimensions in feet
            const widthFeet = extent.width * 3.28084;
            const lengthFeet = extent.height * 3.28084;
            const perimeter = 2 * (widthFeet + lengthFeet);

            if (perimeter <= 0) {
                console.warn("handleSqFtExtend: Invalid perimeter");
                return;
            }

            // Calculate uniform buffer distance for proportional expansion
            // For a rectangle: New Area ≈ Current Area + Perimeter * d + π * d²
            // Simplified for small d: d ≈ Target Area / Perimeter
            // But for more accuracy, solve quadratic: π*d² + P*d - TargetSqFt = 0
            const a = Math.PI;
            const b = perimeter;
            const c = -targetSqFtToAdd;
            const discriminant = b * b - 4 * a * c;

            if (discriminant < 0) {
                console.warn("handleSqFtExtend: Math error in buffer calculation");
                return;
            }

            // d = (-b + sqrt(b² + 4πC)) / (2π)
            let bufferFeet = (-b + Math.sqrt(discriminant)) / (2 * a);

            // Ensure positive buffer
            bufferFeet = Math.max(bufferFeet, 0);

            // Safety cap at 50ft buffer
            bufferFeet = Math.min(bufferFeet, 50);

            if (bufferFeet < 0.1) {
                console.warn("handleSqFtExtend: Buffer too small");
                return;
            }

            const bufferMeters = bufferFeet * 0.3048;

            // Apply uniform outward extension (buffer) in all 4 directions
            if (!graphic.geometry.rings || graphic.geometry.rings.length === 0) {
                console.warn("handleSqFtExtend: Invalid geometry rings");
                return;
            }

            // Apply equal extension to all 4 sides
            let newRings = graphic.geometry.rings;
            newRings = extendRectangleRings(newRings, 'north', bufferMeters);
            newRings = extendRectangleRings(newRings, 'south', bufferMeters);
            newRings = extendRectangleRings(newRings, 'east', bufferMeters);
            newRings = extendRectangleRings(newRings, 'west', bufferMeters);

            const newGeom = new Polygon({
                rings: newRings,
                spatialReference: graphic.geometry.spatialReference || { wkid: 3857 }
            });

            // Update Graphic
            graphic.geometry = newGeom;

            // Update State immediately
            updateStructureState(graphic);

            // Refresh handles if active
            if (sketchViewModelRef.current && activeStructureId === id) {
                try {
                    sketchViewModelRef.current.update(graphic, { tool: "transform" });
                } catch (svmErr) {
                    console.warn("handleSqFtExtend: SketchViewModel update failed:", svmErr.message);
                }
            }

        } catch (err) {
            console.error("handleSqFtExtend error:", err);
        }

    }, [updateStructureState, activeStructureId]);


    const handleSaveCustomStructures = useCallback(async (newManualMfaStructures = null) => {
        // Filter only saveable structures
        const saveableStructures = testFitStructures.filter(s => STRUCTURE_TYPES[s.type]?.saveable === true);

        const currentManualStructures = newManualMfaStructures !== null ? newManualMfaStructures : manualMfaStructures;

        if (saveableStructures.length === 0 && currentManualStructures.length === 0) {
            setNotification({ type: 'warning', message: 'No structures to save.' });
            return;
        }

        try {
            const structuresData = saveableStructures.map(s => {
                // Find graphic in either the test-fit layer or the buildings layer
                const testFitGraphic = testFitLayerRef.current?.graphics?.find(g => g.attributes.id === s.id);
                const buildingGraphic = buildingsLayerRef.current?.graphics?.find(g => g.attributes.id === s.id);
                const graphic = testFitGraphic || buildingGraphic;

                // Get color based on type
                const typeConfig = STRUCTURE_TYPES[s.type];
                const colorRgba = typeConfig ? `rgba(${typeConfig.color.join(',')})` : 'rgba(100,100,100,0.5)';
                const colorHex = typeConfig ? rgbToHex(typeConfig.color[0], typeConfig.color[1], typeConfig.color[2]) : '#646464';

                return {
                    id: s.id,
                    type: s.type,
                    typeLabel: typeConfig?.label || s.type,
                    category: (s.type === 'MDA' || s.type === 'NEW_MDA' || s.type === 'EXISTING_MDA') ? 'MDA' : 'MFA',
                    color: colorHex,
                    colorRgba: colorRgba,
                    geometry: graphic ? graphic.geometry.toJSON() : null,
                    area: s.area,
                    dimensions: { width: s.width, length: s.length },
                    isExtended: s.shape === 'extended'
                };
            });

            // Get user_id from JWT token
            const userId = getUserIdFromToken();

            const payload = {
                user_id: userId,
                address,
                apn,
                structures: [
                    ...structuresData,
                    ...currentManualStructures.map(s => ({
                        id: s.id,
                        type: 'EXISTING_MFA',
                        typeLabel: `${s.stories}stories`,
                        stories: s.stories,
                        category: 'MFA',
                        area: s.area,
                        geometry: null,
                        dimensions: null
                    }))
                ],
                manualMfaOffset: currentManualStructures.reduce((sum, s) => sum + (s.area || 0), 0),
                // Include both original types and new extended types in totals
                totalMfaAdded: testFitStructures.filter(s => s.type === 'EXTEND_MFA' || s.type === 'NEW_MFA' || s.type === 'EXISTING_MFA').reduce((sum, s) => sum + s.area, 0),
                totalMdaAdded: testFitStructures.filter(s => s.type === 'MDA' || s.type === 'NEW_MDA' || s.type === 'NEW_MFA' || s.type === 'EXISTING_MDA').reduce((sum, s) => sum + s.area, 0),
                timestamp: new Date().toISOString()
            };

            console.log("🚀 POSTING to backend with Axios:", payload);

            // Real Axios Call
            const response = await axios.post(`${API_BASE}/api/save-custom-structures`, payload);

            console.log("✅ Server Response:", response.data);
            setNotification({ type: 'success', message: 'Structures saved successfully! Other users will see these changes.' });
        } catch (err) {
            console.error("❌ Failed to save structures:", err.response?.data || err.message);

            const errorMessage = err.response?.data?.detail || err.response?.data?.message || err.message;

            if (err.code === 'ERR_NETWORK' || err.response?.status === 404) {
                console.warn("⚠️ API Endpoint not found, simulating success for frontend testing.");
                setNotification({ type: 'success', message: '(Demo) Structures saved successfully!' });
            } else {
                setNotification({
                    type: 'error',
                    message: `Failed to save: ${errorMessage}`
                });
            }
        }
    }, [address, apn, testFitStructures, getUserIdFromToken, manualMfaStructures]);


    // Start editing a structure (with move and resize capabilities)
    const handleStructureClick = useCallback((struct) => {
        try {
            setActiveStructureId(struct.id);

            // Find the graphic from either layer
            const buildingGraphic = buildingsLayerRef.current?.graphics?.find(g => g.attributes?.id === struct.id);
            const testFitGraphic = testFitLayerRef.current?.graphics?.find(g => g.attributes?.id === struct.id);
            const graphic = buildingGraphic || testFitGraphic;

            // Enable transform editing for the selected structure
            if (graphic && !sketchViewModelRef.current && testFitLayerRef.current && setupSketchViewModelRef.current) {
                sketchViewModelRef.current = setupSketchViewModelRef.current(testFitLayerRef.current);
            }
            if (graphic && sketchViewModelRef.current) {
                try {
                    // Cancel any existing update first
                    sketchViewModelRef.current.cancel();

                    // Use 'reshape' tool - allows dragging individual vertices or edges
                    // This satisfies the "expand from point" requirement
                    sketchViewModelRef.current.update(graphic, {
                        tool: "reshape"
                    });
                    console.log(`✏️ Editing enabled for structure: ${struct.id} `);
                    setNotification({ type: 'info', message: '✏️ Edit Mode: Drag points or edges to expand. Click Done when finished.' });
                    // Auto-dismiss after 4 seconds
                    setTimeout(() => setNotification(null), 4000);
                } catch (svmErr) {
                    console.warn("handleStructureClick: SketchViewModel update failed:", svmErr.message);
                    setNotification({ type: 'error', message: 'Could not enable editing. Try clicking on the structure again.' });
                }
            }
        } catch (err) {
            console.error("handleStructureClick error:", err);
        }
    }, []);

    // Cancel editing mode and deselect the structure
    const cancelEditMode = useCallback(() => {
        try {
            // CRITICAL FIX: Explicitly capture the current edited geometry from the map layer
            // BEFORE calling complete(). The ArcGIS SVM reshape tool may not reliably deliver
            // the final geometry via the update event when complete() is called programmatically.
            // Reading directly from the graphic on the layer gives us the live reshaped geometry.
            if (activeStructureId) {
                const testFitGraphic = testFitLayerRef.current?.graphics?.find(g => g.attributes?.id === activeStructureId);
                const buildingGraphic = buildingsLayerRef.current?.graphics?.find(g => g.attributes?.id === activeStructureId);
                const graphic = testFitGraphic || buildingGraphic;

                if (graphic && graphic.geometry) {
                    // Lock the current geometry as the committed last-valid version
                    graphic.attributes.lastValidGeometry = graphic.geometry.clone();
                    // Update the geometry cache with the new shape
                    structureGeometryCacheRef.current[activeStructureId] = graphic.geometry.toJSON();
                    // Update React state (area, dimensions) to reflect the new shape
                    if (updateStructureStateRef.current) {
                        updateStructureStateRef.current(graphic);
                    }
                    console.log(`✅ Geometry committed for structure ${activeStructureId} before complete()`);
                }
            }

            // Now finalize the SVM operation
            if (sketchViewModelRef.current && !sketchViewModelRef.current.destroyed) {
                sketchViewModelRef.current.complete();
            }
        } catch (e) {
            console.warn("Error completing edit mode:", e);
        }
        setActiveStructureId(null);
        setNotification({ type: 'success', message: '✅ Edit saved successfully' });
        setTimeout(() => setNotification(null), 2000);
    }, [activeStructureId]);

    // Effect to fetch and load saved structures after map is fully loaded
    useEffect(() => {
        // Wait until map is fully loaded (loadingStep === 'complete' or null after boundary processing)
        if (!isOpen || !scriptsLoaded) return;
        if (!mapView.current || !mapView.current.map || !modulesRef.current.Graphic) return;
        // Only run after map boundary is found/processed (not during initial loading)
        if (loadingStep === 'initializing' || loadingStep === 'fetching-boundary') return;
        const propertyKey = apn || address || "property";

        const fetchSavedData = async () => {
            // If data has already been loaded for this APN, recreate graphics from cached geometries
            if (dataFetchedForApnRef.current === apn && testFitStructures.length > 0) {
                console.log(`📋 Skipping fetch - recreating ${testFitStructures.length} structures from cached geometries for APN: ${apn} `);

                // Small delay to ensure map is fully ready
                await new Promise(resolve => setTimeout(resolve, 300));

                const { Graphic, GraphicsLayer, Polygon, SimpleFillSymbol, Point } = modulesRef.current;

                // Ensure test-fit layer exists
                let layer = mapView.current.map.findLayerById("test-fit-layer");
                if (!layer) {
                    layer = new GraphicsLayer({ id: "test-fit-layer", title: "Test Fit" });
                    mapView.current.map.add(layer);
                }
                testFitLayerRef.current = layer;

                // Recreate graphics from cached geometries
                let recreatedCount = 0;
                testFitStructures.forEach(struct => {
                    const cachedGeometry = structureGeometryCacheRef.current[struct.id];

                    if (cachedGeometry) {
                        try {
                            // Reconstruct the Polygon from cached geometry JSON
                            const geometry = Polygon.fromJSON(cachedGeometry);
                            if (!geometry) {
                                console.warn(`Failed to reconstruct geometry for cached structure ${struct.id}`);
                                return;
                            }

                            const typeConfig = STRUCTURE_TYPES[struct.type] || STRUCTURE_TYPES.NEW_MFA;

                            const graphic = new Graphic({
                                geometry,
                                attributes: {
                                    id: struct.id,
                                    lastValidGeometry: geometry.clone(),
                                    type: struct.type
                                },
                                symbol: new SimpleFillSymbol({
                                    color: typeConfig.color,
                                    outline: { color: typeConfig.outline, width: 2, style: "dash" }
                                })
                            });

                            layer.add(graphic);

                            // Add label for cached structure
                            if (geometry.extent?.center && Point) {
                                const typeConfig = STRUCTURE_TYPES[struct.type] || STRUCTURE_TYPES.NEW_MFA;
                                const labelText = struct.type.includes('MFA') ? 'MFA' : (struct.type.includes('MDA') ? 'MDA' : '');

                                if (labelText) {
                                    layer.add(new Graphic({
                                        geometry: new Point({
                                            x: geometry.extent.center.x,
                                            y: geometry.extent.center.y,
                                            spatialReference: geometry.spatialReference
                                        }),
                                        symbol: {
                                            type: "text",
                                            text: labelText,
                                            color: "white",
                                            font: { size: 9, weight: "bold", family: "Arial" },
                                            haloColor: typeConfig.outline,
                                            haloSize: 2
                                        },
                                        attributes: {
                                            id: `label-${struct.id}`,
                                            parentId: struct.id,
                                            type: "label"
                                        }
                                    }));
                                }
                            }
                            recreatedCount++;
                        } catch (err) {
                            console.warn(`Failed to recreate graphic for structure ${struct.id}: `, err);
                        }
                    } else {
                        console.warn(`No cached geometry found for structure ${struct.id}`);
                    }
                });

                console.log(`✅ Recreated ${recreatedCount}/${testFitStructures.length} structure graphics from cache`);

                // Initialize SketchViewModel if not already created
                if (!sketchViewModelRef.current && modulesRef.current.SketchViewModel) {
                    sketchViewModelRef.current = setupSketchViewModel(layer);
                }

                setTestFitMode(true);
                setTestFitMode(true);
                hasSavedStructuresRef.current = testFitStructures.some(s => !s.isTraceOverlay);
                setSavedStructuresLoadedForProperty(propertyKey);
                return;
            }

            // Skip if already fetched and no structures in state (already fetched with no results)
            if (dataFetchedForApnRef.current === apn) {
                console.log(`📋 Skipping fetch - no structures for APN: ${apn}`);
                hasSavedStructuresRef.current = testFitStructures.some(s => !s.isTraceOverlay);
                setSavedStructuresLoadedForProperty(propertyKey);
                return;
            }

            // Small delay to ensure map is fully ready

            // Show loading toast
            setNotification({ type: 'info', message: '📡 Loading saved structures...' });

            try {
                // Use centralized API_BASE

                // Fetch saved structures by APN from backend
                const response = await axios.get(`${API_BASE}/api/custom-structures`, {
                    params: { apn: apn, address: address }
                });

                const savedData = response.data?.structures || [];

                // Check for all manual MFA structures in saved data
                const savedManualStructures = savedData
                    .filter(s => s.typeLabel === 'Stories' || s.typeLabel?.includes('stories') || s.id?.startsWith('manual-mfa-offset'))
                    .map(s => {
                        let parsedStories = s.stories;

                        // Parse stories from typeLabel if stories field is missing (e.g. "2stories" -> 2)
                        if (!parsedStories && s.typeLabel?.includes('stories')) {
                            const match = s.typeLabel.match(/(\d+)stories/);
                            if (match) parsedStories = parseInt(match[1]);
                        }

                        return {
                            id: s.id,
                            stories: parsedStories || 1,
                            area: s.area || 0
                        };
                    });

                if (savedManualStructures.length > 0) {
                    setManualMfaStructures(savedManualStructures);
                    const totalOffset = savedManualStructures.reduce((sum, s) => sum + s.area, 0);
                    console.log(`🏡 Loaded ${savedManualStructures.length} saved MFA structures: ${totalOffset} sqft total`);
                } else {
                    setManualMfaStructures([]);
                }

                console.log(`📥 Fetched ${savedData.length} saved structures for APN: ${apn}`);

                if (savedData.length > 0) {
                    hasSavedStructuresRef.current = true;
                    const { Graphic, GraphicsLayer, Polygon, SimpleFillSymbol, Point } = modulesRef.current;

                    // Ensure test-fit layer exists
                    let layer = mapView.current.map.findLayerById("test-fit-layer");
                    if (!layer) {
                        layer = new GraphicsLayer({ id: "test-fit-layer", title: "Test Fit" });
                        mapView.current.map.add(layer);
                    }
                    testFitLayerRef.current = layer;
                    layer.graphics
                        .filter(g => g.attributes?.isTraceOverlay)
                        .forEach(g => layer.remove(g));

                    const loadedStructures = savedData.map(data => {
                        try {
                            if (!data.geometry) {
                                console.warn(`Structure ${data.id} has no geometry data, skipping.`);
                                return null;
                            }

                            const geometry = Polygon.fromJSON(data.geometry);
                            if (!geometry) {
                                console.warn(`Failed to parse geometry for structure ${data.id}, skipping.`);
                                return null;
                            }

                            const type = data.type || 'NEW_MFA';
                            const typeConfig = STRUCTURE_TYPES[type] || STRUCTURE_TYPES.NEW_MFA;

                            const graphic = new Graphic({
                                geometry,
                                attributes: {
                                    id: data.id,
                                    lastValidGeometry: geometry.clone(),
                                    type: type
                                },
                                symbol: new SimpleFillSymbol({
                                    color: typeConfig.color,
                                    outline: { color: typeConfig.outline, width: 2, style: "dash" }
                                })
                            });

                            layer.add(graphic);

                            // Add label for loaded structure
                            if (geometry.extent?.center && Point) {
                                const labelText = type.includes('MFA') ? 'MFA' : (type.includes('MDA') ? 'MDA' : '');
                                if (labelText) {
                                    layer.add(new Graphic({
                                        geometry: new Point({
                                            x: geometry.extent.center.x,
                                            y: geometry.extent.center.y,
                                            spatialReference: geometry.spatialReference
                                        }),
                                        symbol: {
                                            type: "text",
                                            text: labelText,
                                            color: "white",
                                            font: { size: 9, weight: "bold", family: "Arial" },
                                            haloColor: typeConfig.outline,
                                            haloSize: 2
                                        },
                                        attributes: {
                                            id: `label-${data.id}`,
                                            parentId: data.id,
                                            type: "label"
                                        }
                                    }));
                                }
                            }

                            // CRITICAL: Cache the geometry so it persists when modal closes/reopens
                            structureGeometryCacheRef.current[data.id] = geometry.toJSON();

                            return {
                                id: data.id,
                                area: data.area || 0,
                                isValid: true,
                                width: data.dimensions?.width || 0,
                                length: data.dimensions?.length || 0,
                                shape: data.isExtended ? 'extended' : 'manual',
                                type: type
                            };
                        } catch (err) {
                            console.error(`Error processing saved structure ${data?.id}:`, err);
                            return null;
                        }
                    }).filter(s => s !== null);

                    setTestFitStructures(loadedStructures);

                    // Initialize SketchViewModel if not already created (required for NEW MFA/MDA buttons)
                    if (!sketchViewModelRef.current && modulesRef.current.SketchViewModel) {
                        sketchViewModelRef.current = setupSketchViewModel(layer);
                        console.log('✅ SketchViewModel initialized for loaded structures');
                    }

                    setTestFitMode(true);

                    // Calculate MFA and MDA totals from loaded structures
                    const totalMfa = loadedStructures.filter(s => s.type === 'NEW_MFA' || s.type === 'EXTEND_MFA' || s.type === 'EXISTING_MFA').reduce((sum, s) => sum + s.area, 0);
                    const totalMda = loadedStructures.filter(s => s.type === 'MDA' || s.type === 'NEW_MDA' || s.type === 'NEW_MFA' || s.type === 'EXISTING_MDA').reduce((sum, s) => sum + s.area, 0);

                    console.log(`✅ Loaded ${loadedStructures.length} saved structures for this property.`);


                    // Show success toast with MFA/MDA totals for 10 seconds
                    setNotification({
                        type: 'success',
                        message: `✅ Loaded ${loadedStructures.length} structure(s): MFA +${Math.round(totalMfa).toLocaleString()} sqft, MDA +${Math.round(totalMda).toLocaleString()} sqft`
                    });

                    // Mark this APN as loaded to prevent refetch
                    dataFetchedForApnRef.current = apn;
                    setSavedStructuresLoadedForProperty(propertyKey);

                    // Auto-dismiss after 10 seconds
                    setTimeout(() => {
                        setNotification(null);
                    }, 10000);
                } else {
                    hasSavedStructuresRef.current = false;
                    // No structures found, clear notification
                    setNotification(null);
                    console.log('📭 No saved structures found for this property.');
                    // Mark as loaded even if no structures (don't refetch empty results)
                    dataFetchedForApnRef.current = apn;
                    setSavedStructuresLoadedForProperty(propertyKey);
                }
            } catch (err) {
                // If 404 or no data, silently continue (no saved structures for this property)
                if (err.response?.status === 404 || err.code === 'ERR_NETWORK') {
                    hasSavedStructuresRef.current = false;
                    dataFetchedForApnRef.current = apn;
                    setSavedStructuresLoadedForProperty(propertyKey);
                    setNotification(null);
                    console.log('📭 No saved structures found for this property.');
                } else {
                    console.error("Error loading saved structures:", err);
                    setNotification({ type: 'error', message: `Failed to load structures: ${err.message}` });
                    // Auto-dismiss error after 10 seconds
                    setTimeout(() => {
                        setNotification(null);
                    }, 10000);
                }
            }
        };

        // Only fetch once when map is ready - use a ref to track if already fetched
        fetchSavedData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, scriptsLoaded, apn, loadingStep]); // Remove 'address' to prevent refetch on address change

    // Fullscreen toggle
    const toggleFullscreen = useCallback(() => {
        setIsFullscreen(prev => !prev);
    }, []);

    // Zoom to fit the property boundary
    const handleZoomToFit = useCallback(() => {
        if (!mapView.current || !boundaryGeometryRef.current) return;
        mapView.current.goTo({
            target: boundaryGeometryRef.current,
            padding: { top: 50, bottom: 50, left: 50, right: 50 }
        }, { duration: 1000 });
    }, []);

    // Load ArcGIS scripts once
    useEffect(() => {
        if (scriptsLoaded) return;

        // Check if already loaded
        if (window.require && window.require.defined && window.require.defined("esri/config")) {
            setScriptsLoaded(true);
            return;
        }

        // Load CSS
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://js.arcgis.com/4.28/esri/themes/light/main.css";
        document.head.appendChild(link);

        // Load ArcGIS script
        const script = document.createElement("script");
        script.src = "https://js.arcgis.com/4.28/";
        script.async = true;
        script.onload = () => {
            setScriptsLoaded(true);
        };
        script.onerror = () => {
            setError("Failed to load ArcGIS library");
            setLoadingStep(null);
        };
        document.head.appendChild(script);
    }, [scriptsLoaded]);

    useEffect(() => {
        if (!isOpen || !address || !scriptsLoaded) return;

        // IMPORTANT: Do NOT reset testFitStructures, testFitMode, activeStructureId here
        // The fetchSavedData effect will load saved structures from the backend
        // Only reset UI-related state and cleanup resources

        // However, if the APN has changed (switching to a different property), clear old structures
        if (dataFetchedForApnRef.current && dataFetchedForApnRef.current !== apn) {
            console.log(`🔄 APN changed from ${dataFetchedForApnRef.current} to ${apn}, clearing old structures`);
            setTestFitStructures([]);
            setManualMfaStructures([]);
            setIsEditingMfaOffset(false);
            setEditingMfaStructureId(null);
            setMfaOffsetInput("");
            setMfaStoriesInput("");
            setTestFitMode(false);
            setActiveStructureId(null);
            dataFetchedForApnRef.current = null; // Reset so new data will be fetched
        }

        // Reset only UI drawing modes
        setExtendMode(false);
        setIsDrawing(false);

        // Clean up any existing sketchViewModel to prevent conflicts
        if (sketchViewModelRef.current) {
            try {
                if (!sketchViewModelRef.current.destroyed) {
                    sketchViewModelRef.current.cancel();
                    sketchViewModelRef.current.destroy();
                }
            } catch (e) {
                console.warn('Error cleaning up previous sketchViewModel:', e);
            }
            sketchViewModelRef.current = null;
        }

        // Clear test fit layer graphics - they will be recreated from state by fetchSavedData
        if (testFitLayerRef.current) {
            try {
                testFitLayerRef.current.removeAll();
            } catch (e) {
                console.warn('Error clearing testFitLayer:', e);
            }
        }

        // Reset pending structure type
        pendingStructureTypeRef.current = 'NEW_MFA';

        let isMounted = true;
        setLoadingStep("initializing");
        setError(null);
        setLocationInfo(null);
        setBoundaryStatus("searching");
        setParcelMatchWarning(null);

        const loadMap = () => {
            window.require([
                "esri/config",
                "esri/Map",
                "esri/views/MapView",
                "esri/Graphic",
                "esri/rest/locator",
                "esri/geometry/Point",
                "esri/symbols/SimpleMarkerSymbol",
                "esri/symbols/SimpleFillSymbol",
                "esri/geometry/Polygon",
                "esri/geometry/Polyline",
                "esri/rest/query",
                "esri/rest/support/Query",
                "esri/layers/GraphicsLayer",
                "esri/layers/ElevationLayer",
                "esri/layers/ImageryLayer",
                "esri/layers/FeatureLayer",
                "esri/layers/VectorTileLayer",
                "esri/geometry/geometryEngine",
                "esri/geometry/support/webMercatorUtils",
                "esri/widgets/Sketch/SketchViewModel",
                "esri/symbols/TextSymbol"
            ], (esriConfig, Map, MapView, Graphic, locator, Point, SimpleMarkerSymbol, SimpleFillSymbol, Polygon, Polyline, query, Query, GraphicsLayer, ElevationLayer, ImageryLayer, FeatureLayer, VectorTileLayer, geometryEngine, webMercatorUtils, SketchViewModel, TextSymbol) => {
                if (!isMounted) return;

                // Handle script errors inside the require callback
                try {
                    // Configure fonts to avoid loading issues
                    esriConfig.fontsUrl = "https://static.arcgis.com/fonts";

                    // Store modules for later use
                    modulesRef.current = {
                        Map, MapView, Graphic, locator, Point, SimpleMarkerSymbol,
                        SimpleFillSymbol, Polygon, Polyline, query, Query, GraphicsLayer,
                        ElevationLayer, ImageryLayer, FeatureLayer, VectorTileLayer, geometryEngine, webMercatorUtils, SketchViewModel, TextSymbol
                    };

                    // Create map with Imagery Hybrid basemap
                    const map = new Map({
                        basemap: "hybrid"
                    });

                    const contourLayer = new FeatureLayer({
                        url: "https://carto.nationalmap.gov/arcgis/rest/services/contours/MapServer/26",
                        id: "contour-layer",
                        title: "USGS 5ft Contours",
                        visible: true,
                        outFields: ["OBJECTID", "contourelevation"],
                        definitionExpression: "contourelevation IS NOT NULL",
                        renderer: {
                            type: "simple",
                            symbol: {
                                type: "simple-line",
                                color: [24, 24, 27, 0.95],
                                width: 1.35,
                                style: "solid"
                            }
                        },
                        labelingInfo: [{
                            symbol: {
                                type: "text",
                                color: [24, 24, 27, 0.9],
                                haloColor: [255, 255, 255, 0.85],
                                haloSize: 1,
                                font: { size: 8, family: "Arial", weight: "bold" }
                            },
                            labelExpressionInfo: { expression: "Text($feature.contourelevation, '#')" },
                            labelPlacement: "center-along"
                        }],
                        labelsVisible: false
                    });
                    map.add(contourLayer);
                    const isMerged = address && address.includes(" & ");
                    const addressList = isMerged ? address.split(" & ").map(s => s.trim()) : [address];
                    const apnList = apn && apn.includes(" & ") ? apn.split(" & ").map(s => s.trim()) : [apn];
                    const primaryAddress = addressList[0];

                    // Geocode the address with retry logic
                    const geocodeUrl = "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer";

                    const performGeocode = async (retries = 3) => {
                        for (let i = 0; i < retries; i++) {
                            try {
                                const results = await locator.addressToLocations(geocodeUrl, {
                                    address: { SingleLine: primaryAddress },
                                    maxLocations: 1,
                                    outFields: ["*"]
                                });
                                return results;
                            } catch (err) {
                                console.warn(`Geocoding attempt ${i + 1} failed:`, err);
                                if (i === retries - 1) throw err;
                                // Wait 1s before retry
                                await new Promise(resolve => setTimeout(resolve, 1000));
                            }
                        }
                    };

                    performGeocode().then((results) => {
                        if (!isMounted) return;

                        if (results && results.length > 0) {
                            const result = results[0];

                            // Validate location data before creating Point
                            if (!result.location || typeof result.location.longitude !== 'number' || typeof result.location.latitude !== 'number') {
                                throw new Error("Invalid location data received from geocoder");
                            }

                            const point = new Point({
                                longitude: result.location.longitude,
                                latitude: result.location.latitude,
                                type: "point",
                                spatialReference: { wkid: 4326 }
                            });

                            // Store location info
                            setLocationInfo({
                                lat: result.location.latitude,
                                lon: result.location.longitude,
                                score: result.score
                            });

                            // NOW create the map view centered on the property location
                            const view = new MapView({
                                container: mapDiv.current,
                                map: map,
                                center: point, // Use the Point object directly
                                zoom: 18,
                                popup: {
                                    dockEnabled: false,
                                    dockOptions: {
                                        buttonEnabled: false
                                    },
                                    collapseEnabled: false
                                }
                            });

                            mapView.current = view;

                            // Create marker symbol
                            const markerSymbol = new SimpleMarkerSymbol({
                                color: [226, 119, 40],
                                size: 18,
                                outline: {
                                    color: [255, 255, 255],
                                    width: 4
                                }
                            });

                            // Create graphic for the location
                            const pointGraphic = new Graphic({
                                geometry: point,
                                symbol: markerSymbol,
                                attributes: result.attributes,
                                popupTemplate: {
                                    title: "📍 Property Location",
                                    content: `
                        <div style="padding: 12px; font-family: system-ui; min-width: 280px;">
                            <div style="margin-bottom: 12px;">
                                <div style="font-weight: 600; color: #1e293b; margin-bottom: 4px; font-size: 13px;">Property Address</div>
                                <div style="color: #475569; font-size: 14px; line-height: 1.5;">${address}</div>
                            </div>
                            ${apn ? `
                                <div style="margin-bottom: 12px;">
                                    <div style="font-weight: 600; color: #1e293b; margin-bottom: 4px; font-size: 13px;">APN</div>
                                    <div style="color: #475569; font-family: monospace; font-size: 13px; background: #f8fafc; padding: 6px; border-radius: 4px;">
                                    ${apn}
                                    </div>
                                </div>
                            ` : ''}
                            ${parcelAreaAcres ? `
                                <div style="margin-bottom: 12px;">
                                    <div style="font-weight: 600; color: #1e293b; margin-bottom: 4px; font-size: 13px;">Parcel Area</div>
                                    <div style="color: #475569; font-size: 14px;">
                                    <strong>${parcelAreaAcres.toFixed(2)}</strong> acres
                                    </div>
                                </div>
                            ` : ''}
                            <div style="margin-bottom: 12px;">
                                <div style="font-weight: 600; color: #1e293b; margin-bottom: 4px; font-size: 13px;">GPS Coordinates</div>
                                <div style="color: #475569; font-family: monospace; font-size: 12px; background: #f8fafc; padding: 8px; border-radius: 4px;">
                                Lat: ${result.location.latitude.toFixed(6)}<br/>
                                Lon: ${result.location.longitude.toFixed(6)}
                                </div>
                            </div>
                            <div style="padding: 8px; background: linear-gradient(135deg, #dbeafe 0%, #e0e7ff 100%); border-radius: 6px; font-size: 12px; color: #1e40af; border-left: 3px solid #3b82f6;">
                                <strong>Match Confidence:</strong> ${result.score}%
                            </div>
                        </div>
                    `
                                }
                            });

                            // Wait for view to be ready
                            view.when(() => {
                                if (!isMounted) return;

                                // Add marker to map
                                view.graphics.add(pointGraphic);

                                // Helper to project geometry from feet SR to WebMercator
                                const projectGeometryToWebMercator = async (geometry) => {
                                    const layers = GIS_LAYERS[county] || GIS_LAYERS.SCC;

                                    // Use ArcGIS Geometry Service to project from 2227 to 3857
                                    const geomServiceUrl = layers.GEOMETRY_SERVICE_PROJECT || `${layers.GEOMETRY_SERVICE}/project`;

                                    const response = await fetch(geomServiceUrl, {
                                        method: "POST",
                                        headers: { "Content-Type": "application/x-www-form-urlencoded" },
                                        body: new URLSearchParams({
                                            f: "json",
                                            inSR: resolvedWkid.toString(),
                                            outSR: "3857",
                                            geometries: JSON.stringify({
                                                geometryType: "esriGeometryPolygon",
                                                geometries: [geometry]
                                            })
                                        })
                                    });

                                    const result = await response.json();
                                    if (result.geometries && result.geometries.length > 0) {
                                        return result.geometries[0];
                                    }
                                    throw new Error("Projection failed");
                                };

                                // Hook grid generation into boundary finding
                                const addBoundaryToMap = (geometryOrGeometries, view, options = {}) => {
                                    const geometries = Array.isArray(geometryOrGeometries) ? geometryOrGeometries : [geometryOrGeometries];
                                    
                                    // Create a unioned geometry for boundaryGeometryRef and grid generation
                                    let unionedGeometry = null;
                                    if (geometries.length === 1) {
                                        unionedGeometry = geometries[0];
                                    } else if (geometryEngine && geometries.length > 1) {
                                        unionedGeometry = geometryEngine.union(geometries);
                                    } else {
                                        unionedGeometry = geometries[0];
                                    }

                                    // SAVE BOUNDARY FOR RE-GENERATION
                                    boundaryGeometryRef.current = unionedGeometry;

                                    const outlineColor = options.mismatch ? [220, 38, 38] : [22, 163, 74];
                                    const boundarySymbol = new SimpleFillSymbol({
                                        color: options.mismatch ? [239, 68, 68, 0.08] : [34, 197, 94, 0.0],
                                        outline: {
                                            color: outlineColor,
                                            width: 3
                                        }
                                    });

                                    geometries.forEach(geom => {
                                        if (geom) {
                                            const boundaryGraphic = new Graphic({
                                                geometry: geom,
                                                symbol: boundarySymbol
                                            });
                                            view.graphics.add(boundaryGraphic);
                                        }
                                    });

                                    if (!options.skipGrid && unionedGeometry) {
                                        // TRIGGER GRID GENERATION
                                        buildGridAndCalculateSlope(unionedGeometry, view, modulesRef.current, gridSettings);
                                    }

                                    // Add click handler for existing building selection
                                    view.on("click", async (event) => {
                                        // Only handle clicks when in existing mode
                                        if (structureVariant !== 'existing') return;

                                        // Get hit test results
                                        const response = await view.hitTest(event);

                                        // Find clicked building from buildings-layer
                                        const buildingGraphic = response.results.find(result =>
                                            result.graphic &&
                                            result.graphic.layer &&
                                            result.graphic.layer.id === "buildings-layer" &&
                                            result.graphic.attributes &&
                                            result.graphic.attributes.type // Only actual building graphics, not labels
                                        );

                                        if (!buildingGraphic) return;

                                        const clickedBuilding = buildingGraphic.graphic;
                                        const buildingType = clickedBuilding.attributes.type; // 'main' or 'accessory'

                                        // Handle existing MFA buildings (main structures)
                                        if (buildingType === 'main') {
                                            const { SimpleFillSymbol, Graphic } = modulesRef.current;

                                            // Create a new editable structure from the clicked building
                                            const id = Date.now();
                                            const areaSqFt = clickedBuilding.attributes.areaSqFt || 0;
                                            const originalGeometry = clickedBuilding.geometry.clone();

                                            // Create graphic for the test fit layer
                                            const structureType = 'EXISTING_MFA';
                                            const typeConfig = STRUCTURE_TYPES[structureType];

                                            const newGraphic = new Graphic({
                                                geometry: originalGeometry,
                                                symbol: new SimpleFillSymbol({
                                                    color: typeConfig.color,
                                                    outline: { color: typeConfig.outline, width: 2, style: "solid" }
                                                }),
                                                attributes: {
                                                    id: id,
                                                    lastValidGeometry: originalGeometry.clone(),
                                                    type: structureType,
                                                    originalBuildingId: clickedBuilding.attributes.id
                                                }
                                            });

                                            // Add to test fit layer
                                            const testFitLayer = view.map.findLayerById("test-fit-layer");
                                            if (testFitLayer) {
                                                testFitLayer.add(newGraphic);
                                            }

                                            // Calculate dimensions
                                            const extent = originalGeometry.extent;
                                            const width = Math.round(extent.width * 3.28084);
                                            const length = Math.round(extent.height * 3.28084);

                                            // Add to state
                                            const newStructure = {
                                                id,
                                                area: areaSqFt,
                                                isValid: true,
                                                rejectReason: '',
                                                width,
                                                length,
                                                shape: 'existing',
                                                type: structureType
                                            };

                                            setTestFitStructures(prev => [...prev, newStructure]);
                                            setActiveStructureId(id);

                                            // Hide the original building graphic
                                            clickedBuilding.visible = false;

                                            setNotification({ type: 'success', message: '✅ Existing MFA building selected! You can now edit it.' });

                                            // Enable edit mode on the new structure
                                            setTimeout(() => {
                                                if (sketchViewModelRef.current) {
                                                    sketchViewModelRef.current.update(newGraphic, { tool: "transform" });
                                                }
                                            }, 100);
                                        }

                                        // Handle existing MDA buildings (accessory structures)
                                        if (buildingType === 'accessory') {
                                            const { SimpleFillSymbol, Graphic } = modulesRef.current;

                                            // Create a new editable structure from the clicked building
                                            const id = Date.now();
                                            const areaSqFt = clickedBuilding.attributes.areaSqFt || 0;
                                            const originalGeometry = clickedBuilding.geometry.clone();

                                            // Create graphic for the test fit layer
                                            const structureType = 'EXISTING_MDA';
                                            const typeConfig = STRUCTURE_TYPES[structureType];

                                            const newGraphic = new Graphic({
                                                geometry: originalGeometry,
                                                symbol: new SimpleFillSymbol({
                                                    color: typeConfig.color,
                                                    outline: { color: typeConfig.outline, width: 2, style: "solid" }
                                                }),
                                                attributes: {
                                                    id: id,
                                                    lastValidGeometry: originalGeometry.clone(),
                                                    type: structureType,
                                                    originalBuildingId: clickedBuilding.attributes.id
                                                }
                                            });

                                            // Add to test fit layer
                                            const testFitLayer = view.map.findLayerById("test-fit-layer");
                                            if (testFitLayer) {
                                                testFitLayer.add(newGraphic);
                                            }

                                            // Calculate dimensions
                                            const extent = originalGeometry.extent;
                                            const width = Math.round(extent.width * 3.28084);
                                            const length = Math.round(extent.height * 3.28084);

                                            // Add to state
                                            const newStructure = {
                                                id,
                                                area: areaSqFt,
                                                isValid: true,
                                                rejectReason: '',
                                                width,
                                                length,
                                                shape: 'existing',
                                                type: structureType
                                            };

                                            setTestFitStructures(prev => [...prev, newStructure]);
                                            setActiveStructureId(id);

                                            // Hide the original building graphic
                                            clickedBuilding.visible = false;

                                            setNotification({ type: 'success', message: '✅ Existing MDA building selected! You can now edit and upgrade it.' });

                                            // Enable edit mode on the new structure
                                            setTimeout(() => {
                                                if (sketchViewModelRef.current) {
                                                    sketchViewModelRef.current.update(newGraphic, { tool: "transform" });
                                                }
                                            }, 100);
                                        }
                                    });
                                };

                                // Function to try multiple parcel services
                                const tryFetchParcelBoundary = async () => {
                                    setLoadingStep("fetching-boundary");

                                    // 0. Project Point to WebMercator (critical for spatial queries)
                                    let searchGeometry = point;
                                    if (point.spatialReference.isWGS84 && webMercatorUtils) {
                                        searchGeometry = webMercatorUtils.geographicToWebMercator(point);
                                    }

                                    const layers = GIS_LAYERS[county] || GIS_LAYERS.SCC;

                                    // List of parcel services to try - prioritized by jurisdiction
                                    const parcelServices = [
                                        {
                                            name: `${getJurisdictionInfo(jurisdiction).name} Property Database`,
                                            url: layers.PROPERTY,
                                            apnField: layers.APN_FIELD || ((county === 'SCC' || county === 'CONTRA_COSTA') ? "APN" : "APN_S"),
                                            useFeetSR: county === 'SCC',
                                            jurisdictionMatch: true
                                        },
                                        {
                                            name: "USA Parcels",
                                            url: "https://services.arcgis.com/P3ePLMYs2RVChkJx/arcgis/rest/services/USA_Parcels/FeatureServer/0",
                                            apnField: "APN",
                                            priority: jurisdiction === 'fremont' ? 1 : 2
                                        },
                                        {
                                            name: "California Parcels",
                                            url: "https://services1.arcgis.com/pMeXRvgWzCmyJ9RM/arcgis/rest/services/Parcels/FeatureServer/0",
                                            apnField: "APN",
                                            priority: jurisdiction === 'fremont' ? 2 : 3
                                        },
                                        {
                                            name: "Regrid Parcels", // Fallback
                                            url: "https://services.arcgis.com/jIL9msH9OI208GCb/arcgis/rest/services/Regrid_Parcels/FeatureServer/0",
                                            apnField: "parcelnumb"
                                        }
                                    ].sort((a, b) => {
                                        if (a.jurisdictionMatch && !b.jurisdictionMatch) return -1;
                                        if (!a.jurisdictionMatch && b.jurisdictionMatch) return 1;
                                        return (a.priority || 10) - (b.priority || 10);
                                    });

                                    let foundGeometries = [];
                                    let boundaryFound = false;

                                    // Helper function to query parcel by geocoded address location
                                    const fetchParcelByAddressLocation = async (addr) => {
                                        try {
                                            const results = await locator.addressToLocations(geocodeUrl, {
                                                address: { SingleLine: addr },
                                                maxLocations: 1,
                                                outFields: ["*"]
                                            });
                                            if (results && results.length > 0) {
                                                const result = results[0];
                                                if (!result.location || typeof result.location.longitude !== 'number' || typeof result.location.latitude !== 'number') {
                                                    return null;
                                                }
                                                const addrPoint = new Point({
                                                    longitude: result.location.longitude,
                                                    latitude: result.location.latitude,
                                                    type: "point",
                                                    spatialReference: { wkid: 4326 }
                                                });
                                                
                                                let searchGeom = addrPoint;
                                                if (addrPoint.spatialReference.isWGS84 && webMercatorUtils) {
                                                    searchGeom = webMercatorUtils.geographicToWebMercator(addrPoint);
                                                }

                                                for (const service of parcelServices) {
                                                    try {
                                                        const queryTask = new Query();
                                                        if (service.useFeetSR) {
                                                            const geomServiceUrl = `${layers.GEOMETRY_SERVICE}/project`;
                                                            const projResponse = await fetch(geomServiceUrl, {
                                                                method: "POST",
                                                                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                                                                body: new URLSearchParams({
                                                                    f: "json",
                                                                    inSR: "4326",
                                                                    outSR: "2227",
                                                                    geometries: JSON.stringify({
                                                                        geometryType: "esriGeometryPoint",
                                                                        geometries: [{
                                                                            x: addrPoint.longitude,
                                                                            y: addrPoint.latitude
                                                                        }]
                                                                    })
                                                                })
                                                            });
                                                            const projResult = await projResponse.json();
                                                            if (projResult.geometries && projResult.geometries.length > 0) {
                                                                const feetPoint = projResult.geometries[0];
                                                                queryTask.geometry = new Point({
                                                                    x: feetPoint.x,
                                                                    y: feetPoint.y,
                                                                    spatialReference: { wkid: 2227 }
                                                                });
                                                                queryTask.distance = 10;
                                                                queryTask.units = "feet";
                                                            } else {
                                                                continue;
                                                            }
                                                        } else {
                                                            queryTask.geometry = searchGeom;
                                                            queryTask.distance = 2;
                                                            queryTask.units = "meters";
                                                        }

                                                        queryTask.spatialRelationship = "intersects";
                                                        queryTask.returnGeometry = true;
                                                        queryTask.outFields = ["*"];
                                                        const outSR = service.useFeetSR ? { wkid: 2227 } : { wkid: 3857 };
                                                        queryTask.outSpatialReference = outSR;

                                                        const results = await query.executeQueryJSON(service.url, queryTask);
                                                        if (results.features && results.features.length > 0) {
                                                            let parcelFeature = results.features[0];
                                                            if (results.features.length > 1 && geometryEngine) {
                                                                const containingFeature = results.features.find(f =>
                                                                    geometryEngine.contains(f.geometry, searchGeom)
                                                                );
                                                                if (containingFeature) {
                                                                    parcelFeature = containingFeature;
                                                                }
                                                            }
                                                            let displayGeom = parcelFeature.geometry;
                                                            if (service.useFeetSR && webMercatorUtils) {
                                                                try {
                                                                    const projResult = await projectGeometryToWebMercator(parcelFeature.geometry);
                                                                    displayGeom = new Polygon({
                                                                        rings: projResult.rings,
                                                                        spatialReference: { wkid: 3857 }
                                                                    });
                                                                } catch (projErr) {
                                                                    console.warn("Projection failed:", projErr);
                                                                }
                                                            }
                                                            return displayGeom;
                                                        }
                                                    } catch (e) {
                                                        // continue next service
                                                    }
                                                }
                                            }
                                        } catch (e) {
                                            console.warn("Failed location query for address:", addr, e);
                                        }
                                        return null;
                                    };

                                    // Try with APN first if available
                                    const validApns = apnList.filter(a => a && a !== "UNKNOWN");
                                    if (validApns.length > 0) {
                                        for (const service of parcelServices) {
                                            try {
                                                const queryTask = new Query();
                                                queryTask.where = validApns.map(a => `${service.apnField} = '${a}'`).join(" OR ");
                                                queryTask.returnGeometry = true;
                                                queryTask.outFields = ["*"];

                                                // Santa Clara County uses feet SR (2227), others use WebMercator
                                                const outSR = service.useFeetSR ? { wkid: 2227 } : { wkid: 3857 };
                                                queryTask.outSpatialReference = outSR;

                                                const results = await query.executeQueryJSON(service.url, queryTask);

                                                if (results.features && results.features.length > 0) {
                                                    for (const parcelFeature of results.features) {
                                                        let displayGeometry = parcelFeature.geometry;
                                                        if (service.useFeetSR && webMercatorUtils) {
                                                            try {
                                                                const projResult = await projectGeometryToWebMercator(parcelFeature.geometry);
                                                                displayGeometry = new Polygon({
                                                                    rings: projResult.rings,
                                                                    spatialReference: { wkid: 3857 }
                                                                });
                                                            } catch (projErr) {
                                                                console.warn("Projection failed, using original geometry:", projErr);
                                                            }
                                                        }
                                                        foundGeometries.push(displayGeometry);
                                                    }
                                                    
                                                    if (foundGeometries.length > 0) {
                                                        boundaryFound = true;
                                                        // If we successfully found at least as many geometries as valid APNs, stop searching other services
                                                        if (foundGeometries.length >= validApns.length) {
                                                            break;
                                                        }
                                                    }
                                                }
                                            } catch (err) {
                                                console.log(`${service.name} failed, trying next service...`);
                                            }
                                        }
                                    }

                                    // If APN search was incomplete/failed, or we didn't search by APN, try location query for each address.
                                    // In merged mode, ALWAYS run this to catch multi-parcel properties where one address
                                    // spans multiple lots (e.g., address has 2 parcels but only 1 APN is known).
                                    if (foundGeometries.length < addressList.length || isMerged) {
                                        console.log(isMerged
                                            ? "Merged mode: checking all address locations for additional parcels..."
                                            : "APN search incomplete or failed, trying location-based search for missing addresses...");
                                        for (const addr of addressList) {
                                            try {
                                                const addrGeom = await fetchParcelByAddressLocation(addr);
                                                if (addrGeom) {
                                                    const alreadyFound = foundGeometries.some(g => 
                                                        geometryEngine ? geometryEngine.equals(g, addrGeom) : false
                                                    );
                                                    if (!alreadyFound) {
                                                        foundGeometries.push(addrGeom);
                                                        boundaryFound = true;
                                                        console.log(`✅ Found additional parcel at location for: ${addr}`);
                                                    }
                                                }
                                            } catch (e) {
                                                console.warn("Could not fetch parcel for address:", addr, e);
                                            }
                                        }
                                    }

                                    // Check compatibility against ALL found geometries (multi-APN properties
                                    // may have the geocode point land on any one of the parcels)
                                    let isCompatible = true;
                                    if (!isMerged && foundGeometries.length > 0 && geometryEngine) {
                                        isCompatible = foundGeometries.some(geom =>
                                            isAddressPointCompatibleWithParcel(geometryEngine, geom, searchGeometry)
                                        );
                                    }

                                    // If the APN parcel doesn't contain the geocode point, the property likely
                                    // spans multiple parcels (only one APN known). Try to also find the parcel
                                    // at the geocoded address location so BOTH parcels are shown on the map.
                                    if (!isCompatible && foundGeometries.length > 0 && boundaryFound) {
                                        console.log("🔍 APN parcel doesn't contain geocode point — searching for parcel at address location...");
                                        try {
                                            const addrGeom = await fetchParcelByAddressLocation(primaryAddress);
                                            if (addrGeom) {
                                                const alreadyFound = foundGeometries.some(g =>
                                                    geometryEngine ? geometryEngine.equals(g, addrGeom) : false
                                                );
                                                if (!alreadyFound) {
                                                    foundGeometries.push(addrGeom);
                                                    console.log("✅ Found additional parcel at address location — now showing both parcels");
                                                }
                                                // Re-check compatibility with the newly added parcel
                                                isCompatible = foundGeometries.some(geom =>
                                                    isAddressPointCompatibleWithParcel(geometryEngine, geom, searchGeometry)
                                                );
                                            }
                                        } catch (e) {
                                            console.warn("Could not fetch parcel at address location:", e);
                                        }
                                    }

                                    if (!isCompatible && foundGeometries.length > 0) {
                                        const mismatchMessage = `Address marker does not match APN ${apn}. Terrain grid and Dev Planner are paused until the parcel is corrected.`;
                                        console.warn("APN/address mismatch:", {
                                            apn,
                                            address,
                                            service: "APN/Location query"
                                        });
                                        setParcelMatchWarning(mismatchMessage);
                                        setBoundaryStatus("apn-mismatch");
                                        addBoundaryToMap(foundGeometries, view, { skipGrid: true, mismatch: true });
                                        setLoadingStep("complete");
                                    } else if (boundaryFound && foundGeometries.length > 0) {
                                        setParcelMatchWarning(null);
                                        addBoundaryToMap(foundGeometries, view);
                                        setBoundaryStatus(isMerged ? "found-apn" : (apn ? "found-apn" : "found-location"));
                                    } else {
                                        console.log("⚠ No actual boundary data available for this property");
                                        setParcelMatchWarning(null);
                                        setBoundaryStatus("not-found");
                                        setLoadingStep("complete");
                                    }
                                };

                                // Execute boundary fetch
                                tryFetchParcelBoundary();


                            }).catch((err) => {
                                if (isMounted) {
                                    console.error("Map view error:", err);
                                    setError("Error loading map view");
                                    setLoadingStep(null);
                                }
                            });

                        } else {
                            if (isMounted) {
                                setError("Address not found. Please verify the address is correct.");
                                setLoadingStep(null);
                            }
                        }
                    }).catch((err) => {
                        if (isMounted) {
                            console.error("Geocoding error:", err);
                            setError("Unable to locate address. Please check the address format.");
                            setLoadingStep(null);
                        }
                    });

                } catch (err) {
                    if (isMounted) {
                        console.error("Error creating map:", err);
                        setError("Failed to create map. Please try again.");
                        setLoadingStep(null);
                    }
                }
            }, (err) => {
                if (isMounted) {
                    console.error("Error loading ArcGIS modules:", err);
                    // Detect network-related errors
                    const errorMessage = err?.message || err?.toString() || '';
                    const isNetworkError = errorMessage.includes('scriptError') ||
                        errorMessage.includes('NETWORK') ||
                        errorMessage.includes('timeout') ||
                        errorMessage.includes('Failed to fetch');

                    if (isNetworkError) {
                        setError("Network connection issue detected. Please check your internet connection and try again.");
                    } else {
                        setError("Failed to load map components. Please refresh and try again.");
                    }
                    setLoadingStep(null);
                }
            });
        };

        const timer = setTimeout(loadMap, 100);

        return () => {
            isMounted = false;
            clearTimeout(timer);
            if (mapView.current) {
                mapView.current.destroy();
                mapView.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, address, apn, scriptsLoaded, retryCount]); // Added apn to dependencies for APN change detection

    // const handleRecenter = () => {
    //     if (mapView.current && locationInfo) {
    //         mapView.current.goTo({
    //             center: [locationInfo.lon, locationInfo.lat],
    //             zoom: 18
    //         }, {
    //             duration: 1000
    //         });
    //     }
    // };

    const getBoundaryStatusMessage = () => {
        switch (boundaryStatus) {
            case "found-apn":
                return "✅ Exact boundary from APN";
            case "found-location":
                return "✅ Boundary from location";
            case "apn-mismatch":
                return "⚠ APN/address mismatch";
            case "not-found":
                return "⚠ No boundary data available";
            default:
                return "🔍 Searching boundary...";
        }
    };

    if (!isOpen && !isInline) return null;

    const modalContent = (
        <div
            ref={modalRef}
            className={`bg-white shadow-2xl flex flex-col overflow-hidden animate-fadeIn transition-all duration-300 ${isFullscreen
                    ? 'fixed inset-0 !z-[999999] rounded-none max-w-none h-full'
                    : (isInline ? 'w-full h-full' : 'rounded-2xl w-full max-w-[98vw] h-[95vh]')
                }`}
        >
            {/* Notification Toast */}
            {notification && (
                <div
                    className={`fixed top-4 left-1/2 -translate-x-1/2 z-[9999] px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-fadeIn transition-all ${notification.type === 'success' ? 'bg-emerald-600 text-white' :
                        notification.type === 'error' ? 'bg-red-600 text-white' :
                            notification.type === 'info' ? 'bg-blue-600 text-white' :
                                'bg-amber-500 text-white'
                        }`}
                >
                    <span className="text-lg">
                        {notification.type === 'success' ? '✓' :
                            notification.type === 'error' ? '✕' :
                                notification.type === 'info' ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : '⚠'}
                    </span>
                    <span className="font-medium text-sm">{notification.message}</span>
                    <button
                        onClick={() => setNotification(null)}
                        className="ml-2 text-white/80 hover:text-white text-xl leading-none"
                    >
                        ×
                    </button>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmModal && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadeIn">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden animate-slideUp">
                        {/* Modal Header */}
                        <div className="bg-gradient-to-r from-red-500 to-rose-600 px-5 py-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                                    <Trash2 className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-white font-bold text-lg">Delete Structure?</h3>
                                    <p className="text-white/80 text-xs">This action cannot be undone</p>
                                </div>
                            </div>
                        </div>
                        {/* Modal Body */}
                        <div className="p-5">
                            <div className="bg-slate-50 rounded-xl p-4 mb-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <span className={`text-xs font-bold uppercase tracking-wide ${deleteConfirmModal.type === 'MDA' ? 'text-indigo-600' : 'text-sky-500'}`}>
                                            {deleteConfirmModal.type === 'MDA' ? mdaMetricShortLabel : (jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel)} Structure
                                        </span>
                                        <div className="text-2xl font-black text-slate-800 mt-1">
                                            {deleteConfirmModal.area.toLocaleString()} <span className="text-sm font-medium text-slate-500">sqft</span>
                                        </div>
                                    </div>
                                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${deleteConfirmModal.type === 'MDA' ? 'bg-indigo-100' : 'bg-sky-100'}`}>
                                        <span className="text-2xl">{deleteConfirmModal.type === 'MDA' ? '🏊' : '🏡'}</span>
                                    </div>
                                </div>
                            </div>
                            <p className="text-slate-600 text-sm text-center mb-4">
                                This will permanently remove the structure from the database.
                            </p>
                        </div>
                        {/* Modal Footer */}
                        <div className="px-5 pb-5 flex gap-3">
                            <button
                                onClick={() => setDeleteConfirmModal(null)}
                                className="flex-1 py-3 px-4 rounded-xl border-2 border-slate-200 text-slate-600 font-bold text-sm hover:bg-slate-50 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteStructure}
                                className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-red-500 to-rose-600 text-white font-bold text-sm hover:from-red-600 hover:to-rose-700 transition-all shadow-lg shadow-red-200"
                            >
                                🗑️ Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Header - Premium Responsive Design */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 shadow-lg z-20 relative">
                {/* Single Row - All Controls */}
                <div className="px-2 sm:px-4 lg:px-6 py-2 sm:py-2.5 flex flex-wrap items-center gap-1.5 sm:gap-2 lg:gap-3">

                    {/* City Name - Left */}
                    <div className="flex items-center space-x-2 mr-1">
                        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-2 shadow-inner">
                            <MapPin className="w-3 h-3 text-white" />
                        </div>
                        <div>
                            <p className="text-white font-semibold text-[12px] max-w-[160px] lg:max-w-xs truncate leading-tight" title={address}>{address}</p>
                            {analysisStats && analysisStats.buildingArea === 0 && (
                                <div className="mt-1 inline-flex items-center gap-2 px-2.5 py-1 bg-amber-900/40 backdrop-blur-md rounded-md border border-amber-400/30 shadow-[0_0_20px_rgba(251,191,36,0.15)] animate-in fade-in slide-in-from-top-3 duration-1000 group">
                                    <div className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]"></span>
                                    </div>
                                    <span className="text-[10px] font-bold text-amber-100 tracking-wider uppercase flex items-center gap-1">
                                        <span className="text-amber-400/80 font-black">!</span>
                                        <span className="opacity-70 font-medium">Data Notice:</span>
                                        <span className="text-white drop-shadow-sm">ArcGIS footprints not found. Please manually update design.</span>
                                    </span>
                                    <AlertTriangle className="w-3 h-3 text-amber-400 group-hover:rotate-12 transition-transform duration-500" />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="hidden lg:block w-px h-6 bg-white/20"></div>

                    {/* Grid Visibility Toggle */}
                    <button
                        onClick={() => setShowGrid(!showGrid)}
                        className={`flex items-center px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all ${showGrid
                            ? 'bg-white text-sky-500 shadow-lg hover:shadow-xl'
                            : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/20'
                            }`}
                        title={showGrid ? "Hide Grid Overlay" : "Show Grid Overlay"}
                    >
                        {showGrid ? (
                            <>
                                <Eye className="w-3.5 h-3.5 mr-1.5" />
                                <span className="hidden sm:inline">Grid</span> On
                            </>
                        ) : (
                            <>
                                <EyeOff className="w-3.5 h-3.5 mr-1.5" />
                                <span className="hidden sm:inline">Grid</span> Off
                            </>
                        )}
                    </button>

                    {/* Divider */}
                    <div className="hidden lg:block w-px h-6 bg-white/20"></div>

                    {/* Contour Toggle */}
                    <div className="flex items-center">
                        <button
                            onClick={() => setShowContours(!showContours)}
                            className={`flex items-center px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all ${showContours
                                ? 'bg-indigo-500 text-white shadow-lg hover:shadow-xl'
                                : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/20'
                                }`}
                            title={showContours ? "Hide Contour Lines" : "Show Contour Lines"}
                        >
                            <Navigation className={`w-3.5 h-3.5 mr-1.5 ${showContours ? "fill-white/20" : ""}`} />
                            <span className="hidden sm:inline">Contour</span> {showContours ? "On" : "Off"}
                        </button>
                        {showContours && (
                            <span className="ml-1.5 rounded-lg border border-white/20 bg-white/10 px-2 py-1 text-[10px] font-bold text-white/85">
                                {contourInterval}ft
                            </span>
                        )}
                        {showContours && demContourError && (
                            <span
                                className="ml-1.5 rounded-lg border border-amber-400/30 bg-amber-500/20 px-2 py-1 text-[10px] font-bold text-amber-100"
                                title={demContourError}
                            >
                                DEM fallback
                            </span>
                        )}
                        {showContours && analysisStats && (analysisStats.flatArea / (analysisStats.totalArea || 1)) > 0.95 && (
                            <div className="ml-2 flex items-center gap-1.5 px-2 py-1 bg-amber-500/20 border border-amber-500/30 rounded-lg text-[10px] text-amber-200 font-bold animate-in fade-in slide-in-from-left-2 duration-500">
                                <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                                <span className="hidden lg:inline uppercase tracking-wider">Flat Terrain: No Contours</span>
                                <span className="lg:hidden uppercase tracking-wider">Flat</span>
                            </div>
                        )}
                    </div>

                    {/* Divider */}
                    <div className="hidden lg:block w-px h-6 bg-white/20"></div>

                    {/* View Filter Pills */}
                    <div className="flex items-center">
                        <span className="text-white/60 text-[10px] font-semibold uppercase tracking-wide mr-2 hidden md:inline">Show:</span>
                        <div className="flex bg-black/20 rounded-xl p-0.5 backdrop-blur-sm">
                            <button
                                onClick={() => setViewFilter(["all"])}
                                className={`px-2.5 py-1 text-[10px] rounded-lg transition-all font-bold ${viewFilter.includes("all")
                                    ? 'bg-white text-slate-700 shadow-md'
                                    : 'text-white/60 hover:text-white hover:bg-white/10'
                                    }`}
                                title="Show all layers"
                            >
                                All
                            </button>
                            {jurisdiction !== 'los_gatos' && (
                                <button
                                    onClick={() => {
                                        if (viewFilter.includes("mda")) {
                                            setViewFilter(viewFilter.filter(f => f !== "mda"));
                                        } else {
                                            setViewFilter([...viewFilter.filter(f => f !== "all"), "mda"]);
                                        }
                                    }}
                                    className={`px-2.5 py-1 text-[10px] rounded-lg transition-all font-bold flex items-center ${viewFilter.includes("mda")
                                        ? 'bg-purple-500 text-white shadow-md'
                                        : 'text-white/60 hover:text-white hover:bg-white/10'
                                        }`}
                                    title={`${mdaMetricShortLabel} overlays only`}
                                >
                                    <span className="w-1.5 h-1.5 rounded-sm bg-purple-500 mr-1 hidden sm:block"></span>
                                    {mdaMetricShortLabel}
                                </button>
                            )}
                            <button
                                onClick={() => {
                                    if (viewFilter.includes("mfa")) {
                                        setViewFilter(viewFilter.filter(f => f !== "mfa"));
                                    } else {
                                        setViewFilter([...viewFilter.filter(f => f !== "all"), "mfa"]);
                                    }
                                }}
                                className={`px-2.5 py-1 text-[10px] rounded-lg transition-all font-bold flex items-center ${viewFilter.includes("mfa")
                                    ? 'bg-sky-500 text-white shadow-md'
                                    : 'text-white/60 hover:text-white hover:bg-white/10'
                                    }`}
                                title={jurisdiction === 'los_gatos' ? 'Maximum Allowed Gross Floor Area' : `${mfaMetricShortLabel} overlays only`}
                            >
                                <span className="w-1.5 h-1.5 rounded-sm bg-sky-500 mr-1 hidden sm:block"></span>
                                {jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel}
                            </button>
                            <button
                                onClick={() => {
                                    if (viewFilter.includes("flat")) {
                                        setViewFilter(viewFilter.filter(f => f !== "flat"));
                                    } else {
                                        setViewFilter([...viewFilter.filter(f => f !== "all"), "flat"]);
                                    }
                                }}
                                className={`px-2.5 py-1 text-[10px] rounded-lg transition-all font-bold flex items-center ${viewFilter.includes("flat")
                                    ? 'bg-green-500 text-white shadow-md'
                                    : 'text-white/60 hover:text-white hover:bg-white/10'
                                    }`}
                                title="Flat terrain (0-10% slope)"
                            >
                                <span className="w-1.5 h-1.5 rounded-sm bg-green-500 mr-1 hidden sm:block"></span>
                                Flat
                            </button>
                            <button
                                onClick={() => {
                                    if (viewFilter.includes("moderate")) {
                                        setViewFilter(viewFilter.filter(f => f !== "moderate"));
                                    } else {
                                        setViewFilter([...viewFilter.filter(f => f !== "all"), "moderate"]);
                                    }
                                }}
                                className={`px-2.5 py-1 text-[10px] rounded-lg transition-all font-bold flex items-center ${viewFilter.includes("moderate")
                                    ? 'bg-yellow-500 text-white shadow-md'
                                    : 'text-white/60 hover:text-white hover:bg-white/10'
                                    }`}
                                title="Moderate slope (10-20%)"
                            >
                                <span className="w-1.5 h-1.5 rounded-sm bg-yellow-500 mr-1 hidden sm:block"></span>
                                Moderate
                            </button>
                            <button
                                onClick={() => {
                                    if (viewFilter.includes("buildable")) {
                                        setViewFilter(viewFilter.filter(f => f !== "buildable"));
                                    } else {
                                        setViewFilter([...viewFilter.filter(f => f !== "all"), "buildable"]);
                                    }
                                }}
                                className={`px-2.5 py-1 text-[10px] rounded-lg transition-all font-bold flex items-center ${viewFilter.includes("buildable")
                                    ? 'bg-gradient-to-r from-green-500 to-yellow-500 text-white shadow-md'
                                    : 'text-white/60 hover:text-white hover:bg-white/10'
                                    }`}
                                title="Flat + Moderate (0-20% slope)"
                            >
                                <span className="w-1.5 h-1.5 rounded-sm bg-gradient-to-r from-green-500 to-yellow-500 mr-1 hidden sm:block"></span>
                                Buildable
                            </button>
                            <button
                                onClick={() => {
                                    if (viewFilter.includes("steep")) {
                                        setViewFilter(viewFilter.filter(f => f !== "steep"));
                                    } else {
                                        setViewFilter([...viewFilter.filter(f => f !== "all"), "steep"]);
                                    }
                                }}
                                className={`px-2.5 py-1 text-[10px] rounded-lg transition-all font-bold flex items-center ${viewFilter.includes("steep")
                                    ? 'bg-orange-500 text-white shadow-md'
                                    : 'text-white/60 hover:text-white hover:bg-white/10'
                                    }`}
                                title="Steep terrain (20%+ slope)"
                            >
                                <span className="w-1.5 h-1.5 rounded-sm bg-orange-500 mr-1 hidden sm:block"></span>
                                Steep
                            </button>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="hidden lg:block w-px h-6 bg-white/20"></div>

                    <>
                        <button
                            onClick={toggleTestFitMode}
                            disabled={boundaryStatus === "apn-mismatch"}
                            className={`flex items-center px-3 py-1.5 rounded-xl text-[11px] font-bold transition-all ${testFitMode
                                ? 'bg-white text-sky-500 shadow-lg'
                                : boundaryStatus === "apn-mismatch"
                                    ? 'bg-white/5 text-white/35 border border-white/10 cursor-not-allowed'
                                    : 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/20'
                                }`}
                            title={boundaryStatus === "apn-mismatch" ? "Resolve APN/address mismatch before planning development" : (testFitMode ? "Exit Dev Planner" : "Plan Development")}
                        >
                            <Plus className={`w-3.5 h-3.5 mr-1.5 ${testFitMode ? "rotate-45" : ""}`} />
                            <span className="hidden sm:inline">{testFitMode ? "Exit" : "Dev Planner"}</span>
                        </button>
                    </>

                    {/* Divider */}
                    <div className="hidden lg:block w-px h-6 bg-white/20"></div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1">
                        <>
                            {/* Zoom to Fit */}
                            <button
                                onClick={handleZoomToFit}
                                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all"
                                title="Zoom to fit property (Z)"
                            >
                                <Navigation className="w-4 h-4" />
                            </button>

                            {/* Screenshot */}
                            <button
                                onClick={handleScreenshot}
                                className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all"
                                title="Take screenshot (S)"
                            >
                                <Camera className="w-4 h-4" />
                            </button>
                        </>


                        {/* Fullscreen */}
                        <button
                            onClick={toggleFullscreen}
                            className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all"
                            title="Toggle fullscreen (F)"
                        >
                            {isFullscreen ? (
                                <Minimize2 className="w-4 h-4" />
                            ) : (
                                <Maximize2 className="w-4 h-4" />
                            )}
                        </button>
                    </div>

                    {/* Spacer - pushes Grid + Close to the right end */}
                    <div className="flex-1" />

                    {/* Grid Size Settings - Right End */}
                    <div className="flex items-center bg-white/10 hover:bg-white/15 rounded-xl px-3 py-1.5 backdrop-blur-sm border border-white/10 transition-colors">
                        <Grid className="w-3.5 h-3.5 text-white/70 mr-2" />
                        <span className="text-white/80 text-[10px] font-semibold uppercase tracking-wide mr-2 hidden sm:inline">Grid</span>
                        <div className="flex items-center bg-white rounded-lg px-2 py-0.5 mr-2">
                            <input
                                type="number"
                                min="3"
                                max="500"
                                value={gridSettings.size}
                                onChange={(e) => setGridSettings({ ...gridSettings, size: parseInt(e.target.value) || 10 })}
                                className="w-10 text-xs font-bold text-slate-700 focus:outline-none text-center bg-transparent"
                            />
                        </div>
                        <div className="flex bg-black/20 rounded-lg p-0.5">
                            <button
                                onClick={() => setGridSettings({ ...gridSettings, unit: 'ft' })}
                                className={`px-2 py-0.5 text-[10px] rounded-md transition-all font-bold ${gridSettings.unit === 'ft' ? 'bg-white text-sky-500 shadow' : 'text-white/60 hover:text-white'}`}
                            >
                                ft
                            </button>
                            <button
                                onClick={() => setGridSettings({ ...gridSettings, unit: 'sqft' })}
                                className={`px-2 py-0.5 text-[10px] rounded-md transition-all font-bold ${gridSettings.unit === 'sqft' ? 'bg-white text-sky-500 shadow' : 'text-white/60 hover:text-white'}`}
                            >
                                ft²
                            </button>
                        </div>
                        <button
                            onClick={handleUpdateGrid}
                            disabled={loadingStep === "processing-grid"}
                            className="ml-2 bg-white hover:bg-sky-50 text-sky-500 px-3 py-1 rounded-lg font-bold text-[11px] transition-all shadow disabled:opacity-50 disabled:cursor-wait flex items-center"
                        >
                            {loadingStep === "processing-grid" ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                                "Apply"
                            )}
                        </button>
                    </div>

                    {/* Close Button - Far Right (Hidden in Inline Mode) */}
                    {!isInline && (
                        <>
                            <div className="w-px h-6 bg-white/20"></div>
                            <button
                                onClick={onClose}
                                className="bg-white/10 hover:bg-red-500/80 rounded-xl p-2 transition-all group"
                                aria-label="Close map"
                            >
                                <X className="w-4 h-4 text-white group-hover:scale-110 transition-transform" />
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Map Container */}
            <div className="flex-1 relative">
                {/* Elaborative High-Standard ArcGIS Data Notice */}
                <div className="absolute top-1 left-1/2 -translate-x-1/2 z-30 pointer-events-none w-full flex justify-center px-4">
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!testFitMode) toggleTestFitMode();
                        }}
                        className="pointer-events-auto flex items-center gap-4 px-5 py-3 bg-white/90 backdrop-blur-xl border border-slate-200/60 shadow-[0_8px_30px_rgb(0,0,0,0.12)] rounded-2xl animate-in slide-in-from-top-4 fade-in duration-500 group cursor-pointer hover:border-indigo-400 hover:shadow-indigo-500/10 transition-all active:scale-[0.98] max-w-xl"
                    >
                        <div className="relative flex h-2.5 w-2.5 shrink-0">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500"></span>
                        </div>
                        <div className="flex flex-col gap-0.5">
                            <p className="text-[10px] sm:text-[11px] font-medium text-slate-500 leading-tight">
                                MFA and MDA metrics are derived from regional ArcGIS data and may contain slight variations. For enhanced precision, <span className="text-indigo-600 underline decoration-indigo-200 decoration-2 underline-offset-2 group-hover:text-indigo-700 transition-colors pl-1">Click to Open Dev Planner & Adjust for Precision</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Full Screen Loader - Only for Initial Setup */}
                {(loadingStep === "initializing" || loadingStep === "locating") && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-50 z-20">
                        <div className="text-center">
                            <Loader2 className="animate-spin h-12 w-12 text-sky-500 mx-auto mb-4" />
                            <p className="text-slate-800 font-bold text-xl mb-1">
                                {loadingStep === "initializing" ? "Initializing Map..." : "Locating Property..."}
                            </p>
                            <p className="text-slate-500 text-sm mt-2">
                                {loadingStep === "initializing" ? "Loading mapping engine" : `Searching for: ${address}`}
                            </p>
                        </div>
                    </div>
                )}

                {/* Floating Status Pill - For Boundary & Grid Processing */}
                {(loadingStep === "fetching-boundary" || loadingStep === "processing-grid") && (
                    <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-30 animate-in slide-in-from-top-4 fade-in duration-300">
                        <div className="bg-white/95 backdrop-blur-md border border-blue-100 shadow-xl rounded-xl px-6 py-3 flex items-center space-x-4 min-w-[300px]">
                            <Loader2 className="w-5 h-5 text-sky-500 animate-spin flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-slate-800 font-semibold text-sm">
                                    {loadingStep === "fetching-boundary" ? "Fetching Boundary..." : "Analyzing Terrain..."}
                                </p>
                                <p className="text-slate-500 text-xs">
                                    {loadingStep === "fetching-boundary" ? "Checking parcel records" : "Generating 10ft slope grid"}
                                </p>
                            </div>
                            {loadingStep === "processing-grid" && (
                                <div className="h-1.5 w-16 bg-slate-100 rounded-xl overflow-hidden">
                                    <div className="h-full bg-blue-600 rounded-xl animate-pulse"></div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* LiDAR Contour Loading Indicator - High Fidelity Telemetry */}
                {demContourLoading && (
                    <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-30 animate-in slide-in-from-top-4 fade-in duration-300">
                        <div className="bg-indigo-600/95 backdrop-blur-md border border-indigo-400/50 shadow-2xl rounded-2xl px-6 py-3 flex items-center space-x-4 min-w-[320px]">
                            <div className="relative">
                                <Loader2 className="w-5 h-5 text-white animate-spin flex-shrink-0" />
                                <div className="absolute inset-0 bg-white/20 blur-sm rounded-full animate-pulse" />
                            </div>
                            <div className="flex-1 flex flex-col gap-1.5">
                                <div className="flex items-center justify-between">
                                    <p className="text-white font-black text-[10px] uppercase tracking-[0.2em]">
                                        {demContourTrace ? 'Refining Map Geometry' : 'LiDAR Analysis Pipeline'}
                                    </p>
                                    {demContourStatus && demContourStatus.includes('/') && (
                                        <span className="text-[9px] font-black text-indigo-200 tabular-nums">
                                            {(() => {
                                                const match = demContourStatus.match(/(\d+)\/(\d+)/);
                                                if (match) {
                                                    const pct = Math.round((parseInt(match[1]) / parseInt(match[2])) * 100);
                                                    return `${pct}%`;
                                                }
                                                return '';
                                            })()}
                                        </span>
                                    )}
                                </div>

                                <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        animate={{
                                            width: (() => {
                                                const match = demContourStatus?.match(/(\d+)\/(\d+)/);
                                                return match ? `${(parseInt(match[1]) / parseInt(match[2])) * 100}%` : '0%';
                                            })()
                                        }}
                                        className="h-full bg-white rounded-full shadow-[0_0_8px_rgba(255,255,255,0.5)]"
                                    />
                                </div>

                                <p className="text-indigo-100 text-[9px] font-bold opacity-80 leading-none flex items-center gap-1.5">
                                    <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                                    {demContourStatus || (demContourTrace ? 'Synchronizing topographical layers' : 'Generating survey-grade contours')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Drawing Mode Status */}
                {isDrawing && (
                    <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-40 animate-in slide-in-from-top-4 fade-in duration-300">
                        <div className="bg-blue-600 text-white shadow-xl rounded-full px-6 py-2 flex items-center space-x-3">
                            <PenTool className="w-4 h-4 animate-pulse" />
                            <span className="text-sm font-bold">Drawing Mode</span>
                            <span className="text-xs bg-sky-500 px-2 py-0.5 rounded text-blue-100">Click to place points • Double-click to finish</span>
                        </div>
                    </div>
                )}



                {/* Test Fit Panel - Integrated Layout with Structure List */}
                {testFitMode && (() => {
                    // Calculate structure counts
                    const mfaStructures = testFitStructures.filter(s =>
                        s.type === 'EXTEND_MFA' || s.type === 'NEW_MFA' || s.type === 'EXISTING_MFA'
                    );
                    const mdaStructures = testFitStructures.filter(s =>
                        s.type === 'NEW_MDA' || s.type === 'EXISTING_MDA' || s.type === 'CUSTOM_MDA'
                    );

                    return (
                        <div
                            className="absolute bottom-2 sm:bottom-4 lg:bottom-6 right-2 sm:right-3 z-30 animate-in slide-in-from-bottom-4 fade-in duration-300"
                            style={{ width: '750px', maxWidth: 'calc(100vw - 420px)' }}
                        >
                            <div className="bg-white/95 backdrop-blur-xl shadow-2xl rounded-xl border border-slate-200/60 overflow-hidden flex flex-col max-h-[50vh] sm:max-h-[60vh] lg:max-h-[400px]">
                                {/* Main Container - Flex Row Layout */}
                                <div className="flex flex-1 min-h-0">
                                    {/* Left Section - Controls */}
                                    <div className="flex-1 flex flex-col min-w-0">
                                        {/* Controls Row */}
                                        <div className="flex items-center gap-1.5 sm:gap-2 p-1.5 sm:p-2 flex-wrap">
                                            {/* Header - Compact */}
                                            <div className="flex items-center gap-2 px-2 shrink-0">
                                                <div className="w-6 h-6 rounded-md bg-indigo-600 shadow-sm flex items-center justify-center">
                                                    <PenTool className="w-3 h-3 text-white" />
                                                </div>
                                                <div>
                                                    <div className="text-[10px] font-bold text-slate-800 leading-none">Dev Planner</div>
                                                    <div className="text-[8px] font-medium text-slate-500 uppercase">Capacity</div>
                                                </div>
                                            </div>

                                            {/* Divider */}
                                            <div className="h-12 w-px bg-slate-200"></div>

                                            {/* Type Toggle - Compact */}
                                            <div className="flex items-center gap-1 shrink-0">
                                                <span className="text-[9px] font-semibold text-slate-600">Type:</span>
                                                <div className="inline-flex rounded-md border border-slate-300 bg-slate-50 p-0.5">
                                                    <button
                                                        onClick={() => setStructureVariant('new')}
                                                        className={`px-2 py-1 text-[9px] font-bold rounded transition-all ${structureVariant === 'new'
                                                            ? 'bg-indigo-500 text-white shadow-sm'
                                                            : 'text-slate-600 hover:bg-white'
                                                            }`}
                                                    >
                                                        ✨ New
                                                    </button>
                                                    <button
                                                        onClick={() => setStructureVariant('existing')}
                                                        className={`px-2 py-1 text-[9px] font-bold rounded transition-all ${structureVariant === 'existing'
                                                            ? 'bg-fuchsia-500 text-white shadow-sm'
                                                            : 'text-slate-600 hover:bg-white'
                                                            }`}
                                                    >
                                                        📋 Exist
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Divider */}
                                            <div className="h-12 w-px bg-slate-200"></div>

                                            {/* Drawing Mode - Compact */}
                                            <div className="flex gap-1 shrink-0">
                                                <button
                                                    onClick={() => setDrawingMode('box')}
                                                    className={`py-1 px-2 rounded text-[9px] font-bold transition-all ${drawingMode === 'box'
                                                        ? 'bg-indigo-500 text-white shadow-sm'
                                                        : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                                                        }`}
                                                >
                                                    📦 Box
                                                </button>
                                                <button
                                                    onClick={() => setDrawingMode('draw')}
                                                    className={`py-1 px-2 rounded text-[9px] font-bold transition-all ${drawingMode === 'draw'
                                                        ? 'bg-indigo-500 text-white shadow-sm'
                                                        : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                                                        }`}
                                                >
                                                    ✏️ Draw
                                                </button>
                                            </div>

                                            {/* Divider */}
                                            <div className="h-12 w-px bg-slate-200"></div>

                                            {/* Action Buttons - MFA and MDA - Compact */}
                                            <div className="flex gap-1.5 shrink-0">
                                                <button
                                                    onClick={() => {
                                                        if (structureVariant === 'existing') {
                                                            // Use drawing mode for existing MFA (Box or Draw)
                                                            const type = 'EXISTING_MFA';
                                                            if (drawingMode === 'box') {
                                                                addTestFitStructure('rectangle', type);
                                                            } else {
                                                                addTestFitStructure('manual', type);
                                                            }
                                                        } else {
                                                            // Create new MFA structure
                                                            const type = 'NEW_MFA';
                                                            if (drawingMode === 'box') {
                                                                addTestFitStructure('rectangle', type);
                                                            } else {
                                                                addTestFitStructure('manual', type);
                                                            }
                                                        }
                                                    }}
                                                    disabled={isDrawing}
                                                    className={`py-1.5 px-3 rounded-md text-[9px] font-bold transition-all flex items-center gap-1 ${!isDrawing
                                                        ? `bg-white ${structureVariant === 'new' ? 'text-indigo-500 hover:bg-indigo-50' : 'text-fuchsia-500 hover:bg-fuchsia-50'} shadow-sm`
                                                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                                        }`}
                                                    title={`${structureVariant === 'existing' ? 'Select existing' : 'Add new'} ${jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel} structure`}
                                                >
                                                    <Plus className="w-3 h-3" />
                                                    {jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel}
                                                </button>
                                                {jurisdiction !== 'los_gatos' && (
                                                    <button
                                                        onClick={() => {
                                                            const type = structureVariant === 'new' ? 'NEW_MDA' : 'EXISTING_MDA';
                                                            if (drawingMode === 'box') {
                                                                addTestFitStructure('rectangle', type);
                                                            } else {
                                                                addTestFitStructure('manual', type);
                                                            }
                                                        }}
                                                        disabled={isDrawing}
                                                        className={`py-1.5 px-3 rounded-md text-[9px] font-bold transition-all flex items-center gap-1 ${!isDrawing
                                                            ? `bg-white ${structureVariant === 'new' ? 'text-teal-500 hover:bg-teal-50' : 'text-violet-500 hover:bg-violet-50'} shadow-sm`
                                                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                                            }`}
                                                        title={`Add ${structureVariant} ${mdaMetricShortLabel} structure`}
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                        {mdaMetricShortLabel}
                                                    </button>
                                                )}
                                            </div>

                                            {/* Divider */}
                                            <div className="h-12 w-px bg-slate-200"></div>

                                            {/* MFA & MDA Capacity Display - Horizontal Compact */}
                                            <div className="flex gap-2 shrink-0">
                                                {/* MFA */}
                                                <div className={`rounded-md px-2 py-1 border transition-all min-w-[80px] ${totalStats.isMfaOver ? 'bg-red-50 border-red-200' : 'bg-sky-50/50 border-sky-200'}`}>
                                                    <div className="flex items-center justify-between gap-1">
                                                        <span className={`text-[8px] font-bold uppercase ${totalStats.isMfaOver ? 'text-red-600' : 'text-sky-600'}`}>
                                                            {totalStats.isMfaOver ? `⚠️ ${jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel}` : `🏡 ${jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel}`}
                                                        </span>
                                                        <span className={`text-[11px] font-black ${totalStats.isMfaOver ? 'text-red-700' : 'text-sky-700'}`}>
                                                            {Math.round(totalStats.mfaRemaining).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <div className="h-1 bg-white/50 rounded-full overflow-hidden mt-0.5">
                                                        <div
                                                            className={`h-full rounded-full transition-all duration-500 ${totalStats.isMfaOver ? 'bg-red-500' : 'bg-sky-500'}`}
                                                            style={{ width: `${Math.min(100, ((totalStats.mfaLimit - totalStats.mfaRemaining) / totalStats.mfaLimit) * 100)}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                                {/* MDA */}
                                                {jurisdiction !== 'los_gatos' && (
                                                    <div className={`rounded-md px-2 py-1 border transition-all min-w-[80px] ${totalStats.isMdaOver ? 'bg-red-50 border-red-200' : 'bg-indigo-50/50 border-indigo-200'}`}>
                                                        <div className="flex items-center justify-between gap-1">
                                                            <span className={`text-[8px] font-bold uppercase ${totalStats.isMdaOver ? 'text-red-600' : 'text-indigo-600'}`}>
                                                                {totalStats.isMdaOver ? `⚠️ ${mdaMetricShortLabel}` : `🏊 ${mdaMetricShortLabel}`}
                                                            </span>
                                                            <span className={`text-[11px] font-black ${totalStats.isMdaOver ? 'text-red-700' : 'text-indigo-700'}`}>
                                                                {Math.round(totalStats.mdaRemaining).toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <div className="h-1 bg-white/50 rounded-full overflow-hidden mt-0.5">
                                                            <div
                                                                className={`h-full rounded-full transition-all duration-500 ${totalStats.isMdaOver ? 'bg-red-500' : 'bg-indigo-500'}`}
                                                                style={{ width: `${Math.min(100, ((totalStats.mdaLimit - totalStats.mdaRemaining) / totalStats.mdaLimit) * 100)}%` }}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Capacity Warning - Compact */}
                                            {(totalStats.isMfaOver || totalStats.isMdaOver) && (
                                                <div className="px-2 py-1 rounded-md bg-red-600 text-white flex items-center gap-1 shrink-0">
                                                    <span className="text-[10px]">🚫</span>
                                                    <span className="text-[8px] font-black">CAPACITY EXCEEDED</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Right Section - Structure List - Only if structures exist */}
                                        {testFitStructures.length > 0 && (
                                            <>
                                                {/* Divider between sections */}
                                                <div className="w-px bg-slate-200"></div>

                                                {/* Structure List Panel */}
                                                <div className="flex-1 flex flex-col bg-slate-50/50 min-h-0">
                                                    {/* Header with Count Summary - Clickable for Accordion */}
                                                    <div
                                                        onClick={() => setIsStructuresExpanded(!isStructuresExpanded)}
                                                        className="px-3 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 border-b border-slate-200/60 shrink-0 cursor-pointer hover:from-indigo-600 hover:to-purple-700 transition-all"
                                                    >
                                                        <div className="flex items-center justify-between mb-1">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-white text-[10px] font-bold uppercase tracking-wide">Structures</span>
                                                                {isStructuresExpanded ? (
                                                                    <ChevronUp className="w-3 h-3 text-white/80" />
                                                                ) : (
                                                                    <ChevronDown className="w-3 h-3 text-white/80" />
                                                                )}
                                                            </div>
                                                            <div className="flex gap-1.5">
                                                                {mfaStructures.length > 0 && (
                                                                    <div className="flex items-center gap-1 bg-sky-500/30 px-1.5 py-0.5 rounded-md">
                                                                        <span className="text-white text-[9px] font-bold">{jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel}</span>
                                                                        <span className="text-white/90 text-[9px] font-semibold">{mfaStructures.length}</span>
                                                                    </div>
                                                                )}
                                                                {jurisdiction !== 'los_gatos' && mdaStructures.length > 0 && (
                                                                    <div className="flex items-center gap-1 bg-indigo-500/30 px-1.5 py-0.5 rounded-md">
                                                                        <span className="text-white text-[9px] font-bold">{mdaMetricShortLabel}</span>
                                                                        <span className="text-white/90 text-[9px] font-semibold">{mdaStructures.length}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Scrollable Structure List - Collapsible */}
                                                    {isStructuresExpanded && (
                                                        <div className="overflow-x-auto flex-1 p-2 flex flex-row space-x-1.5 min-h-0">
                                                            {[
                                                                ...testFitStructures.filter(s => jurisdiction !== 'los_gatos' || !s.type.includes('MDA')),
                                                                ...manualMfaStructures.map((s, i) => ({
                                                                    ...s,
                                                                    type: 'EXISTING_MFA',
                                                                    isManualOffset: true,
                                                                    manualIndex: i + 1
                                                                }))
                                                            ].map((struct, index) => {
                                                                const isActive = struct.id === activeStructureId;


                                                                // Dynamic Title based on Type
                                                                let title = `Structure #${index + 1}`;
                                                                const mfaLabelText = jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel;
                                                                const mdaLabelText = mdaMetricShortLabel;
                                                                if (struct.type === 'NEW_MFA') title = `New ${mfaLabelText}`;
                                                                if (struct.type === 'EXISTING_MFA') title = `Existing ${mfaLabelText}`;
                                                                if (struct.type === 'NEW_MDA') title = `New ${mdaLabelText}`;
                                                                if (struct.type === 'EXISTING_MDA') title = `Existing ${mdaLabelText}`;
                                                                if (struct.type === 'EXTEND_MFA') title = `Extended ${mfaLabelText}`;
                                                                if (struct.type === 'CUSTOM_MDA') title = `Custom ${mdaLabelText}`;
                                                                if (struct.isTraceOverlay && struct.traceLabel) title = struct.traceLabel;

                                                                const isMfaType = struct.type === 'EXTEND_MFA' || struct.type === 'NEW_MFA' || struct.type === 'EXISTING_MFA';
                                                                const isStories = struct.isManualOffset;

                                                                return (
                                                                    <div key={struct.id} className="flex-shrink-0 min-w-[200px]">
                                                                        {/* Structure Card */}
                                                                        <div
                                                                            onClick={() => handleStructureClick(struct)}
                                                                            className={`group rounded-lg border transition-all duration-200 cursor-pointer ${isActive
                                                                                ? 'bg-indigo-50 border-indigo-400 shadow-md ring-2 ring-indigo-100'
                                                                                : 'bg-white border-slate-200 hover:border-indigo-300 hover:shadow-sm'
                                                                                }`}
                                                                        >
                                                                            <div className="p-1.5">
                                                                                {/* Structure Header */}
                                                                                <div className="flex items-center justify-between mb-1">
                                                                                    <div className="flex items-center gap-1">
                                                                                        <div className={`w-1.5 h-1.5 rounded-full ${struct.isValid ? 'bg-emerald-500' : 'bg-rose-500'}`}></div>
                                                                                        <span className={`text-[9px] font-bold ${isActive ? 'text-indigo-900' : 'text-slate-700'
                                                                                            }`}>
                                                                                            {isStories ? `Existing Stories ${struct.manualIndex}` : title}
                                                                                        </span>
                                                                                    </div>
                                                                                    <span className={`text-[7px] px-1 py-0.5 rounded font-bold ${isStories
                                                                                        ? 'bg-amber-100 text-amber-600'
                                                                                        : isMfaType
                                                                                            ? 'bg-sky-100 text-sky-600'
                                                                                            : 'bg-indigo-100 text-indigo-600'
                                                                                        }`}>
                                                                                        {isStories ? 'OFFSET' : (isMfaType ? (jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel) : mdaMetricShortLabel)}
                                                                                    </span>
                                                                                </div>

                                                                                {/* Area & Stories Display */}
                                                                                <div className="mb-1 flex items-center justify-between">
                                                                                    <div className="text-[10px] font-black text-slate-800">
                                                                                        {Math.round(struct.area).toLocaleString()}
                                                                                        <span className="text-[8px] font-medium text-slate-500 ml-0.5">sqft</span>
                                                                                    </div>
                                                                                    <div className="text-[9px] font-bold text-slate-500 bg-slate-100 px-1 py-0.5 rounded">
                                                                                        {struct.stories || 1} {(struct.stories || 1) === 1 ? 'Story' : 'Stories'}
                                                                                    </div>
                                                                                </div>

                                                                                {/* Validation Warning Message */}
                                                                                {/* {!struct.isValid && struct.rejectReason && (
                                                                                    <div className="mb-1 px-1.5 py-1 bg-amber-50 border border-amber-200 rounded">
                                                                                        <div className="flex items-start gap-1">
                                                                                            <span className="text-amber-600 mt-0.5">⚠️</span>
                                                                                            <span className="text-[8px] text-amber-700 font-medium leading-tight">
                                                                                                {struct.rejectReason}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                )} */}

                                                                                {/* Action Buttons */}
                                                                                <div className="flex items-center gap-0.5">
                                                                                    <button
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            if (isStories) {
                                                                                                setEditingMfaStructureId(struct.id);
                                                                                                setMfaStoriesInput(struct.stories?.toString() || "1");
                                                                                                setMfaOffsetInput(struct.area.toString());
                                                                                                setIsEditingMfaOffset(true);
                                                                                            } else {
                                                                                                handleStructureClick(struct);
                                                                                            }
                                                                                        }}
                                                                                        className="flex-1 py-0.5 px-1.5 rounded bg-slate-100 hover:bg-sky-50 text-slate-600 hover:text-sky-600 transition-all text-[8px] font-semibold flex items-center justify-center gap-0.5"
                                                                                    >
                                                                                        <Edit2 className="w-2 h-2" />
                                                                                        Edit
                                                                                    </button>
                                                                                    {!isStories && (
                                                                                        <button
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                deleteStructureAndSync(struct.id);
                                                                                            }}
                                                                                            className="py-0.5 px-1.5 rounded bg-slate-100 hover:bg-rose-50 text-slate-400 hover:text-rose-600 transition-all"
                                                                                            title="Delete"
                                                                                        >
                                                                                            <Trash2 className="w-2 h-2" />
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Advanced Editor Panel - Shows when structure is active */}
                                                                        {isActive && (
                                                                            <div className="mt-1.5 bg-white rounded-lg border-2 border-indigo-300 shadow-lg p-2 space-y-2 animate-in fade-in duration-200">
                                                                                {/* Editor Header */}
                                                                                <div className="flex items-center justify-between pb-1.5 border-b border-slate-200">
                                                                                    <div className="flex items-center gap-1">
                                                                                        <span className="text-[9px] font-black text-indigo-600 uppercase">Editor</span>
                                                                                        <span className="w-1 h-1 rounded-full bg-indigo-400 animate-pulse"></span>
                                                                                    </div>
                                                                                </div>

                                                                                {isStories ? (
                                                                                    <div className="space-y-1">
                                                                                        <label className="text-[8px] font-bold text-slate-600 uppercase">Offset Sqft</label>
                                                                                        <div className="flex items-center gap-1">
                                                                                            <input
                                                                                                type="number"
                                                                                                value={mfaOffsetInput}
                                                                                                onChange={(e) => setMfaOffsetInput(e.target.value)}
                                                                                                className="w-full px-1.5 py-1 text-[9px] font-bold border border-slate-300 rounded focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                                                                                                autoFocus
                                                                                            />
                                                                                            <button
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    const val = parseInt(mfaOffsetInput) || 0;
                                                                                                    setManualMfaStructures(prev => prev.map(s => s.id === struct.id ? { ...s, area: val } : s));
                                                                                                    setIsEditingMfaOffset(false);
                                                                                                    setActiveStructureId(null);
                                                                                                    handleSaveCustomStructures(manualMfaStructures.map(s => s.id === struct.id ? { ...s, area: val } : s));
                                                                                                }}
                                                                                                className="bg-emerald-500 hover:bg-emerald-600 text-white p-1 rounded transition-colors"
                                                                                            >
                                                                                                <Check className="w-2 h-2" />
                                                                                            </button>
                                                                                            <button
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    setActiveStructureId(null);
                                                                                                    setIsEditingMfaOffset(false);
                                                                                                }}
                                                                                                className="bg-slate-300 hover:bg-slate-400 text-slate-600 p-1 rounded transition-colors"
                                                                                            >
                                                                                                <X className="w-2 h-2" />
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                ) : (
                                                                                    <>
                                                                                        {/* Dimensions Section */}
                                                                                        <div className="space-y-1">
                                                                                            <div className="text-[8px] font-bold text-slate-600 uppercase">Dimensions</div>
                                                                                            <div className="grid grid-cols-2 gap-1">
                                                                                                <div>
                                                                                                    <label className="text-[7px] text-slate-500 font-semibold">Width (ft)</label>
                                                                                                    <input
                                                                                                        type="number"
                                                                                                        value={Math.round(struct.width || 50)}
                                                                                                        onChange={(e) => handleDimensionChange(struct.id, 'width', e.target.value)}
                                                                                                        className="w-full px-1.5 py-1 text-[9px] font-bold border border-slate-300 rounded focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                                                                                                    />
                                                                                                </div>
                                                                                                <div>
                                                                                                    <label className="text-[7px] text-slate-500 font-semibold">Length (ft)</label>
                                                                                                    <input
                                                                                                        type="number"
                                                                                                        value={Math.round(struct.length || 50)}
                                                                                                        onChange={(e) => handleDimensionChange(struct.id, 'length', e.target.value)}
                                                                                                        className="w-full px-1.5 py-1 text-[9px] font-bold border border-slate-300 rounded focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                                                                                                    />
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>

                                                                                        {/* Directional Extend Section */}
                                                                                        <div className="space-y-1">
                                                                                            <div className="text-[8px] font-bold text-slate-600 uppercase">Extend Direction</div>
                                                                                            <div className="grid grid-cols-3 gap-1">
                                                                                                <div className="col-start-2">
                                                                                                    <button
                                                                                                        onClick={() => handleDirectionalExtend(struct, 'north')}
                                                                                                        className="w-full py-1 rounded bg-gradient-to-b from-emerald-500 to-emerald-600 text-white text-[8px] font-bold hover:from-emerald-600 hover:to-emerald-700 shadow-sm"
                                                                                                    >
                                                                                                        ↑ N
                                                                                                    </button>
                                                                                                </div>
                                                                                                <button
                                                                                                    onClick={() => handleDirectionalExtend(struct, 'west')}
                                                                                                    className="py-1 rounded bg-gradient-to-r from-blue-500 to-blue-600 text-white text-[8px] font-bold hover:from-blue-600 hover:to-blue-700 shadow-sm"
                                                                                                >
                                                                                                    ← W
                                                                                                </button>
                                                                                                <div></div>
                                                                                                <button
                                                                                                    onClick={() => handleDirectionalExtend(struct, 'east')}
                                                                                                    className="py-1 rounded bg-gradient-to-l from-orange-500 to-orange-600 text-white text-[8px] font-bold hover:from-orange-600 hover:to-orange-700 shadow-sm"
                                                                                                >
                                                                                                    E →
                                                                                                </button>
                                                                                                <div className="col-start-2">
                                                                                                    <button
                                                                                                        onClick={() => handleDirectionalExtend(struct, 'south')}
                                                                                                        className="w-full py-1 rounded bg-gradient-to-t from-purple-500 to-purple-600 text-white text-[8px] font-bold hover:from-purple-600 hover:to-purple-700 shadow-sm"
                                                                                                    >
                                                                                                        ↓ S
                                                                                                    </button>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>

                                                                                        {/* SqFt Extend Section */}
                                                                                        <div className="space-y-1">
                                                                                            <div className="text-[8px] font-bold text-slate-600 uppercase">Quick Expand</div>
                                                                                            <div className="grid grid-cols-3 gap-1">
                                                                                                {[100, 250, 500].map(sqft => (
                                                                                                    <button
                                                                                                        key={sqft}
                                                                                                        onClick={() => handleSqFtExtend(struct, sqft)}
                                                                                                        className="py-1 px-1.5 rounded bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-[8px] font-bold hover:from-teal-600 hover:to-cyan-600 shadow-sm"
                                                                                                    >
                                                                                                        +{sqft}
                                                                                                    </button>
                                                                                                ))}
                                                                                            </div>
                                                                                        </div>

                                                                                        {/* Type Change Section */}
                                                                                        <div className="space-y-1">
                                                                                            <div className="text-[8px] font-bold text-slate-600 uppercase">Change Type</div>
                                                                                            <div className="grid grid-cols-2 gap-1">
                                                                                                <button
                                                                                                    onClick={() => handleTypeChange(struct.id, isMfaType ? 'NEW_MDA' : 'NEW_MFA')}
                                                                                                    className={`py-1 px-2 rounded text-[8px] font-bold transition-all ${!isMfaType
                                                                                                        ? 'bg-sky-500 text-white shadow-sm'
                                                                                                        : 'bg-slate-100 text-slate-600 hover:bg-sky-50'
                                                                                                        }`}
                                                                                                >
                                                                                                    🏡 MFA
                                                                                                </button>
                                                                                                <button
                                                                                                    onClick={() => handleTypeChange(struct.id, isMfaType ? 'NEW_MDA' : 'NEW_MFA')}
                                                                                                    className={`py-1 px-2 rounded text-[8px] font-bold transition-all ${isMfaType
                                                                                                        ? 'bg-indigo-500 text-white shadow-sm'
                                                                                                        : 'bg-slate-100 text-slate-600 hover:bg-indigo-50'
                                                                                                        }`}
                                                                                                >
                                                                                                    🏊 MDA
                                                                                                </button>
                                                                                            </div>
                                                                                        </div>

                                                                                        {/* Done Editing Button */}
                                                                                        <div className="pt-1 border-t border-slate-200">
                                                                                            <button
                                                                                                onClick={cancelEditMode}
                                                                                                className="w-full py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white text-[9px] font-black uppercase tracking-wide shadow-md transition-all flex items-center justify-center gap-1.5"
                                                                                            >
                                                                                                <Check className="w-3 h-3" />
                                                                                                Done Editing
                                                                                            </button>
                                                                                        </div>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Panel Footer - Clean Uniform Grid */}
                                <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 shrink-0">
                                    <div className="flex items-center justify-between gap-3">
                                        {/* Stats Grid - Uniform font sizes */}
                                        {(() => {
                                            const valNewMfa = Math.round(testFitStructures.filter(s => s.type === 'NEW_MFA').reduce((sum, s) => sum + s.area, 0));
                                            const valExistMfa = Math.round(testFitStructures.filter(s => s.type === 'EXISTING_MFA').reduce((sum, s) => sum + s.area, 0));
                                            const areaStories = Math.round(manualMfaStructures.reduce((sum, s) => sum + (s.area || 0), 0));
                                            const countStories = manualMfaStructures.reduce((sum, s) => sum + (s.stories || 0), 0);
                                            const valNewMda = Math.round(testFitStructures.filter(s => s.type === 'NEW_MDA').reduce((sum, s) => sum + s.area, 0));
                                            const valExistMda = Math.round(testFitStructures.filter(s => s.type === 'EXISTING_MDA').reduce((sum, s) => sum + s.area, 0));
                                            const valTotal = valNewMfa + valExistMfa + areaStories + valNewMda + valExistMda;
                                            const valTotalMfa = valNewMfa + valExistMfa;
                                            const valTotalMda = valNewMda + valExistMda;
                                            const valTotalStories = countStories;

                                            const mfaLabel = jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel;
                                            const mdaLabel = mdaMetricShortLabel;

                                            const statCells = [
                                                { label: `New ${mfaLabel}`, value: valNewMfa, color: 'text-sky-600', unit: 'sqft', isMda: false },
                                                { label: `Exist ${mfaLabel}`, value: valExistMfa, color: 'text-pink-600', unit: 'sqft', isMda: false },
                                                { label: `${mfaLabel} Stories`, value: countStories, color: 'text-amber-600', unit: 'Story', isMda: false },
                                                { label: `New ${mdaLabel}`, value: valNewMda, color: 'text-indigo-600', unit: 'sqft', isMda: true },
                                                { label: `Exist ${mdaLabel}`, value: valExistMda, color: 'text-violet-600', unit: 'sqft', isMda: true },
                                            ].filter(cell => {
                                                if (jurisdiction === 'los_gatos') {
                                                    return !cell.isMda;
                                                }
                                                return true;
                                            });

                                            const totalCells = [
                                                { label: `Total ${mfaLabel}`, value: valTotalMfa, color: 'text-sky-700', unit: 'sqft', isMda: false },
                                                { label: `Total ${mdaLabel}`, value: valTotalMda, color: 'text-indigo-700', unit: 'sqft', isMda: true },
                                                { label: `Total Stories`, value: valTotalStories, color: 'text-amber-700', unit: 'Story', isMda: false },
                                            ].filter(cell => {
                                                if (jurisdiction === 'los_gatos') {
                                                    return !cell.isMda;
                                                }
                                                return true;
                                            });

                                            return (
                                                <div className="flex-1 min-w-0 overflow-x-auto pb-1 scrollbar-hide">
                                                    <div className="flex items-center bg-slate-100/40 backdrop-blur-md rounded-xl px-3 py-1.5 border border-slate-200/50 min-w-max gap-4">
                                                        {/* Granular Breakdown Group */}
                                                        <div className="flex items-center gap-3">
                                                            {statCells.map((cell, i) => (
                                                                <React.Fragment key={`granular-${i}`}>
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[7px] font-black text-slate-400 uppercase tracking-widest mb-0.5 leading-none">{cell.label}</span>
                                                                        <div className="flex items-baseline gap-0.5">
                                                                            <span className={`text-[12px] font-black ${cell.color} leading-none`}>{cell.value.toLocaleString()}</span>
                                                                            <span className="text-[7.5px] font-bold text-slate-400/80 leading-none">{cell.unit}</span>
                                                                        </div>
                                                                    </div>
                                                                    {i < statCells.length - 1 && (
                                                                        <div className="h-6 w-px bg-slate-200/60 mx-1"></div>
                                                                    )}
                                                                </React.Fragment>
                                                            ))}
                                                        </div>

                                                        {/* Separator - Plus to Total */}
                                                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white shadow-sm border border-slate-200">
                                                            <span className="text-[12px] font-bold text-slate-400">=</span>
                                                        </div>

                                                        {/* Totals Group */}
                                                        <div className="flex items-center gap-4">
                                                            {totalCells.map((cell, i) => (
                                                                <React.Fragment key={`total-${i}`}>
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[7px] font-black text-slate-500 uppercase tracking-widest mb-0.5 leading-none">{cell.label}</span>
                                                                        <div className="flex items-baseline gap-0.5">
                                                                            <span className={`text-[12px] font-black ${cell.color} leading-none`}>{cell.value.toLocaleString()}</span>
                                                                            <span className="text-[7.5px] font-bold text-slate-400/80 leading-none">{cell.unit}</span>
                                                                        </div>
                                                                    </div>
                                                                    {i < totalCells.length - 1 && (
                                                                        <div className="h-6 w-px bg-slate-200/80 mx-1"></div>
                                                                    )}
                                                                </React.Fragment>
                                                            ))}

                                                            {/* Grand Final Total */}
                                                            <div className="h-8 w-px bg-slate-300/80 mx-1"></div>
                                                            <div className="flex flex-col">
                                                                <span className="text-[7px] font-black text-emerald-600 uppercase tracking-[0.15em] mb-0.5 leading-none">Total Project</span>
                                                                <div className="flex items-baseline gap-0.5">
                                                                    <span className="text-[14px] font-black text-emerald-700 leading-none">{valTotal.toLocaleString()}</span>
                                                                    <span className="text-[8px] font-black text-emerald-500 leading-none">sqft</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Save Button */}
                                        <button
                                            onClick={() => handleSaveCustomStructures()}
                                            disabled={testFitStructures.length === 0 && !extendMode}
                                            className={`group relative px-5 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all duration-200 flex items-center gap-2 shrink-0 ${testFitStructures.length === 0 && !extendMode
                                                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                                : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-md hover:shadow-lg active:scale-95'
                                                }`}
                                        >
                                            <span>💾</span>
                                            <span>Save</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })()}


                {/* Elevation Data Warning */}
                {
                    elevationError && (
                        <div className="absolute top-24 left-1/2 transform -translate-x-1/2 z-30 animate-in slide-in-from-top-4 fade-in duration-300 w-full max-w-md px-4">
                            <div className="bg-amber-50/95 backdrop-blur-sm border border-amber-200/60 shadow-xl rounded-xl p-4 flex items-start gap-3">
                                <div className="bg-amber-100 p-2 rounded-lg shrink-0">
                                    <span className="text-amber-600 text-lg">⚠️</span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-amber-900 font-bold text-sm">Terrain Data Unavailable</h3>
                                    <p className="text-amber-700 text-xs mt-1 leading-relaxed">
                                        Unable to fetch elevation data. Slope analysis grid cannot be generated at this time.
                                    </p>
                                    <p className="text-amber-600/70 text-[10px] mt-2 font-medium">
                                        Satellite imagery and boundaries are still active.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setElevationError(null)}
                                    className="text-amber-400 hover:text-amber-600 transition-colors p-1 hover:bg-amber-100 rounded-lg"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )
                }

                {
                    error && (
                        <div className="absolute inset-0 flex items-center justify-center bg-slate-50 z-10">
                            <div className="text-center max-w-md px-6">
                                <div className={`rounded-xl w-16 h-16 flex items-center justify-center mx-auto mb-4 ${error.includes('Network') ? 'bg-orange-100' : 'bg-red-100'
                                    }`}>
                                    <span className={`text-2xl ${error.includes('Network') ? 'text-orange-600' : 'text-red-600'}`}>
                                        {error.includes('Network') ? '📡' : '⚠'}
                                    </span>
                                </div>
                                <p className={`font-bold text-xl mb-2 ${error.includes('Network') ? 'text-orange-600' : 'text-red-600'}`}>
                                    {error.includes('Network') ? 'Connection Issue' : 'Map Error'}
                                </p>
                                <p className="text-slate-600 mb-4">{error}</p>
                                {error.includes('Network') && (
                                    <p className="text-slate-500 text-sm mb-4">
                                        This usually happens when your network connection changed. Try again when your connection is stable.
                                    </p>
                                )}
                                <div className="bg-slate-100 rounded-lg p-3 mb-6">
                                    <p className="text-slate-600 text-sm font-medium">Address:</p>
                                    <p className="text-slate-800 text-sm mt-1">{address}</p>
                                </div>
                                <div className="flex items-center justify-center space-x-3">
                                    {retryCount < maxRetries && (
                                        <button
                                            onClick={handleRetry}
                                            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all font-semibold flex items-center space-x-2"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            <span>Retry ({maxRetries - retryCount} left)</span>
                                        </button>
                                    )}
                                    <button
                                        onClick={onClose}
                                        className="bg-slate-200 text-slate-700 px-6 py-3 rounded-lg hover:bg-slate-300 transition-all font-semibold"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )
                }

                <div ref={mapDiv} className="w-full h-full" />

                {/* Floating Controls - Only show when complete */}
                {
                    loadingStep === "complete" && !error && locationInfo && (
                        <div className="absolute top-2 sm:top-4 right-2 sm:right-4 z-10 flex flex-col space-y-2 max-w-[calc(100%-1rem)] sm:max-w-none">
                            {/* Boundary Status Badge */}
                            <div className={`rounded-lg px-4 py-3 shadow-lg ${boundaryStatus === 'apn-mismatch' ? 'bg-red-50 border border-red-200' : boundaryStatus === 'not-found' ? 'bg-orange-50' : 'bg-white'
                                }`}>
                                <div className="flex items-center space-x-2">
                                    <Info className={`w-4 h-4 ${boundaryStatus === 'apn-mismatch' ? 'text-red-600' : boundaryStatus === 'not-found' ? 'text-orange-600' : 'text-green-600'
                                        }`} />
                                    <span className={`text-xs font-medium ${boundaryStatus === 'apn-mismatch' ? 'text-red-700' : boundaryStatus === 'not-found' ? 'text-orange-700' : 'text-slate-700'
                                        }`}>
                                        {getBoundaryStatusMessage()}
                                    </span>
                                </div>
                                {parcelMatchWarning && (
                                    <p className="mt-2 max-w-xs text-[11px] leading-snug text-red-700">
                                        {parcelMatchWarning}
                                    </p>
                                )}
                            </div>

                            {/* Development Capacity Panel - Compact Design */}
                            {(mda || mfa || jurisdictionCalc) && analysisStats && (() => {
                                // Use values from totalStats for perfect consistency with Test Fit panel
                                const effectiveMDA = totalStats.mdaLimit;
                                const effectiveMFA = totalStats.mfaLimit;
                                const remainingMDA = totalStats.mdaRemaining;
                                const remainingMFA = totalStats.mfaRemaining;
                                // Calculate percent used including test fit structures
                                const totalUsedMDA = effectiveMDA - remainingMDA;
                                const totalUsedMFA = effectiveMFA - remainingMFA;
                                const mdaPercent = effectiveMDA ? Math.min(100, (totalUsedMDA / effectiveMDA) * 100) : 0;
                                const mfaPercent = effectiveMFA ? Math.min(100, (totalUsedMFA / effectiveMFA) * 100) : 0;

                                return (
                                    <div className="bg-white/95 backdrop-blur-xl rounded-lg shadow-lg overflow-hidden border border-slate-200/60 w-[180px] sm:w-auto sm:min-w-[200px] animate-in slide-in-from-right-4 fade-in duration-500">
                                        {/* Header - Compact */}
                                        <div
                                            onClick={() => setIsCapacityExpanded(!isCapacityExpanded)}
                                            className="px-2 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-between cursor-pointer hover:from-indigo-700 hover:to-purple-700 transition-all"
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <span className="text-[9px]">📊</span>
                                                <span className="text-[10px] font-bold text-white uppercase tracking-wide">Dev Potential</span>
                                            </div>
                                            {isCapacityExpanded ? (
                                                <ChevronUp className="w-3 h-3 text-white/80" />
                                            ) : (
                                                <ChevronDown className="w-3 h-3 text-white/80" />
                                            )}
                                        </div>

                                        {/* Collapsible Content - Compact */}
                                        {isCapacityExpanded && (
                                            <div className="p-2 space-y-1.5">
                                                {/* Remaining MDA */}
                                                {jurisdiction !== 'los_gatos' && (
                                                    <div className={`rounded-md p-2 border ${remainingMDA >= 0
                                                        ? 'bg-emerald-50 border-emerald-200'
                                                        : 'bg-red-50 border-red-300'
                                                        }`}>
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-1.5">
                                                                <div className={`w-2 h-2 rounded-full ${remainingMDA >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
                                                                <span className={`text-xs font-bold ${remainingMDA >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                                                    Remain {mdaMetricShortLabel}
                                                                </span>
                                                            </div>
                                                            <div className={`text-sm font-black ${remainingMDA >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                                                {effectiveMDA ? Math.round(remainingMDA).toLocaleString() : "N/A"}
                                                                <span className="text-xs font-medium opacity-60 ml-0.5">sqft</span>
                                                            </div>
                                                        </div>
                                                        <div className="mt-1.5 h-1.5 bg-white rounded-full overflow-hidden">
                                                            <div
                                                                className={`h-full rounded-full ${remainingMDA < 0 ? 'bg-red-500' : 'bg-emerald-500'}`}
                                                                style={{ width: `${mdaPercent}%` }}
                                                            ></div>
                                                        </div>
                                                        <div className="flex justify-between text-xs text-slate-400 mt-1">
                                                            <span>Used: {Math.round(totalUsedMDA).toLocaleString()}</span>
                                                            <span>Max: {effectiveMDA ? Math.round(effectiveMDA).toLocaleString() : 'N/A'}</span>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Remaining MFA */}
                                                <div className={`rounded-md p-2 border ${remainingMFA >= 0
                                                    ? 'bg-sky-50 border-sky-200'
                                                    : 'bg-red-50 border-red-300'
                                                    }`}>
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-1.5">
                                                            <div className={`w-2 h-2 rounded-full ${remainingMFA >= 0 ? 'bg-sky-500' : 'bg-red-500'}`}></div>
                                                            <span className={`text-xs font-bold ${remainingMFA >= 0 ? 'text-sky-500' : 'text-red-600'}`}>
                                                                Remain {jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel}
                                                            </span>
                                                        </div>
                                                        <div className={`text-sm font-black ${remainingMFA >= 0 ? 'text-sky-600' : 'text-red-700'}`}>
                                                            {effectiveMFA ? Math.round(remainingMFA).toLocaleString() : "N/A"}
                                                            <span className="text-xs font-medium opacity-60 ml-0.5">sqft</span>
                                                        </div>
                                                    </div>
                                                    <div className="mt-1.5 h-1.5 bg-white rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full ${remainingMFA < 0 ? 'bg-red-500' : 'bg-sky-500'}`}
                                                            style={{ width: `${mfaPercent}%` }}
                                                        ></div>
                                                    </div>
                                                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                                                        <span>Used: {Math.round(totalUsedMFA).toLocaleString()}</span>
                                                        <span>{jurisdiction === 'los_gatos' ? 'MAGFA' : 'Max'}: {effectiveMFA ? Math.round(effectiveMFA).toLocaleString() : 'N/A'}</span>
                                                    </div>
                                                </div>

                                                {/* San Jose Specific Warnings & Flags */}
                                                {jurisdiction === 'san_jose' && jurisdictionCalc && (
                                                    <div className="space-y-1.5 mt-1 border-t border-slate-100 pt-1.5">
                                                        {/* Design Review Flag */}
                                                        {jurisdictionCalc.requiresDesignReview && (
                                                            <div className="bg-amber-50 border border-amber-200 rounded p-1.5 flex items-start gap-1.5 animate-pulse">
                                                                <AlertTriangle className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                                                                <div className="text-[9px] font-bold text-amber-700 leading-tight">
                                                                    DESIGN REVIEW REQUIRED: Effective FAR {jurisdictionCalc.formulaValue} exceeds 0.65 threshold.
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Slope Restricted Flag */}
                                                        {jurisdictionCalc.slopeRestricted && (
                                                            <div className="bg-red-50 border border-red-200 rounded p-1.5 flex items-start gap-1.5">
                                                                <AlertTriangle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                                                                <div className="text-[9px] font-bold text-red-700 leading-tight">
                                                                    SLOPE RESTRICTED: Avg slope ≥ 30%. Standard grading prohibited.
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* General San Jose Warnings (Mismatch, Clipped Polygons etc) */}
                                                        {jurisdictionCalc.warnings?.map((warn, i) => (
                                                            <div key={i} className="bg-slate-50 border border-slate-200 rounded p-1.5 flex items-start gap-1.5">
                                                                <Info className="w-3 h-3 text-slate-400 mt-0.5 flex-shrink-0" />
                                                                <div className="text-[8px] font-medium text-slate-600 leading-tight">
                                                                    {warn}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    )
                }

                {/* Terrain Analysis Report - Bottom Left - Premium Light Theme */}
                {
                    loadingStep === "complete" && !error && locationInfo && (
                        <div className="absolute bottom-14 sm:bottom-8 left-2 sm:left-4 lg:left-6 z-10 animate-in slide-in-from-bottom-4 fade-in duration-500 max-w-[calc(100%-1rem)] sm:max-w-none">
                            <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl w-[260px] sm:w-[300px] lg:min-w-[320px] border border-slate-200/60">
                                {/* Header with Gradient - Clickable for Accordion */}
                                <div
                                    onClick={() => setIsTerrainExpanded(!isTerrainExpanded)}
                                    className="px-2.5 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-b border-slate-100 cursor-pointer hover:from-blue-100 hover:via-indigo-100 hover:to-purple-100 transition-all"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md shadow-blue-500/20">
                                                <Grid className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                                            </div>
                                            <div>
                                                <div className="text-xs sm:text-sm font-bold text-slate-800 tracking-tight">Terrain Analysis</div>
                                                <div className="text-[10px] sm:text-[11px] text-slate-500 hidden sm:block">Slope Classification Report</div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="text-right bg-white/80 px-1.5 sm:px-2.5 py-1 sm:py-1.5 rounded-lg border border-slate-100 shadow-sm">
                                                <div className="text-sm sm:text-lg font-bold text-slate-800">{analysisStats ? (analysisStats.totalArea / 43560).toFixed(2) : '0.00'}</div>
                                                <div className="text-[8px] sm:text-[9px] text-slate-500 uppercase font-semibold">Total Acres</div>
                                            </div>
                                            {isTerrainExpanded ? (
                                                <ChevronUp className="w-5 h-5 text-slate-400" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-slate-400" />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Collapsible Metrics Section */}
                                {isTerrainExpanded && (
                                    <div className="flex flex-col max-h-[45vh] sm:max-h-[55vh]">
                                        {/* Tabs */}
                                        <div className="flex border-b border-slate-100 bg-slate-50/50">
                                            <button
                                                onClick={() => setActiveReportTab('overview')}
                                                className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider transition-all border-b-2 ${activeReportTab === 'overview' ? 'text-blue-600 border-blue-600 bg-white' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
                                            >
                                                Overview
                                            </button>
                                            <button
                                                onClick={() => setActiveReportTab('worksheet')}
                                                className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider transition-all border-b-2 ${activeReportTab === 'worksheet' ? 'text-blue-600 border-blue-600 bg-white' : 'text-slate-400 border-transparent hover:text-slate-600'}`}
                                            >
                                                Worksheet
                                            </button>
                                        </div>

                                        <div className="px-2.5 sm:px-4 py-2 sm:py-3 space-y-2 sm:space-y-2.5 overflow-y-auto flex-1">
                                            {activeReportTab === 'overview' ? (
                                                <>
                                                    {/* Flat Terrain */}
                                                    <div className="group">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-3 h-3 rounded-full bg-gradient-to-br from-green-400 to-green-600 shadow-sm"></div>
                                                                <span className="text-xs font-semibold text-slate-700">Flat Terrain</span>
                                                                <span className="text-[10px] text-slate-400 font-medium bg-slate-100 px-1.5 py-0.5 rounded">(0-10%)</span>
                                                            </div>
                                                            <span className="text-sm font-bold text-green-600">
                                                                {analysisStats && analysisStats.totalArea > 0 ? Math.round((analysisStats.flatArea / analysisStats.totalArea) * 100) : 0}%
                                                            </span>
                                                        </div>
                                                        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                                            <div className="h-full rounded-full transition-all duration-1000" style={{
                                                                width: `${analysisStats && analysisStats.totalArea > 0 ? (analysisStats.flatArea / analysisStats.totalArea) * 100 : 0}%`,
                                                                background: 'linear-gradient(90deg, #22c55e 0%, #4ade80 100%)'
                                                            }}></div>
                                                        </div>
                                                        <div className="flex items-center justify-between mt-1">
                                                            <span className="text-[10px] text-green-600 font-semibold">{analysisStats ? (analysisStats.flatArea / 43560).toFixed(2) : '0.00'} acres</span>
                                                            <span className="text-[10px] text-slate-400">{analysisStats ? Math.round(analysisStats.flatArea).toLocaleString() : '0'} sqft</span>
                                                        </div>
                                                    </div>

                                                    {/* Moderate Terrain */}
                                                    <div className="group">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-3 h-3 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-sm"></div>
                                                                <span className="text-xs font-semibold text-slate-700">Moderate Slope</span>
                                                                <span className="text-[10px] text-slate-400 font-medium bg-slate-100 px-1.5 py-0.5 rounded">(10-20%)</span>
                                                            </div>
                                                            <span className="text-sm font-bold text-amber-600">
                                                                {analysisStats && analysisStats.totalArea > 0 ? Math.round((analysisStats.moderateArea / analysisStats.totalArea) * 100) : 0}%
                                                            </span>
                                                        </div>
                                                        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                                            <div className="h-full rounded-full transition-all duration-1000" style={{
                                                                width: `${analysisStats && analysisStats.totalArea > 0 ? (analysisStats.moderateArea / analysisStats.totalArea) * 100 : 0}%`,
                                                                background: 'linear-gradient(90deg, #eab308 0%, #f59e0b 100%)'
                                                            }}></div>
                                                        </div>
                                                        <div className="flex items-center justify-between mt-1">
                                                            <span className="text-[10px] text-amber-600 font-semibold">{analysisStats ? (analysisStats.moderateArea / 43560).toFixed(2) : '0.00'} acres</span>
                                                            <span className="text-[10px] text-slate-400">{analysisStats ? Math.round(analysisStats.moderateArea).toLocaleString() : '0'} sqft</span>
                                                        </div>
                                                    </div>

                                                    {/* Steep Terrain */}
                                                    <div className="group">
                                                        <div className="flex items-center justify-between mb-1">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-3 h-3 rounded-full bg-gradient-to-br from-orange-400 to-red-500 shadow-sm"></div>
                                                                <span className="text-xs font-semibold text-slate-700">Steep/Critical</span>
                                                                <span className="text-[10px] text-slate-400 font-medium bg-slate-100 px-1.5 py-0.5 rounded">(20%+)</span>
                                                            </div>
                                                            <span className="text-sm font-bold text-orange-600">
                                                                {analysisStats && analysisStats.totalArea > 0 ? Math.round((analysisStats.steepArea / analysisStats.totalArea) * 100) : 0}%
                                                            </span>
                                                        </div>
                                                        <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                                            <div className="h-full rounded-full transition-all duration-1000" style={{
                                                                width: `${analysisStats && analysisStats.totalArea > 0 ? (analysisStats.steepArea / analysisStats.totalArea) * 100 : 0}%`,
                                                                background: 'linear-gradient(90deg, #f97316 0%, #ef4444 100%)'
                                                            }}></div>
                                                        </div>
                                                        <div className="flex items-center justify-between mt-1">
                                                            <span className="text-[10px] text-orange-600 font-semibold">{analysisStats ? (analysisStats.steepArea / 43560).toFixed(2) : '0.00'} acres</span>
                                                            <span className="text-[10px] text-slate-400">{analysisStats ? Math.round(analysisStats.steepArea).toLocaleString() : '0'} sqft</span>
                                                        </div>
                                                    </div>

                                                    {/* Contour Stats Summary */}
                                                    <div className="pt-2 mt-2 border-t border-slate-100 grid grid-cols-2 gap-2">
                                                        <div className="bg-slate-50/50 p-2 rounded-lg border border-slate-100">
                                                            <div className="text-[8px] font-bold text-slate-400 uppercase">Contour Lines</div>
                                                            <div className="text-xs font-black text-slate-700">{demContourTrace?.contour_count || analysisStats?.contourCount || '0'}</div>
                                                        </div>
                                                        <div className="bg-slate-50/50 p-2 rounded-lg border border-slate-100">
                                                            <div className="text-[8px] font-bold text-slate-400 uppercase">Total Length</div>
                                                            <div className="text-xs font-black text-slate-700">{Math.round(demContourTrace?.contour_total_length_ft || analysisStats?.totalContourLength || 0).toLocaleString()} ft</div>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : (
                                                /* Worksheet Tab */
                                                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                                                    <div className="p-2.5 bg-blue-50/50 rounded-xl border border-blue-100">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <div className="p-1.5 bg-blue-100 rounded-lg">
                                                                <PenTool className="w-3.5 h-3.5 text-blue-600" />
                                                            </div>
                                                            <div>
                                                                <div className="text-[11px] font-bold text-slate-800">Average Slope Formula</div>
                                                                <div className="text-[9px] font-medium text-slate-500">Hillside Worksheet Standard</div>
                                                            </div>
                                                        </div>
                                                        <div className="bg-white/80 p-2 rounded-lg border border-blue-50 text-center mb-2 shadow-sm">
                                                            <div className="text-xs font-mono font-black text-blue-700">S (%) = 0.0023 × I × L / An</div>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-2 text-[9px]">
                                                            <div className="flex justify-between items-center py-1 border-b border-blue-50">
                                                                <span className="text-slate-500 font-semibold">I (Interval)</span>
                                                                <span className="font-bold text-slate-800">{demContourTrace?.interval_ft || 2} ft</span>
                                                            </div>
                                                            <div className="flex justify-between items-center py-1 border-b border-blue-50">
                                                                <span className="text-slate-500 font-semibold">L (Length)</span>
                                                                <span className="font-bold text-slate-800">{Math.round(demContourTrace?.contour_total_length_ft || 0).toLocaleString()} ft</span>
                                                            </div>
                                                            <div className="flex justify-between items-center py-1">
                                                                <span className="text-slate-500 font-semibold">An (Net Area)</span>
                                                                <span className="font-bold text-slate-800">{parcelAreaAcres || '0.00'} ac</span>
                                                            </div>
                                                            <div className="flex justify-between items-center py-1">
                                                                <span className="text-slate-500 font-semibold">Avg Slope</span>
                                                                <span className="font-black text-blue-600">{(avgSlope !== undefined && avgSlope !== null) ? avgSlope : (estimatedSlope || 0)}%</span>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Contour Breakdown Table */}
                                                    <div className="overflow-hidden rounded-xl border border-slate-100 shadow-sm">
                                                        <table className="w-full text-left text-[10px]">
                                                            <thead className="bg-slate-50 text-slate-500 font-bold uppercase tracking-wider border-b border-slate-100">
                                                                <tr>
                                                                    <th className="px-3 py-2">Elevation (ft)</th>
                                                                    <th className="px-3 py-2 text-right">Length (ft)</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-slate-50">
                                                                {demContourTrace?.contour_details?.length > 0 ? (
                                                                    demContourTrace.contour_details.map((detail, idx) => (
                                                                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                                                            <td className="px-3 py-2 font-bold text-slate-700">{detail.elevation}</td>
                                                                            <td className="px-3 py-2 text-right font-mono font-bold text-slate-900">{detail.length.toLocaleString()}</td>
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan="2" className="px-3 py-4 text-center text-slate-400 italic">
                                                                            {demContourError ? demContourError : "No contour details available"}
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                            {demContourTrace?.contour_details?.length > 0 && (
                                                                <tfoot className="bg-slate-50/80 font-black text-slate-900 border-t border-slate-100">
                                                                    <tr>
                                                                        <td className="px-3 py-2">Total</td>
                                                                        <td className="px-3 py-2 text-right">{Math.round(demContourTrace.contour_total_length_ft).toLocaleString()} ft</td>
                                                                    </tr>
                                                                </tfoot>
                                                            )}
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Divider */}
                                            <div className="mx-4 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent my-2"></div>

                                            {/* MDA / Lot Coverage Structures */}
                                            {jurisdiction !== 'los_gatos' && (
                                                <div className="group">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 shadow-sm"></div>
                                                            <span className="text-xs font-semibold text-slate-700">🏊 Current {mdaMetricLabel}</span>
                                                        </div>
                                                        <span className="text-sm font-bold text-purple-600">
                                                            {(() => {
                                                                const mdaUsed = totalStats.mdaAlreadyBuilt + totalStats.mdaPlaced;
                                                                const effectiveMda = totalStats.mdaLimit;
                                                                return effectiveMda > 0 ? Math.round((mdaUsed / effectiveMda) * 100) : 0;
                                                            })()}%
                                                        </span>
                                                    </div>
                                                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                                        <div className="h-full rounded-full transition-all duration-1000" style={{
                                                            width: `${(() => {
                                                                const mdaUsed = totalStats.mdaAlreadyBuilt + totalStats.mdaPlaced;
                                                                const effectiveMda = totalStats.mdaLimit;
                                                                return effectiveMda > 0 ? (mdaUsed / effectiveMda) * 100 : 0;
                                                            })()}%`,
                                                            background: 'linear-gradient(90deg, #a855f7 0%, #7e22ce 100%)'
                                                        }}></div>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-1">
                                                        <span className="text-[10px] text-purple-600 font-semibold">
                                                            {(() => {
                                                                const mdaUsed = totalStats.mdaAlreadyBuilt + totalStats.mdaPlaced;
                                                                return (mdaUsed / 43560).toFixed(2);
                                                            })()} acres
                                                        </span>
                                                        <span className="text-[10px] text-purple-600 font-medium">
                                                            {(() => {
                                                                const mdaUsed = totalStats.mdaAlreadyBuilt + totalStats.mdaPlaced;
                                                                return Math.round(mdaUsed).toLocaleString();
                                                            })()} sqft
                                                        </span>
                                                    </div>
                                                    {/* Existing & New MDA Count - Row Layout */}
                                                    {(() => {
                                                        const existingMdaArea = totalStats.mdaAlreadyBuilt;
                                                        const newMdaArea = totalStats.mdaPlaced;
                                                        const hasAnyMda = existingMdaArea > 0 || newMdaArea > 0;

                                                        if (!hasAnyMda) return null;

                                                        return (
                                                            <div className="mt-1 grid grid-cols-2 gap-1">
                                                                {existingMdaArea > 0 && (
                                                                    <div className="px-2 py-1 bg-purple-50 rounded border border-purple-200">
                                                                        <div className="flex flex-row gap-5">
                                                                            <span className="text-[10px] text-purple-600 font-semibold">Existing {mdaMetricShortLabel}</span>
                                                                            <span className="text-[10px] text-purple-700 font-bold">
                                                                                {Math.round(existingMdaArea).toLocaleString()} sqft
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                {newMdaArea > 0 && (
                                                                    <div className="px-2 py-1 bg-rose-50 rounded border border-rose-200">
                                                                        <div className="flex flex-row gap-5">
                                                                            <span className="text-[10px] text-rose-600 font-semibold">New {mdaMetricShortLabel}</span>
                                                                            <span className="text-[10px] text-rose-700 font-bold">
                                                                                {Math.round(newMdaArea).toLocaleString()} sqft
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                    {(developmentAreaTrace || developmentAreaTraceError) && (
                                                        <div className="mt-1 text-[10px] leading-snug text-slate-500">
                                                            {developmentAreaTrace ? (
                                                                <>
                                                                    Trace shows detected buildings
                                                                    {developmentAreaTrace.pool_detection?.counted_candidate_count > 0
                                                                        ? ` and ${developmentAreaTrace.pool_detection.counted_candidate_count} pool trace${developmentAreaTrace.pool_detection.counted_candidate_count === 1 ? "" : "s"}`
                                                                        : ""}
                                                                    {developmentAreaTrace.pool_detection?.review_candidate_count > 0
                                                                        ? `; ${developmentAreaTrace.pool_detection.review_candidate_count} pool review candidate${developmentAreaTrace.pool_detection.review_candidate_count === 1 ? "" : "s"} not counted`
                                                                        : ""}
                                                                    . Paths, driveways and patios need manual draw review.
                                                                </>
                                                            ) : (
                                                                <>Trace unavailable: {developmentAreaTraceError}</>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* MFA Structures */}
                                            <div className="group">
                                                <div className="flex items-center justify-between mb-1">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-3 h-3 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 shadow-sm"></div>
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-xs font-semibold text-slate-700">🏡 Current {jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel} Structures</span>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setMfaOffsetInput("");
                                                                    setMfaStoriesInput("");
                                                                    setEditingMfaStructureId(null);
                                                                    setIsEditingMfaOffset(true);
                                                                }}
                                                                className="p-0.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                                                                title="Add manual structure"
                                                            >
                                                                <Plus className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <span className="text-sm font-bold text-sky-600">
                                                        {(() => {
                                                            const mfaUsed = totalStats.mfaAlreadyBuilt + totalStats.mfaPlaced + totalStats.manualMfaOffset;
                                                            const effectiveMfa = totalStats.mfaLimit;
                                                            return effectiveMfa > 0 ? Math.round((mfaUsed / effectiveMfa) * 100) : 0;
                                                        })()}%
                                                    </span>
                                                </div>
                                                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                                    <div className="h-full rounded-full transition-all duration-1000" style={{
                                                        width: `${(() => {
                                                            const mfaUsed = totalStats.mfaAlreadyBuilt + totalStats.mfaPlaced + totalStats.manualMfaOffset;
                                                            const effectiveMfa = totalStats.mfaLimit;
                                                            return effectiveMfa > 0 ? (mfaUsed / effectiveMfa) * 100 : 0;
                                                        })()}%`,
                                                        background: 'linear-gradient(90deg, #0ea5e9 0%, #0284c7 100%)'
                                                    }}></div>
                                                </div>

                                                {/* Manual Structures List - 2 Column Grid */}
                                                {manualMfaStructures.length > 0 && (
                                                    <div className="mt-2 grid grid-cols-2 gap-1.5">
                                                        {manualMfaStructures.map((struct) => (
                                                            <div key={struct.id} className="flex flex-col p-1.5 bg-slate-50/50 rounded-lg border-l-2 border-slate-300 hover:bg-slate-100/80 transition-all group/item relative">
                                                                <div className="flex flex-col gap-1 pr-6">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[7px] font-black text-slate-400 uppercase tracking-widest leading-none mb-0.5">Structure</span>
                                                                        <span className="text-[10px] font-black text-slate-800 leading-none">
                                                                            {struct.stories} Story
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[7px] font-black text-slate-400 uppercase tracking-widest leading-none mb-0.5">Area</span>
                                                                        <div className="flex items-baseline gap-0.5">
                                                                            <span className="text-[9px] font-bold text-slate-600 leading-none">
                                                                                {struct.area.toLocaleString()}
                                                                            </span>
                                                                            <span className="text-[7px] font-bold text-slate-400">sqft</span>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Top Right Actions */}
                                                                <div className="absolute top-1 right-1 flex items-center gap-0.5 opacity-0 group-hover/item:opacity-100 transition-all">
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setEditingMfaStructureId(struct.id);
                                                                            setMfaStoriesInput(struct.stories?.toString() || "1");
                                                                            setMfaOffsetInput(struct.area.toString());
                                                                            setIsEditingMfaOffset(true);
                                                                        }}
                                                                        className="p-1 rounded text-slate-400 hover:text-blue-500 hover:bg-white transition-colors"
                                                                        title="Edit"
                                                                    >
                                                                        <Pencil className="w-2.5 h-2.5" />
                                                                    </button>
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            const newList = manualMfaStructures.filter(s => s.id !== struct.id);
                                                                            setManualMfaStructures(newList);
                                                                            handleSaveCustomStructures(newList);
                                                                        }}
                                                                        className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-white transition-colors"
                                                                        title="Delete"
                                                                    >
                                                                        <Trash2 className="w-2.5 h-2.5" />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                <div className="flex items-center justify-between mt-1">
                                                    <span className="text-[10px] text-sky-600 font-semibold">
                                                        {(() => {
                                                            const mfaUsed = totalStats.mfaAlreadyBuilt + totalStats.mfaPlaced + totalStats.manualMfaOffset;
                                                            return (mfaUsed / 43560).toFixed(2);
                                                        })()} acres
                                                    </span>
                                                    <span className="text-[10px] text-sky-600 font-medium whitespace-normal text-right">
                                                        {(() => {
                                                            const mfaBase = totalStats.mfaAlreadyBuilt + totalStats.mfaPlaced;
                                                            const total = mfaBase + totalStats.manualMfaOffset;

                                                            if (manualMfaStructures.length > 0) {
                                                                const parts = [Math.round(mfaBase).toLocaleString()];
                                                                manualMfaStructures.forEach(struct => {
                                                                    parts.push(Math.round(struct.area || 0).toLocaleString());
                                                                });
                                                                return `${parts.join(' + ')} = ${Math.round(total).toLocaleString()} sqft`;
                                                            }
                                                            return `${Math.round(mfaBase).toLocaleString()} sqft`;
                                                        })()}
                                                    </span>
                                                </div>

                                                {isEditingMfaOffset && (
                                                    <div className="mt-2 p-2 bg-slate-100 rounded-lg border border-slate-200 animate-in fade-in slide-in-from-top-1 duration-200">
                                                        <div className="flex flex-col gap-2">
                                                            <div className="flex gap-2">
                                                                <div className="flex-1">
                                                                    <label className="text-[9px] font-bold text-slate-500 uppercase ml-1">Stories</label>
                                                                    <input
                                                                        type="number"
                                                                        value={mfaStoriesInput}
                                                                        onChange={(e) => setMfaStoriesInput(e.target.value)}
                                                                        placeholder="Stories"
                                                                        className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 transition-colors"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        autoFocus
                                                                    />
                                                                </div>
                                                                <div className="flex-[2]">
                                                                    <label className="text-[9px] font-bold text-slate-500 uppercase ml-1">Area (sqft)</label>
                                                                    <input
                                                                        type="number"
                                                                        value={mfaOffsetInput}
                                                                        onChange={(e) => setMfaOffsetInput(e.target.value)}
                                                                        placeholder="Offset sqft"
                                                                        className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-xs font-bold text-slate-700 outline-none focus:border-indigo-500 transition-colors"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="flex justify-end gap-2">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setIsEditingMfaOffset(false);
                                                                    }}
                                                                    className="px-2 py-1 text-[10px] font-bold text-slate-500 hover:text-slate-700 border border-slate-300 rounded"
                                                                >
                                                                    Cancel
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const area = parseInt(mfaOffsetInput) || 0;
                                                                        const stories = parseInt(mfaStoriesInput) || 1;

                                                                        let newList;
                                                                        if (editingMfaStructureId) {
                                                                            newList = manualMfaStructures.map(s =>
                                                                                s.id === editingMfaStructureId
                                                                                    ? { ...s, area, stories }
                                                                                    : s
                                                                            );
                                                                        } else {
                                                                            newList = [
                                                                                ...manualMfaStructures,
                                                                                {
                                                                                    id: `manual-mfa-offset-${Date.now()}`,
                                                                                    stories,
                                                                                    area
                                                                                }
                                                                            ];
                                                                        }

                                                                        setManualMfaStructures(newList);
                                                                        setIsEditingMfaOffset(false);
                                                                        handleSaveCustomStructures(newList);
                                                                    }}
                                                                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1 rounded text-[10px] font-bold transition-colors flex items-center gap-1"
                                                                >
                                                                    <Check className="w-3 h-3" />
                                                                    {editingMfaStructureId ? 'Update' : 'Add'}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Existing & New MFA Count - Row Layout */}
                                                {(() => {
                                                    const existingMfaArea = totalStats.mfaAlreadyBuilt;
                                                    const newMfaArea = totalStats.mfaPlaced;
                                                    const hasAnyMfa = existingMfaArea > 0 || newMfaArea > 0;

                                                    if (!hasAnyMfa) return null;

                                                    return (
                                                        <div className="mt-1 grid grid-cols-2 gap-1">
                                                            {existingMfaArea > 0 && (
                                                                <div className="px-2 py-1 bg-sky-50 rounded border border-sky-200">
                                                                    <div className="flex flex-row gap-5">
                                                                        <span className="text-[10px] text-sky-600 font-semibold">Existing {jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel}</span>
                                                                        <span className="text-[10px] text-sky-700 font-bold">
                                                                            {Math.round(existingMfaArea).toLocaleString()} sqft
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                            {newMfaArea > 0 && (
                                                                <div className="px-2 py-1 bg-sky-50 rounded border border-sky-200">
                                                                    <div className="flex flex-row gap-5">
                                                                        <span className="text-[10px] text-sky-600 font-semibold">New {jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricShortLabel}</span>
                                                                        <span className="text-[10px] text-sky-700 font-bold">
                                                                            {Math.round(newMfaArea).toLocaleString()} sqft
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>

                                            {/* Divider */}
                                            <div className="mx-4 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent my-2"></div>

                                            {/* Feasibility Section - Premium Light Design */}
                                            {(mda || mfa || jurisdictionCalc?.mda || jurisdictionCalc?.mfa) && analysisStats && (
                                                <div>
                                                    <div className="flex items-center justify-between gap-2 mb-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm">📊</span>
                                                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Development Feasibility</span>
                                                        </div>
                                                        {/* Jurisdiction Badge */}
                                                        {jurisdictionInfo.name && (
                                                            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${jurisdiction === 'palo_alto' || jurisdiction === 'los_altos_hills'
                                                                ? 'bg-sky-100 text-sky-600'
                                                                : (jurisdictionInfo.name.includes('Unknown') ? 'bg-slate-100 text-slate-500' : 'bg-green-100 text-green-700')
                                                                }`}>
                                                                {jurisdictionInfo.name}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Zoning Formula Badge - Standardized (LUF, NSA, SAF, etc.) */}
                                                    {(jurisdictionCalc?.formulaLabel || (mda && mfa)) && (
                                                        <div className={`mb-2 p-2 rounded-lg bg-gradient-to-r border ${jurisdiction === 'palo_alto' || jurisdiction === 'cupertino'
                                                            ? 'from-blue-50/80 to-indigo-50/80 border-blue-200'
                                                            : jurisdiction === 'los_altos_hills' || jurisdiction === 'los_altos'
                                                                ? 'from-emerald-50/80 to-green-50/80 border-emerald-200'
                                                                : 'from-slate-50 to-slate-100 border-slate-200'
                                                            }`}>
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="text-xs">📐</span>
                                                                    <span className={`text-[9px] font-bold tracking-tight ${jurisdiction === 'palo_alto' || jurisdiction === 'cupertino' ? 'text-blue-700' :
                                                                        jurisdiction === 'los_altos_hills' || jurisdiction === 'los_altos' ? 'text-emerald-700' :
                                                                            'text-slate-700'
                                                                        }`}>
                                                                        Zoning Analysis ({jurisdictionInfo.name})
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-1.5">
                                                                    <span
                                                                        className="text-[8px] font-black px-1.5 py-0.5 rounded shadow-sm border text-white"
                                                                        style={{
                                                                            backgroundColor: getJurisdictionStyle(jurisdiction).color,
                                                                            borderColor: getJurisdictionStyle(jurisdiction).color
                                                                        }}
                                                                        title={jurisdiction === 'los_gatos' ? 'Maximum Allowed Gross Floor Area' : (jurisdictionCalc?.formulaTooltip || "Using backend specified values for this property.")}
                                                                    >
                                                                        {jurisdiction === 'los_gatos' ? 'MAGFA' : (jurisdictionCalc?.formulaLabel || "GIS DATA")}
                                                                    </span>
                                                                    <span className="text-[11px] font-mono font-bold text-slate-700">
                                                                        {jurisdiction === 'los_gatos' && jurisdictionCalc?.farValue
                                                                            ? `${(jurisdictionCalc.farValue * 100).toFixed(1)}%`
                                                                            : (jurisdictionCalc?.convertedValue ? jurisdictionCalc.convertedValue.toFixed(2) : (mfa && parcelAreaAcres ? (mfa / (parcelAreaAcres * 43560)).toFixed(2) : "0.00"))
                                                                        }
                                                                        {jurisdiction === 'los_gatos' && jurisdictionCalc?.convertedValue ? (
                                                                            <span className="text-[10px] text-slate-400 font-normal ml-1">
                                                                                ({jurisdictionCalc.convertedValue.toFixed(2)} ac)
                                                                            </span>
                                                                        ) : " ac"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            {/* Detailed formula explanation */}
                                                            <div className="text-[9px] text-slate-500 mt-1.5 flex flex-col gap-0.5 border-t border-slate-100/50 pt-1.5">
                                                                <div className="flex justify-between items-center">
                                                                    <span>Slope: {jurisdictionCalc?.slopePercent ?? estimatedSlope}%</span>
                                                                </div>
                                                                <div className="font-medium opacity-85 italic break-words leading-normal mt-0.5">
                                                                    {jurisdictionCalc?.formulaTooltip ? jurisdictionCalc.formulaTooltip : (mda && mfa ? "Using Regional Mapping Data" : "")}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Specific Notices (CDP, Slope Density, etc.) */}
                                                    {jurisdictionCalc?.type === 'palo_alto' && jurisdictionCalc?.slopeApplies && (
                                                        <div className="mb-2 p-1.5 rounded-lg bg-amber-50 border border-amber-200">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="text-[10px]">⚠️</span>
                                                                <span className="text-[9px] font-bold text-amber-700">PAMC 18.16.090 Slope Density Applied</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {jurisdiction === 'los_altos_hills' && jurisdictionCalc?.requiresCdp && (
                                                        <div className="mb-2 p-1.5 rounded-lg bg-purple-50 border border-purple-200">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="text-[10px]">📋</span>
                                                                <span className="text-[9px] font-bold text-purple-700">CDP Required (LUF ≤ 0.50)</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {jurisdiction !== 'los_altos_hills' && jurisdictionCalc?.requiresCdp && (
                                                        <div className="mb-2 p-1.5 rounded-lg bg-amber-50 border border-amber-200">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="text-[10px]">📋</span>
                                                                <span className="text-[9px] font-bold text-amber-700">CDP Required (Density Rule)</span>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Limits Grid */}
                                                    <div className={`grid ${jurisdiction === 'los_gatos' ? 'grid-cols-1' : 'grid-cols-2'} gap-2 mb-2`}>
                                                        {jurisdiction !== 'los_gatos' && (
                                                            <div className="p-2 rounded-lg text-center border"
                                                                style={{
                                                                    background: `linear-gradient(to bottom right, ${getJurisdictionStyle(jurisdiction).bg}80, ${getJurisdictionStyle(jurisdiction).bg}40)`,
                                                                    borderColor: getJurisdictionStyle(jurisdiction).border.split(' ')[2]
                                                                }}
                                                            >
                                                                <div className="text-[9px] font-bold uppercase tracking-wider mb-1"
                                                                    style={{ color: getJurisdictionStyle(jurisdiction).color, opacity: 0.7 }}
                                                                >
                                                                    📏 {mdaMetricLabel}
                                                                </div>
                                                                <div className="flex flex-row gap-5 items-center justify-center">
                                                                    <div className="text-xs font-bold border-l-2 pl-2 mt-0.5"
                                                                        style={{ color: getJurisdictionStyle(jurisdiction).color, borderColor: getJurisdictionStyle(jurisdiction).color }}
                                                                    >
                                                                        {/* Use backend values for Palo Alto, frontend calc for LAH */}
                                                                        {(jurisdictionCalc?.type && !jurisdictionCalc.useBackendValues)
                                                                            ? (jurisdictionCalc.mda / 43560).toFixed(2)
                                                                            : (mda ? (mda / 43560).toFixed(2) : "N/A")
                                                                        }
                                                                        <span className="text-[10px] font-normal text-slate-400 ml-1">ac</span>
                                                                    </div>
                                                                    <div className="text-xs font-bold text-violet-700 border-l-2 border-violet-500 pl-2 mt-0.5">
                                                                        {(jurisdictionCalc?.type && !jurisdictionCalc.useBackendValues)
                                                                            ? Math.round(jurisdictionCalc.mda).toLocaleString()
                                                                            : (mda ? Math.round(mda).toLocaleString() : "N/A")
                                                                        } <span className="text-[10px] text-slate-500">sqft</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div className="p-2 rounded-lg text-center border"
                                                            style={{
                                                                background: `linear-gradient(to bottom right, ${getJurisdictionStyle(jurisdiction).bg}80, ${getJurisdictionStyle(jurisdiction).bg}40)`,
                                                                borderColor: getJurisdictionStyle(jurisdiction).border.split(' ')[2]
                                                            }}
                                                            title={jurisdiction === 'los_gatos' ? 'Maximum Allowed Gross Floor Area' : undefined}
                                                        >
                                                            <div className="text-[9px] font-bold uppercase tracking-wider mb-1"
                                                                style={{ color: getJurisdictionStyle(jurisdiction).color, opacity: 0.7 }}
                                                            >
                                                                📐 {jurisdiction === 'los_gatos' ? 'MAGFA' : mfaMetricLabel}
                                                            </div>
                                                            <div className="flex flex-row gap-5 items-center justify-center">
                                                                <div className="text-xs font-bold border-l-2 pl-2 mt-0.5"
                                                                    style={{ color: getJurisdictionStyle(jurisdiction).color, borderColor: getJurisdictionStyle(jurisdiction).color }}
                                                                >
                                                                    {/* Use backend values for Palo Alto, frontend calc for LAH */}
                                                                    {(jurisdictionCalc?.type && !jurisdictionCalc.useBackendValues)
                                                                        ? (jurisdictionCalc.mfa / 43560).toFixed(2)
                                                                        : (mfa ? (mfa / 43560).toFixed(2) : "N/A")
                                                                    }
                                                                    <span className="text-[10px] font-normal text-slate-400 ml-1">ac</span>
                                                                </div>
                                                                <div className="text-xs font-bold text-indigo-700 border-l-2 border-indigo-500 pl-2 mt-0.5">
                                                                    {(jurisdictionCalc?.type && !jurisdictionCalc.useBackendValues)
                                                                        ? Math.round(jurisdictionCalc.mfa).toLocaleString()
                                                                        : (mfa ? Math.round(mfa).toLocaleString() : "N/A")
                                                                    } <span className="text-[10px] text-slate-500">sqft</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Low Priority Metrics Grid */}
                                                    <div className="grid grid-cols-2 gap-2">
                                                        {/* Buildable Area (Flat + Moderate) */}
                                                        <div className="p-1 rounded-lg bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200">
                                                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Buildable</span>
                                                            <div className="flex justify-between items-center mb-1">
                                                                <span className="text-[9px] font-bold text-slate-700">
                                                                    {analysisStats ? ((analysisStats.flatArea + analysisStats.moderateArea) / 43560).toFixed(2) : "0.00"} ac
                                                                </span>
                                                                <div className="text-[9px] font-medium text-slate-500 text-right">
                                                                    {analysisStats ? Math.round(analysisStats.flatArea + analysisStats.moderateArea).toLocaleString() : "0"} sqft
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Hardscape Allowance */}
                                                        <div className="p-1 rounded-lg bg-gradient-to-r from-slate-50 to-slate-100 border border-slate-200">
                                                            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Hardscape</span>
                                                            <div className="flex justify-between items-center mb-1">
                                                                <span className="text-[9px] font-bold text-slate-700">
                                                                    {mda && mfa ? ((mda - mfa) / 43560).toFixed(2) : "N/A"} ac
                                                                </span>
                                                                <div className="text-[9px] font-medium text-slate-500 text-right">
                                                                    {mda && mfa ? Math.round(mda - mfa).toLocaleString() : "N/A"} sqft
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }

                {/* Footer Info */}
                <div className="bg-slate-50 px-3 sm:px-6 py-2 sm:py-3 border-t border-slate-200">
                    <div className="flex flex-wrap items-center justify-between gap-1 sm:gap-0">
                        <div className="flex items-center space-x-2 sm:space-x-4">
                            <p className="text-[10px] sm:text-sm text-slate-600">
                                <span className="font-semibold">Basemap:</span> <span className="hidden sm:inline">ArcGIS Imagery</span> Hybrid
                            </p>
                            {parcelAreaAcres && (
                                <p className="text-[10px] sm:text-sm text-slate-600">
                                    <span className="font-semibold">Area:</span> {parcelAreaAcres.toFixed(2)} acres
                                </p>
                            )}
                        </div>
                        {locationInfo && (
                            <p className="text-[9px] sm:text-xs text-slate-500 font-mono">
                                📍 {locationInfo.lat.toFixed(6)}, {locationInfo.lon.toFixed(6)}
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    if (isInline) return modalContent;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 bg-black bg-opacity-50 backdrop-blur-sm">
            {modalContent}
        </div>
    );
};

export default MapModal;
